import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { toBlob, toPng } from "html-to-image";


const DRAG_NAME_CATALOG = [
  "Anita Bracket", "Ivana Crown", "Misty Meaner", "Ella Vate", "Tess Tosterone",
  "Bea Haven", "Sue Flay", "Mona Lott", "Didi Devotion", "Fonda Jewels",
  "Ophelia Payne", "Clara Tease", "Polly Glamory", "Tara Dactyl", "Sasha Fierce-ish",
  "Dixie Normous", "Kitty Glitter", "Selma Soul", "Mimi Imfurst Place", "Greta Grip",
  "Lola Voltage", "Hazel Nutt", "Penny Tration", "Paige Turner", "Jenna Rosity",
  "Connie Sequins", "Bella Donna Summer", "Rita Menu", "Wanda Werk", "Gloria Holehearted",
  "Bambi Thunderdome", "Velma Vendetta", "Tina Turnt", "Darla Drama", "Ruby Rhinestone",
  "Pearl Necklace", "Crystal Ballgown", "Candy Wrapper", "Trixie Tangent", "Mandy Morphosis",
  "Venus Flytrap Queen", "Stella Stiletto", "Nora Filter", "Gia Whiz", "Betty Confetti",
  "Judy Booty", "Dolly Pardon Me", "Marge In Charge", "Vera Vixen", "Nancy Nerve",
  "Miss Calculation", "Miss Direction", "Miss Fortune Cookie", "Miss Behave", "Miss Steak",
  "Coco Puffery", "Daphne Drama", "Fifi Foreshadow", "Karma Chameleon", "Lexi Pro",
  "Ava Tantrum", "Carmen Get-It", "Roxy Roulette", "Sandy Beaches", "Stormy Weatherly",
  "April Showers", "Rain Beau", "Sunny Sideup", "Daisy Chainmail", "Petra Dish",
  "Bree Dazzle", "Cherry Ontop", "Honey Dijonay", "Moxie Millions", "Zsa Zsa Zinger",
  "Luna Tick", "Nova Cain", "Electra Cute", "Velvet Thunder", "Disco Nap",
  "Queenie Weenie", "Taffy Puller", "Satin Dollface", "Myrtle Kombat", "Tanya Hide",
  "Donna Tellme", "Ima Winner", "Ursula Major", "Mina Minute", "Willa Werk",
  "Frankie Fontaine", "Priscilla Pizzazz", "Marsha Mellow", "Tilda Swintonline", "Barbara Sequin",
  "Vivian Vavoom", "Glitter Fritter", "Neon Nonsense", "Hot Glue Gunner", "Mascara Royale",
  "Lip Syncerbell", "Shantay DeVille", "Sashay LaRue", "Kiki Cabaret", "Pouty McPoutface",
  "Beverly Heels", "Molly Coddle", "Pearl E. Gates", "Rhea Listic", "Silky Smoothie",
  "Chiffon Dynasty", "Tulle Time", "Organza Grande", "Lace Frontier", "Feather Locklear",
  "Madame Mayhem", "Countess Contour", "Duchess Drama", "Baroness Blush", "Lady Lashes",
  "Princess Problems", "Queenie Martini", "Diva Las Vegas", "Ginger Snapback", "Pepper Mint Condition",
  "Cinnamon Swerve", "Vanilla Vice", "Mocha Madness", "Caramel Chaos", "Peaches N Screams",
  "Blaire Necessity", "Tori Telling", "Holly Would", "Dinah Might", "Faye Slay",
  "Robin Banks", "Carrie Onstage", "Eileen Dover", "May B. Later", "June Bugle",
  "Wendy Whiplash", "Autumn Leaves Early", "Summer Clearance", "Winter Formaldehyde", "Spring Breaker",
  "Alotta Tude", "Aura Borealis", "Sparkle Motion", "Drama Llama", "Glamazon Prime"
];


// ── DATA ──────────────────────────────────────────────────────────────────────
const BRACKETS = {
  orange: {
    label: "Orange Bracket",
    color: "#ff6b35",
    glow: "rgba(255,107,53,0.35)",
    queens: [
      "A'Keria C. Davenport",
      "Dawn",
      "Lucky Starzzz",
      "Morgan McMichaels",
      "Morphine Love Dion",
      "Mystique Summers",
    ],
  },
  pink: {
    label: "Pink Bracket",
    color: "#ff2d9a",
    glow: "rgba(255,45,154,0.35)",
    queens: [
      "April Carrión",
      "Aura Mayari",
      "Crystal Methyd",
      "Salina EsTitties",
      "Silky Nutmeg Ganache",
      "Vivacious",
    ],
  },
  purple: {
    label: "Purple Bracket",
    color: "#9b5de5",
    glow: "rgba(155,93,229,0.35)",
    queens: [
      "Hershii LiqCour-Jeté",
      "Jasmine Kennedie",
      "Joey Jay",
      "Kennedy Davenport",
      "Sam Star",
      "Shuga Cain",
    ],
  },
};

// ── LOCK DATE ─────────────────────────────────────────────────────────────────
// Leaderboard submissions lock when All Stars 11 Episode 1 drops on Paramount+.
// May 8 2026 at 12:00 AM ET (midnight into Thursday/Friday) — adjust if Paramount
// confirms a different drop time. Using UTC-4 (EDT): midnight ET = 04:00 UTC.
const LOCK_DATE = new Date("2026-05-08T04:00:00Z");


// Stage 1: Pick TOP 2 from each group (6 queens each) → 6 semi-finalists total
// Stage 2: Pick Winner and Runner-Up directly from those 6 semi-finalists
// Scoring: group advancers + runner-up + winner (clean, no double-counting)
// Source: Paramount+ official press release for All Stars 11

const SCORING = {
  groupAdvance:    5,   // per correct queen advancing from a group (6 possible = 30 max)
  bracketWinner:   2,   // bonus: correctly predicted the #1 finisher within a bracket (×3 brackets = 6 max)
  bracketRunnerUp: 1,   // bonus: correctly predicted the #2 finisher within a bracket (×3 brackets = 3 max)
  podiumSwap:     10,   // partial credit: pick is on the podium but in the wrong spot (doesn't add to max)
  runnerUp:       15,   // correct overall 2nd place (lip sync smackdown)
  winner:         25,   // correct overall 1st place (All Stars Winner)
};
// Max possible is normally 79; a bracket with two tied runner-ups is scored dynamically.

const QUEEN_EMOJI = {
  "A'Keria C. Davenport": "💛", "Morgan McMichaels": "⚡", "Mystique Summers": "🌙",
  "Silky Nutmeg Ganache": "🍪", "Vivacious": "🌹", "Salina EsTitties": "🍑",
  "Crystal Methyd": "💎", "Jasmine Kennedie": "🌸", "Joey Jay": "🦘",
  "Lucky Starzzz": "✨", "Sam Star": "⭐", "Shuga Cain": "🍬",
  "April Carrión": "🌧️", "Aura Mayari": "🔮", "Dawn": "🌅",
  "Hershii LiqCour-Jeté": "🍫", "Kennedy Davenport": "👑", "Morphine Love Dion": "💋",
};

// Fallback official results — starts empty so no fake scores appear.
// Real results are entered via the admin panel and saved to shared storage.
const OFFICIAL_RESULTS = {
  semiFinalists: { orange: [], pink: [], purple: [] },
  runnerUp: null,
  winner: null,
};

const EMPTY_PICKS = {
  groupAdvances: { orange: [], pink: [], purple: [] }, // 2 per group
  runnerUp: null,
  winner: null,
};

function calcScore(picks, results) {
  let score = 0;
  const breakdown = [];

  // Stage 1: group advancers + bracket-level position scoring.
  // Normal bracket result format: [actual #1, actual #2]
  // Three-result bracket format: [actual #1, tied #2, tied #2]
  // For a three-result bracket, users get:
  //   +6 total for picking the bracket winner anywhere in their two picks
  //   +5 total for picking either tied runner-up anywhere in their two picks
  Object.entries(results.semiFinalists || {}).forEach(([bracket, officialRaw]) => {
    const official = Array.isArray(officialRaw) ? officialRaw.filter(Boolean) : [];
    const myPicks = picks.groupAdvances?.[bracket] || [];

    if (official.length >= 3) {
      const bracketWinner = official[0];
      const tiedRunnerUps = official.slice(1);
      myPicks.forEach(queen => {
        if (queen === bracketWinner) {
          score += 6;
          breakdown.push({ label: `${BRACKETS[bracket].label} winner`, queen, pts: 6 });
        } else if (tiedRunnerUps.includes(queen)) {
          score += 5;
          breakdown.push({ label: `${BRACKETS[bracket].label} tied runner-up`, queen, pts: 5 });
        }
      });
      return;
    }

    myPicks.forEach(queen => {
      if (official.includes(queen)) {
        score += SCORING.groupAdvance;
        breakdown.push({ label: `${BRACKETS[bracket].label} advance`, queen, pts: SCORING.groupAdvance });
      }
    });

    // Skip bracket-position bonuses if this bracket was declared a tie.
    if (results.tiedBrackets?.[bracket]) return;

    if (myPicks[0] && official[0] && myPicks[0] === official[0]) {
      score += SCORING.bracketWinner;
      breakdown.push({ label: `${BRACKETS[bracket].label} #1 finisher`, queen: myPicks[0], pts: SCORING.bracketWinner });
    }
    if (myPicks[1] && official[1] && myPicks[1] === official[1]) {
      score += SCORING.bracketRunnerUp;
      breakdown.push({ label: `${BRACKETS[bracket].label} #2 finisher`, queen: myPicks[1], pts: SCORING.bracketRunnerUp });
    }
  });

  // Stage 2: winner pick — full credit if exact, partial credit if they finished as runner-up.
  if (picks.winner) {
    if (picks.winner === results.winner) {
      score += SCORING.winner;
      breakdown.push({ label: "👑 All Stars Winner", queen: results.winner, pts: SCORING.winner });
    } else if (picks.winner === results.runnerUp) {
      score += SCORING.podiumSwap;
      breakdown.push({ label: "🥇→🥈 Picked as winner, finished 2nd", queen: picks.winner, pts: SCORING.podiumSwap });
    }
  }

  // Stage 2: runner-up pick — full credit if exact, partial credit if they took the crown.
  if (picks.runnerUp) {
    if (picks.runnerUp === results.runnerUp) {
      score += SCORING.runnerUp;
      breakdown.push({ label: "🥈 Runner-Up", queen: results.runnerUp, pts: SCORING.runnerUp });
    } else if (picks.runnerUp === results.winner) {
      score += SCORING.podiumSwap;
      breakdown.push({ label: "🥈→🥇 Picked as runner-up, won the season", queen: picks.runnerUp, pts: SCORING.podiumSwap });
    }
  }

  return { score, breakdown };
}

// ── STYLES ────────────────────────────────────────────────────────────────────
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Cormorant+Garamond:ital,wght@0,600;0,700;1,600&family=DM+Sans:wght@300;400;500&display=swap');
  :root {
    --dark: #08030e; --surface: #120820; --card: #1a0d2e;
    --gold: #f5c842; --pink: #ff2d9a; --muted: #8a6aaa;
    --text: #ede5ff; --green: #4ade80; --red: #f87171;
  }
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html { background: #08030e; }
  body { background: #08030e; color: var(--text); font-family: 'DM Sans', sans-serif; min-height: 100vh; }
  .app { min-height: 100vh; background: #08030e; }

  .nav {
    display: flex; align-items: center; justify-content: space-between;
    padding: 0 1.5rem; height: 64px;
    background: rgba(8,3,14,0.92); backdrop-filter: blur(16px);
    border-bottom: 1px solid rgba(245,200,66,0.12);
    position: sticky; top: 0; z-index: 100;
  }
  .nav-logo {
    font-family: 'Bebas Neue', sans-serif; font-size: 1.25rem; letter-spacing: 0.12em;
    line-height: 1.05;
    background: linear-gradient(90deg, #f5c842, #ff2d9a);
    -webkit-background-clip: text; -webkit-text-fill-color: transparent;
    flex-shrink: 0;
  }
  .nav-tabs { display: flex; gap: 0.5rem; align-items: center; }
  .nav-tab {
    padding: 0.45rem 0.9rem; border-radius: 2px; border: none;
    font-family: 'DM Sans', sans-serif; font-size: 0.72rem;
    letter-spacing: 0.1em; text-transform: uppercase; cursor: pointer;
    background: transparent; color: var(--muted); transition: all 0.2s;
    text-decoration: none; display: inline-flex; align-items: center;
    white-space: nowrap; line-height: 1;
  }
  .nav-tab.active { background: rgba(245,200,66,0.12); color: var(--gold); }
  .nav-tab:hover:not(.active) { color: var(--text); }
  .nav-donate {
    display: inline-flex; align-items: center; gap: 0.4rem;
    padding: 0.45rem 0.9rem; border-radius: 2px; border: 1px solid rgba(245,200,66,0.25);
    font-family: 'DM Sans', sans-serif; font-size: 0.72rem;
    letter-spacing: 0.1em; text-transform: uppercase; cursor: pointer;
    background: transparent; color: var(--gold);
    text-decoration: none; transition: all 0.2s; white-space: nowrap; flex-shrink: 0;
  }
  .nav-donate:hover { background: rgba(245,200,66,0.1); border-color: var(--gold); transform: translateY(-1px); }

  /* ── UX REDESIGN PASS: safer club aesthetic + clearer bracket flow ── */
  .app {
    background:
      radial-gradient(circle at 8% 12%, rgba(255,45,154,0.22), transparent 28%),
      radial-gradient(circle at 92% 18%, rgba(155,93,229,0.22), transparent 28%),
      radial-gradient(circle at 50% 0%, rgba(245,200,66,0.08), transparent 24%),
      linear-gradient(180deg, #07020d 0%, #0d0318 46%, #07020d 100%);
    position: relative;
    overflow-x: hidden;
  }
  .app::before {
    content: "";
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 0;
    background:
      radial-gradient(circle at 13% 24%, rgba(255,255,255,0.08) 0 1px, transparent 2px),
      radial-gradient(circle at 77% 18%, rgba(255,45,154,0.10) 0 2px, transparent 3px),
      radial-gradient(circle at 88% 42%, rgba(245,200,66,0.09) 0 1px, transparent 2px),
      linear-gradient(90deg, rgba(255,45,154,0.05), transparent 22%, transparent 78%, rgba(155,93,229,0.06));
    opacity: 0.75;
  }
  .nav, .hero-strip, .page, .toast {
    position: relative;
    z-index: 1;
  }
  .nav {
    height: 72px;
    border-bottom: 1px solid rgba(255,45,154,0.22);
    box-shadow: 0 10px 35px rgba(0,0,0,0.35);
  }
  .nav-logo {
    font-size: 1.35rem;
    filter: drop-shadow(0 0 12px rgba(255,45,154,0.22));
  }
  .nav-tab {
    border: 1px solid transparent;
    font-weight: 700;
  }
  .nav-tab.active {
    border-color: rgba(245,200,66,0.38);
    box-shadow: 0 0 18px rgba(245,200,66,0.08);
  }
  .hero-strip {
    padding: 3.4rem 1rem 2.25rem;
    background:
      linear-gradient(180deg, rgba(0,0,0,0.34), rgba(0,0,0,0.12)),
      radial-gradient(ellipse 70% 55% at 50% 10%, rgba(255,45,154,0.18), transparent 70%),
      radial-gradient(ellipse 45% 40% at 82% 15%, rgba(245,200,66,0.08), transparent 72%);
    border-bottom: 1px solid rgba(255,255,255,0.04);
  }
  .hero-eyebrow {
    color: var(--gold);
    font-weight: 700;
    text-shadow: 0 0 14px rgba(245,200,66,0.20);
  }
  .hero-title {
    text-shadow: 0 5px 22px rgba(0,0,0,0.55);
  }
  .hero-sub {
    color: rgba(237,229,255,0.82);
    font-size: 0.95rem;
    font-weight: 500;
  }
  .username-bar {
    background: rgba(8,3,14,0.55);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 999px;
    padding: 0.45rem 0.7rem;
    width: fit-content;
    margin-left: auto;
    margin-right: auto;
  }
  .page {
    max-width: 1180px;
  }
  .stage-wrap {
    background:
      linear-gradient(135deg, rgba(255,45,154,0.12), rgba(155,93,229,0.08) 45%, rgba(0,0,0,0.08)),
      rgba(18,8,32,0.72);
    border: 1px solid rgba(255,45,154,0.22);
    border-radius: 10px;
    padding: 1.15rem;
    box-shadow: 0 18px 50px rgba(0,0,0,0.28);
  }
  .stage-header {
    align-items: flex-start;
    margin-bottom: 1.1rem;
  }
  .stage-num {
    width: 54px;
    height: 54px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: var(--gold);
    background: rgba(255,45,154,0.12);
    border: 2px solid rgba(255,45,154,0.55);
    box-shadow: 0 0 22px rgba(255,45,154,0.18);
    font-size: 1.8rem;
  }
  .stage-badge {
    background: rgba(245,200,66,0.10) !important;
    color: var(--gold) !important;
    border: 1px solid rgba(245,200,66,0.18);
  }
  .sec-title {
    font-family: 'DM Sans', sans-serif;
    font-size: clamp(1.05rem, 2.2vw, 1.55rem);
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }
  .sec-desc {
    color: rgba(237,229,255,0.74);
    font-size: 0.9rem;
    max-width: 820px;
  }
  .groups-row {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 1rem;
  }
  .group-card, .podium-card, .score-panel, .lb-wrap, .scoring-item, .kofi-banner {
    background: rgba(16,6,29,0.84);
    border-color: rgba(255,255,255,0.10);
    box-shadow: inset 0 0 0 1px rgba(255,255,255,0.025), 0 12px 35px rgba(0,0,0,0.22);
  }
  .group-header, .podium-header {
    background: rgba(0,0,0,0.16);
  }
  .group-name, .podium-label {
    font-size: 0.95rem;
  }
  .queen-row, .podium-opt {
    min-height: 42px;
  }
  .q-name, .p-name {
    font-size: 0.88rem;
  }
  .queen-row.q-selected, .podium-opt.po-winner, .podium-opt.po-runner {
    box-shadow: 0 0 18px rgba(245,200,66,0.06);
  }
  .btn {
    min-height: 42px;
    font-weight: 800;
  }
  .btn-pink {
    background: linear-gradient(90deg, #d80d7c, #ff2d9a);
    box-shadow: 0 10px 28px rgba(255,45,154,0.18);
  }
  .btn-gold {
    box-shadow: 0 10px 28px rgba(245,200,66,0.14);
  }
  .lock-banner.open {
    background: rgba(245,200,66,0.075);
    border-color: rgba(245,200,66,0.30);
  }
  .progress-bar {
    height: 8px;
    background: rgba(255,255,255,0.07);
    overflow: hidden;
  }
  .progress-fill {
    box-shadow: 0 0 18px rgba(255,45,154,0.35);
  }
  .divider {
    margin: 1.35rem 0;
  }




  .group-card.orange {
    background:
      linear-gradient(145deg, rgba(255,107,53,0.20), rgba(255,107,53,0.075) 38%, rgba(16,6,29,0.90) 82%),
      rgba(16,6,29,0.84);
    border-color: rgba(255,107,53,0.22);
  }
  .group-card.pink {
    background:
      linear-gradient(145deg, rgba(255,45,154,0.22), rgba(255,45,154,0.075) 38%, rgba(16,6,29,0.90) 82%),
      rgba(16,6,29,0.84);
    border-color: rgba(255,45,154,0.24);
  }
  .group-card.purple {
    background:
      linear-gradient(145deg, rgba(155,93,229,0.22), rgba(155,93,229,0.075) 38%, rgba(16,6,29,0.90) 82%),
      rgba(16,6,29,0.84);
    border-color: rgba(155,93,229,0.24);
  }
  .group-card.orange:hover {
    box-shadow: inset 0 0 0 1px rgba(255,255,255,0.035), 0 16px 42px rgba(0,0,0,0.28), 0 0 24px rgba(255,107,53,0.10);
  }
  .group-card.pink:hover {
    box-shadow: inset 0 0 0 1px rgba(255,255,255,0.035), 0 16px 42px rgba(0,0,0,0.28), 0 0 24px rgba(255,45,154,0.12);
  }
  .group-card.purple:hover {
    box-shadow: inset 0 0 0 1px rgba(255,255,255,0.035), 0 16px 42px rgba(0,0,0,0.28), 0 0 24px rgba(155,93,229,0.12);
  }
  .group-card {
    transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
  }
  .group-card:hover {
    transform: translateY(-2px);
  }
  .queen-row {
    position: relative;
    overflow: hidden;
    transform: translateZ(0);
  }
  .queen-row::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.06), transparent);
    transform: translateX(-115%);
    transition: transform 0.42s ease;
    pointer-events: none;
  }
  .queen-row:hover:not(.q-disabled)::after {
    transform: translateX(115%);
  }
  .queen-row:hover:not(.q-disabled) {
    transform: translateX(2px);
    border-color: rgba(255,255,255,0.14);
  }
  .queen-row.q-selected {
    box-shadow: 0 0 0 1px color-mix(in srgb, var(--sel-color, #ff2d9a) 34%, transparent), 0 0 18px color-mix(in srgb, var(--sel-color, #ff2d9a) 14%, transparent);
  }
  .queen-row.q-selected .q-name {
    color: #fff;
  }
  .q-rank {
    box-shadow: 0 0 12px color-mix(in srgb, var(--sel-color, #ff2d9a) 38%, transparent);
  }
  .group-swap {
    position: relative;
    overflow: hidden;
  }
  .group-swap::before {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent, rgba(245,200,66,0.10), transparent);
    transform: translateX(-110%);
    transition: transform 0.38s ease;
  }
  .group-swap:hover::before {
    transform: translateX(110%);
  }


  /* ── Scoring page readability/layout polish ── */
  .scoring-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    align-items: stretch;
  }
  .scoring-item {
    min-height: 132px;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
  .scoring-item .scoring-pts,
  .scoring-item .scoring-name {
    text-align: center;
  }
  .scoring-note-card {
    width: min(100%, 760px) !important;
    max-width: 760px !important;
    margin-left: auto !important;
    margin-right: auto !important;
    padding: 1.25rem 1.45rem !important;
    background:
      linear-gradient(135deg, rgba(255,45,154,0.11), rgba(245,200,66,0.065) 48%, rgba(155,93,229,0.11)),
      rgba(16,6,29,0.82) !important;
    border: 1px solid rgba(245,200,66,0.17) !important;
    border-radius: 8px !important;
    box-shadow: 0 14px 38px rgba(0,0,0,0.22), inset 0 0 0 1px rgba(255,255,255,0.025) !important;
    text-align: left !important;
  }

  /* ── Leaderboard pick popup readability polish ── */
  .pick-modal-card {
    max-width: 620px !important;
    width: min(92vw, 620px) !important;
    padding: 1.75rem !important;
  }
  .pick-modal-card h3 {
    font-size: 1.35rem !important;
    margin-bottom: 1.2rem !important;
  }
  .pick-modal-section {
    font-size: 1rem !important;
    line-height: 1.55 !important;
  }
  .pick-modal-section strong {
    color: #ffe48a !important;
    font-size: 0.82rem !important;
    letter-spacing: 0.13em !important;
    text-transform: uppercase !important;
  }
  .pick-modal-line {
    font-size: 1rem !important;
    color: rgba(237,229,255,0.94) !important;
  }
  @media (max-width: 820px) {
    .scoring-grid {
      grid-template-columns: 1fr;
    }
    .scoring-item {
      min-height: 110px;
    }
  }

  .hero-countdown-pill {
    width: fit-content;
    max-width: 94vw;
    margin-left: auto !important;
    margin-right: auto !important;
  }
  .hero-strip .username-bar {
    display: flex !important;
    justify-content: center;
    align-items: center;
    margin: 0.75rem auto 0 !important;
    padding: 0.55rem 0.9rem;
    border-radius: 2px !important;
    background: rgba(8,3,14,0.62);
    border: 1px solid rgba(245,200,66,0.22);
    box-shadow: 0 10px 28px rgba(0,0,0,0.22);
    width: fit-content;
    max-width: min(94vw, 680px);
    clear: both;
  }

  .hero-strip .username-bar .btn {
    min-height: 34px;
  }

  @media (max-width: 900px) {
    .groups-row {
      grid-template-columns: 1fr;
    }
    .stage-wrap {
      padding: 1rem;
    }
    .stage-header {
      gap: 0.7rem;
    }
    .stage-num {
      width: 46px;
      height: 46px;
      font-size: 1.5rem;
    }
    .sec-desc {
      font-size: 0.84rem;
    }
    .hero-strip {
      padding: 2.4rem 1rem 1.7rem;
    }
  }

  @media (max-width: 640px) {
    .nav {
      height: auto;
      padding: 0.8rem 0.75rem;
      gap: 0.65rem;
      align-items: flex-start;
    }
    .nav-tabs {
      flex-wrap: wrap;
      justify-content: flex-end;
      gap: 0.35rem;
    }
    .nav-tab {
      padding: 0.42rem 0.55rem;
      font-size: 0.64rem;
    }
    .hero-title {
      font-size: clamp(2.25rem, 14vw, 3.4rem);
    }
    .hero-sub {
      font-size: 0.84rem;
    }

    .hero-strip .username-bar {
      border-radius: 2px;
      justify-content: center;
      text-align: center;
      width: fit-content;
      max-width: 94vw;
    }

    .page {
      padding: 1.1rem 0.65rem 4.5rem;
    }
    .stage-wrap {
      border-radius: 8px;
    }
    .stage-header {
      align-items: center;
    }
    .sec-title {
      font-size: 1rem;
      letter-spacing: 0.05em;
    }
    .queen-row, .podium-opt {
      min-height: 46px;
      padding: 0.62rem 0.65rem;
    }
    .q-name, .p-name {
      font-size: 0.9rem;
    }
    .podium-row {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 540px) { .nav-donate { display: none; } }

  .kofi-banner {
    margin-top: 3rem; padding: 1.5rem 2rem;
    background: rgba(245,200,66,0.04);
    border: 1px solid rgba(245,200,66,0.12);
    border-radius: 4px; text-align: center;
  }
  .kofi-banner-text {
    color: var(--text); font-size: 0.88rem; margin-bottom: 0.9rem; line-height: 1.6;
  }
  .kofi-btn {
    display: inline-flex; align-items: center; gap: 0.5rem;
    padding: 0.65rem 1.6rem; border-radius: 2px;
    background: rgba(245,200,66,0.1); border: 1px solid rgba(245,200,66,0.35);
    color: var(--gold); font-family: 'DM Sans', sans-serif;
    font-size: 0.82rem; font-weight: 500; letter-spacing: 0.08em;
    text-decoration: none; cursor: pointer; transition: all 0.2s;
  }
  .kofi-btn:hover { background: rgba(245,200,66,0.18); border-color: var(--gold); transform: translateY(-1px); }

  /* Email nudge — collapsed by default */
  .email-nudge {
    display: inline-flex; align-items: center; gap: 0.5rem;
    background: none; border: none; cursor: pointer;
    color: var(--text); font-family: 'DM Sans', sans-serif;
    font-size: 0.95rem; font-weight: 500; padding: 0.4rem 0.6rem;
    border-radius: 2px; transition: color 0.2s;
  }
  .email-nudge:hover { color: var(--gold); }
  .email-nudge-chevron { font-size: 0.8rem; transition: transform 0.2s; display: inline-block; color: var(--muted); }

  /* Drawer that expands */
  .email-drawer {
    margin-top: 0.75rem; padding: 1.1rem 1.2rem;
    background: rgba(255,255,255,0.02);
    border: 1px solid rgba(255,255,255,0.06);
    border-radius: 3px; text-align: left;
    animation: fadeSlideDown 0.2s ease;
  }
  @keyframes fadeSlideDown {
    from { opacity: 0; transform: translateY(-6px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .email-disclaimer {
    font-size: 0.76rem; color: var(--muted); line-height: 1.65;
    margin-bottom: 0.9rem;
  }
  .email-disclaimer strong { color: var(--text); font-weight: 500; }
  .email-row { display: flex; gap: 0.5rem; flex-wrap: wrap; }
  .email-input {
    flex: 1; min-width: 180px;
    background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1);
    border-radius: 2px; padding: 0.5rem 0.9rem;
    color: var(--text); font-family: 'DM Sans', sans-serif;
    font-size: 0.83rem; outline: none; transition: border-color 0.2s;
  }
  .email-input:focus { border-color: var(--pink); }
  .email-input::placeholder { color: rgba(138,106,170,0.45); }
  .email-input:disabled { opacity: 0.5; cursor: not-allowed; }
  .email-saved-msg {
    display: flex; align-items: center; gap: 0.5rem;
    font-size: 0.8rem; color: var(--green);
  }

  /* Lock banner */
  .lock-banner {
    border-radius: 3px; padding: 0.9rem 1.2rem;
    display: flex; align-items: center; gap: 0.8rem; flex-wrap: wrap;
    margin-bottom: 1.2rem;
  }
  .lock-banner.open {
    background: rgba(245,200,66,0.07);
    border: 1px solid rgba(245,200,66,0.25);
  }
  .lock-banner.closed {
    background: rgba(248,113,113,0.07);
    border: 1px solid rgba(248,113,113,0.3);
  }
  .lock-banner-icon { font-size: 1.3rem; flex-shrink: 0; }
  .lock-banner-text { flex: 1; font-size: 0.83rem; line-height: 1.55; }
  .lock-banner-text strong { font-weight: 600; }
  .lock-countdown {
    font-family: 'Bebas Neue', sans-serif; font-size: 1.4rem;
    color: var(--gold); letter-spacing: 0.05em; flex-shrink: 0;
  }

  .hero-strip {
    text-align: center; padding: 2.5rem 1rem 1.5rem;
    background: radial-gradient(ellipse 80% 60% at 50% 0%, rgba(255,45,154,0.12), transparent 70%);
  }
  .hero-eyebrow { font-size: 0.68rem; letter-spacing: 0.35em; color: var(--gold); text-transform: uppercase; margin-bottom: 0.6rem; display: block; }
  .hero-title { font-family: 'Bebas Neue', sans-serif; font-size: clamp(2.4rem, 8vw, 5rem); line-height: 0.95; letter-spacing: 0.03em; }
  .shimmer {
    background: linear-gradient(90deg, #ff2d9a, #f5c842, #9b5de5, #ff2d9a);
    background-size: 300% auto; -webkit-background-clip: text; -webkit-text-fill-color: transparent;
    animation: shimmer 5s linear infinite;
  }
  @keyframes shimmer { to { background-position: 300% center; } }
  .hero-sub { color: var(--muted); font-size: 0.85rem; margin-top: 0.6rem; }

  .username-bar { display: flex; gap: 0.5rem; align-items: center; justify-content: center; margin: 1.2rem 0 0; flex-wrap: wrap; }
  .username-input {
    background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1);
    border-radius: 2px; padding: 0.5rem 1rem; color: var(--text);
    font-family: 'DM Sans', sans-serif; font-size: 0.85rem; outline: none; width: 200px; transition: border-color 0.2s;
  }
  .username-input:focus { border-color: var(--pink); }
  .username-input::placeholder { color: rgba(138,106,170,0.5); }

  .btn { padding: 0.5rem 1.2rem; border-radius: 2px; border: none; font-family: 'DM Sans', sans-serif; font-size: 0.75rem; letter-spacing: 0.12em; text-transform: uppercase; cursor: pointer; font-weight: 500; transition: all 0.2s; }
  .btn-gold { background: var(--gold); color: #08030e; }
  .btn-gold:hover { filter: brightness(1.1); transform: translateY(-1px); }
  .btn-pink { background: var(--pink); color: #fff; }
  .btn-pink:hover { filter: brightness(1.1); transform: translateY(-1px); }
  .btn-ghost { background: transparent; color: var(--muted); border: 1px solid rgba(255,255,255,0.1); }
  .btn-ghost:hover { color: var(--text); border-color: rgba(255,255,255,0.25); }

  .page { max-width: 980px; margin: 0 auto; padding: 2rem 1rem 5rem; }

  .sec-label { font-size: 0.65rem; letter-spacing: 0.3em; color: var(--pink); text-transform: uppercase; display: block; margin-bottom: 0.3rem; }
  .sec-title { font-family: 'Cormorant Garamond', serif; font-size: clamp(1.3rem, 3vw, 1.9rem); font-weight: 700; }
  .sec-title em { font-style: italic; color: var(--gold); }
  .sec-desc { color: var(--muted); font-size: 0.82rem; margin-top: 0.3rem; line-height: 1.6; }

  .progress-bar { height: 2px; background: rgba(255,255,255,0.06); border-radius: 2px; margin: 1rem 0; }
  .progress-fill { height: 100%; border-radius: 2px; background: linear-gradient(90deg, var(--pink), var(--gold)); transition: width 0.5s ease; }

  .stage-wrap { margin-bottom: 2.5rem; }
  .stage-header { display: flex; align-items: center; gap: 0.8rem; margin-bottom: 1rem; }
  .stage-num { font-family: 'Bebas Neue', sans-serif; font-size: 2.2rem; line-height: 1; color: rgba(255,255,255,0.85); flex-shrink: 0; }
  .stage-info { flex: 1; }
  .stage-badge { display: inline-block; padding: 0.2rem 0.7rem; border-radius: 2px; font-size: 0.65rem; letter-spacing: 0.15em; text-transform: uppercase; font-weight: 600; margin-bottom: 0.35rem; }

  /* Group cards */
  .groups-row { display: grid; grid-template-columns: repeat(auto-fit, minmax(275px, 1fr)); gap: 1rem; }
  .group-card { background: var(--card); border: 1px solid rgba(255,255,255,0.06); border-radius: 4px; overflow: hidden; }
  .group-header { padding: 0.75rem 1rem; display: flex; align-items: center; gap: 0.7rem; border-bottom: 1px solid rgba(255,255,255,0.06); }
  .group-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
  .group-name { font-family: 'Bebas Neue', sans-serif; font-size: 0.9rem; letter-spacing: 0.1em; }
  .group-count { margin-left: auto; font-size: 0.72rem; color: var(--muted); }
  .group-count .num { color: var(--gold); font-weight: 600; }
  .queens-list { padding: 0.4rem; }
  .queen-row {
    display: flex; align-items: center; gap: 0.7rem;
    padding: 0.5rem 0.6rem; border-radius: 3px; cursor: pointer;
    transition: background 0.15s; border: 1px solid transparent; margin-bottom: 0.2rem;
  }
  .queen-row:last-child { margin-bottom: 0; }
  .queen-row:hover:not(.q-disabled) { background: rgba(255,255,255,0.04); }
  .queen-row.q-selected { border-color: var(--sel-color, #ff2d9a); background: rgba(255,255,255,0.04); }
  .queen-row.q-correct { border-color: var(--green) !important; background: rgba(74,222,128,0.06) !important; }
  .queen-row.q-wrong { opacity: 0.4; }
  .queen-row.q-disabled { cursor: not-allowed; opacity: 0.45; }
  .q-emoji { font-size: 1.1rem; width: 22px; text-align: center; flex-shrink: 0; }
  .q-name { font-size: 0.82rem; font-weight: 500; flex: 1; line-height: 1.2; }
  .q-rank { font-family: 'Bebas Neue', sans-serif; font-size: 0.78rem; padding: 0.1rem 0.45rem; border-radius: 2px; background: var(--sel-color, #ff2d9a); color: #fff; opacity: 0; transition: opacity 0.15s; }
  .queen-row.q-selected .q-rank { opacity: 1; }
  .q-pts { font-family: 'Bebas Neue', sans-serif; font-size: 0.85rem; color: var(--green); flex-shrink: 0; }

  /* Semi-finalist grid */
  .bracket-section { margin-bottom: 1rem; }
  .bracket-section-label { font-size: 0.65rem; letter-spacing: 0.25em; text-transform: uppercase; margin-bottom: 0.4rem; display: flex; align-items: center; gap: 0.5rem; }
  .bracket-dot { display: inline-block; width: 6px; height: 6px; border-radius: 50%; }
  .semi-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(185px, 1fr)); gap: 0.5rem; }
  .semi-card {
    background: var(--card); border: 1px solid rgba(255,255,255,0.07); border-radius: 3px;
    padding: 0.65rem 0.85rem; display: flex; align-items: center; gap: 0.6rem;
    cursor: pointer; transition: all 0.18s; position: relative; overflow: hidden;
  }
  .semi-card::before { content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 3px; background: var(--bc, #ff2d9a); }
  .semi-card:hover:not(.s-disabled) { background: rgba(255,255,255,0.05); border-color: rgba(255,255,255,0.15); }
  .semi-card.s-selected { border-color: var(--gold); background: rgba(245,200,66,0.06); }
  .semi-card.s-correct { border-color: var(--green) !important; background: rgba(74,222,128,0.06) !important; }
  .semi-card.s-wrong { opacity: 0.38; }
  .semi-card.s-disabled { cursor: not-allowed; opacity: 0.5; }
  .semi-emoji { font-size: 1rem; flex-shrink: 0; }
  .semi-name { font-size: 0.8rem; font-weight: 500; flex: 1; line-height: 1.25; }
  .semi-tag { font-size: 0.58rem; letter-spacing: 0.1em; text-transform: uppercase; font-weight: 700; color: var(--gold); flex-shrink: 0; }

  /* Podium */
  .podium-row { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 1rem; }
  .podium-card { background: var(--card); border: 1px solid rgba(255,255,255,0.07); border-radius: 4px; overflow: hidden; transition: border-color 0.2s; }
  .podium-header { padding: 0.65rem 1rem; display: flex; align-items: center; gap: 0.6rem; border-bottom: 1px solid rgba(255,255,255,0.06); }
  .podium-medal { font-size: 1.2rem; }
  .podium-label { font-family: 'Bebas Neue', sans-serif; font-size: 0.85rem; letter-spacing: 0.1em; flex: 1; }
  .podium-pts { font-family: 'Bebas Neue', sans-serif; font-size: 0.85rem; color: var(--gold); letter-spacing: 0.05em; }
  .podium-options { padding: 0.4rem; }
  .podium-opt {
    display: flex; align-items: center; gap: 0.6rem;
    padding: 0.52rem 0.6rem; border-radius: 3px; cursor: pointer;
    transition: background 0.15s; border: 1px solid transparent; margin-bottom: 0.2rem;
  }
  .podium-opt:last-child { margin-bottom: 0; }
  .podium-opt:hover:not(.po-disabled) { background: rgba(255,255,255,0.04); }
  .podium-opt.po-winner { border-color: var(--gold); background: rgba(245,200,66,0.08); }
  .podium-opt.po-runner { border-color: #aaa; background: rgba(192,192,192,0.06); }
  .podium-opt.po-correct { border-color: var(--green) !important; background: rgba(74,222,128,0.07) !important; }
  .podium-opt.po-partial { border-color: var(--gold) !important; background: rgba(245,200,66,0.08) !important; }
  .podium-opt.po-wrong { opacity: 0.38; }

  .group-swap {
    margin: 0.6rem 0.4rem 0.2rem; padding: 0.4rem 0.7rem;
    border: 1px solid rgba(245,200,66,0.25); border-radius: 3px;
    color: var(--gold); font-size: 0.72rem; letter-spacing: 0.1em;
    text-transform: uppercase; text-align: center; cursor: pointer;
    background: rgba(245,200,66,0.05); transition: all 0.15s; user-select: none;
    display: flex; align-items: center; justify-content: center; gap: 0.3rem;
  }
  .group-swap:hover { background: rgba(245,200,66,0.12); border-color: var(--gold); }
  .podium-opt.po-disabled { cursor: not-allowed; opacity: 0.45; }
  .podium-bracket-dot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }
  .p-emoji { font-size: 1rem; flex-shrink: 0; }
  .p-name { font-size: 0.82rem; font-weight: 500; flex: 1; }
  .p-tag { font-size: 0.6rem; letter-spacing: 0.08em; text-transform: uppercase; font-weight: 700; flex-shrink: 0; }

  /* Score panel */
  .score-panel { background: var(--card); border: 1px solid rgba(245,200,66,0.15); border-radius: 4px; padding: 1.5rem; margin-bottom: 2rem; display: flex; align-items: flex-start; gap: 2rem; flex-wrap: wrap; }
  .score-big { font-family: 'Bebas Neue', sans-serif; font-size: 4rem; line-height: 1; background: linear-gradient(135deg, var(--gold), var(--pink)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
  .score-label { font-size: 0.65rem; letter-spacing: 0.2em; color: var(--muted); text-transform: uppercase; }
  .score-max { font-size: 0.78rem; color: var(--muted); margin-top: 0.2rem; }
  .breakdown-list { flex: 1; min-width: 200px; }
  .breakdown-item { display: flex; align-items: center; justify-content: space-between; padding: 0.38rem 0; border-bottom: 1px solid rgba(255,255,255,0.04); font-size: 0.8rem; gap: 0.5rem; }
  .breakdown-item:last-child { border-bottom: none; }
  .breakdown-sub { color: var(--muted); font-size: 0.72rem; }
  .breakdown-pts { font-family: 'Bebas Neue', sans-serif; font-size: 1rem; color: var(--green); flex-shrink: 0; }

  .divider { height: 1px; background: rgba(255,255,255,0.04); margin: 2rem 0; }

  .scoring-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(175px, 1fr)); gap: 0.6rem; }
  .scoring-item { background: var(--card); border: 1px solid rgba(255,255,255,0.06); border-radius: 3px; padding: 1rem; }
  .scoring-pts { font-family: 'Bebas Neue', sans-serif; font-size: 1.6rem; color: var(--gold); }
  .scoring-name { font-size: 0.78rem; color: var(--muted); margin-top: 0.2rem; }

  .lb-wrap { background: var(--card); border: 1px solid rgba(255,255,255,0.06); border-radius: 4px; overflow: hidden; }
  .lb-header { display: grid; grid-template-columns: 44px 1fr 90px 60px; padding: 0.5rem 1rem; gap: 0.5rem; border-bottom: 1px solid rgba(255,255,255,0.08); background: rgba(0,0,0,0.2); }
  .lb-col { font-size: 0.62rem; letter-spacing: 0.2em; text-transform: uppercase; color: var(--muted); }
  .lb-col.right { text-align: right; }
  .lb-row { display: grid; grid-template-columns: 44px 1fr 90px 60px; align-items: center; padding: 0.85rem 1rem; border-bottom: 1px solid rgba(255,255,255,0.04); gap: 0.5rem; transition: background 0.15s; }
  .lb-row:last-child { border-bottom: none; }
  .lb-row:hover { background: rgba(255,255,255,0.02); }
  .lb-row.me { background: rgba(245,200,66,0.05); border-left: 2px solid var(--gold); }
  .lb-rank { font-family: 'Bebas Neue', sans-serif; font-size: 1.1rem; color: var(--muted); text-align: center; }
  .lb-rank.top { color: var(--gold); }
  .lb-name { font-size: 0.88rem; font-weight: 500; }
  .lb-name span { display: block; font-size: 0.72rem; color: var(--muted); font-weight: 400; }
  .lb-bar { height: 3px; border-radius: 2px; background: rgba(255,255,255,0.06); }
  .lb-bar-fill { height: 100%; border-radius: 2px; background: linear-gradient(90deg, var(--pink), var(--gold)); }
  .lb-score { font-family: 'Bebas Neue', sans-serif; font-size: 1.3rem; color: var(--gold); text-align: right; }

  .share-card-wrap {
    margin-top: 2rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.9rem;
  }
  .share-card-title {
    font-size: 0.7rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--gold);
    text-align: center;
  }
  .share-card {
    width: min(94vw, 500px);
    aspect-ratio: 4 / 5;
    background:
      radial-gradient(circle at 12% 8%, rgba(255,45,154,0.34), transparent 28%),
      radial-gradient(circle at 92% 7%, rgba(245,200,66,0.30), transparent 28%),
      radial-gradient(circle at 72% 83%, rgba(255,45,154,0.20), transparent 34%),
      radial-gradient(circle at 20% 92%, rgba(155,93,229,0.18), transparent 34%),
      linear-gradient(180deg, #12051f 0%, #160725 42%, #0a0310 78%, #020105 100%);
    border: 3px solid transparent;
    background-clip: padding-box;
    box-shadow: 0 20px 70px rgba(0,0,0,0.55), inset 0 0 0 2px rgba(255,255,255,0.04);
    border-radius: 22px;
    overflow: hidden;
    display: grid;
    grid-template-rows: 1fr 12.5%;
    color: var(--text);
    position: relative;
  }
  .share-card::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 22px;
    padding: 3px;
    background: linear-gradient(135deg, #ff2d9a 0%, #ff6b35 32%, #f5c842 58%, #ff2d9a 100%);
    -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
    z-index: 3;
  }
  .share-card-main {
    padding: 1.35rem 1.35rem 0.95rem;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-height: 0;
    position: relative;
    z-index: 2;
  }
  .share-card-kicker {
    width: 100%;
    font-size: clamp(0.68rem, 2.05vw, 0.92rem);
    letter-spacing: 0.25em;
    color: var(--gold);
    text-transform: uppercase;
    text-align: center;
    font-weight: 700;
    margin: 0 auto 0.22rem;
    text-shadow: 0 1px 10px rgba(245,200,66,0.25);
  }
  .share-card-name {
    width: 100%;
    font-family: 'Bebas Neue', sans-serif;
    line-height: 0.9;
    letter-spacing: 0.02em;
    text-align: center;
    text-transform: uppercase;
    white-space: nowrap;
    background: linear-gradient(90deg, #ff2d9a 0%, #ff7b39 24%, #f5c842 48%, #d875ff 78%, #ff5fb8 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    filter: drop-shadow(0 3px 12px rgba(0,0,0,0.45));
    margin: 0 auto 0.42rem;
  }
  .share-card-sub {
    width: 100%;
    font-size: clamp(0.72rem, 2.3vw, 0.98rem);
    letter-spacing: 0.23em;
    text-transform: uppercase;
    color: #b979dd;
    text-align: center;
    font-weight: 700;
    margin: 0 auto 0.62rem;
  }
  .share-card-brackets {
    display: grid;
    gap: 0.44rem;
  }
  .share-bracket-row {
    background: rgba(255,255,255,0.045);
    border: 1px solid rgba(255,255,255,0.11);
    border-left: 7px solid var(--bracket-color, var(--pink));
    border-radius: 9px;
    padding: 0.48rem 0.68rem 0.46rem;
    box-shadow: inset 0 0 0 1px rgba(255,255,255,0.025), 0 6px 18px rgba(0,0,0,0.18);
  }
  .share-bracket-label {
    font-size: clamp(0.66rem, 2vw, 0.86rem);
    letter-spacing: 0.24em;
    text-transform: uppercase;
    color: var(--bracket-color, var(--gold));
    font-weight: 800;
    margin-bottom: 0.28rem;
  }
  .share-pick-line {
    display: grid;
    grid-template-columns: 2.1rem 1.65rem 1fr;
    gap: 0.25rem;
    align-items: center;
    font-size: clamp(0.86rem, 2.55vw, 1.13rem);
    line-height: 1.25;
    font-weight: 700;
  }
  .share-pick-rank {
    font-family: 'Bebas Neue', sans-serif;
    color: var(--gold);
    font-size: clamp(1.1rem, 3.4vw, 1.52rem);
    line-height: 1;
    text-shadow: 0 1px 8px rgba(245,200,66,0.25);
  }
  .share-pick-emoji {
    font-size: clamp(1rem, 3vw, 1.32rem);
    text-align: center;
  }
  .share-overall {
    margin-top: 0.6rem;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.55rem;
  }
  .share-overall-card {
    background: rgba(255,255,255,0.048);
    border: 1px solid rgba(245,200,66,0.18);
    border-radius: 9px;
    padding: 0.5rem 0.64rem;
    min-width: 0;
    box-shadow: inset 0 0 0 1px rgba(255,255,255,0.025);
  }
  .share-overall-label {
    font-size: clamp(0.54rem, 1.55vw, 0.7rem);
    letter-spacing: 0.18em;
    color: var(--gold);
    font-weight: 800;
    text-transform: uppercase;
    margin-bottom: 0.18rem;
  }
  .share-overall-pick {
    display: grid;
    grid-template-columns: 1.35rem 1fr;
    gap: 0.3rem;
    font-size: clamp(0.86rem, 2.55vw, 1.13rem) !important;
    line-height: 1.25;
    font-weight: 700;
    align-items: center;
  }

  .share-overall-card.overall-winner {
    border-color: rgba(245,200,66,0.34);
    background: rgba(245,200,66,0.075);
    box-shadow:
      inset 0 0 0 1px rgba(255,255,255,0.03),
      0 0 18px rgba(245,200,66,0.10);
  }
  .share-overall-card.overall-runner {
    border-color: rgba(185,121,221,0.28);
    background: rgba(155,93,229,0.06);
  }
  .share-overall-card.overall-winner .share-overall-pick {
    color: #fff7c7;
    text-shadow: 0 0 10px rgba(245,200,66,0.18);
  }
  .share-overall-card.overall-runner .share-overall-pick {
    color: rgba(237,229,255,0.94);
  }

  .share-card-actions {
    display: flex;
    gap: 0.65rem;
    justify-content: center;
    flex-wrap: wrap;
  }
  .share-card-brand {
    background: rgba(2,1,6,0.68);
    border-top: none;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    font-family: 'Bebas Neue', sans-serif;
    text-align: center;
    letter-spacing: 0.09em;
    position: relative;
    z-index: 2;
  }
  .share-card-brand-small {
    color: var(--gold);
    font-family: 'DM Sans', sans-serif;
    font-size: clamp(0.48rem, 1.45vw, 0.64rem);
    letter-spacing: 0.16em;
    font-weight: 800;
    text-transform: uppercase;
    line-height: 1;
    margin-bottom: 0.14rem;
  }
  .share-card-brand-url {
    font-family: 'Bebas Neue', sans-serif;
    font-size: clamp(1.22rem, 4.65vw, 1.78rem);
    line-height: 1;
    color: #fff;
    text-shadow: 0 2px 12px rgba(0,0,0,0.72);
  }

  .toast { position: fixed; bottom: 1.5rem; left: 50%; transform: translateX(-50%) translateY(80px); background: var(--surface); border: 1px solid var(--gold); color: var(--text); padding: 0.7rem 1.4rem; border-radius: 2px; font-size: 0.82rem; z-index: 500; transition: transform 0.4s cubic-bezier(0.34,1.56,0.64,1); pointer-events: none; white-space: normal; max-width: 340px; text-align: center; line-height: 1.5; }
  .toast.show { transform: translateX(-50%) translateY(0); }
  .badge-you { display: inline-block; padding: 0.12rem 0.45rem; border-radius: 2px; font-size: 0.62rem; letter-spacing: 0.1em; text-transform: uppercase; font-weight: 600; background: rgba(245,200,66,0.2); color: var(--gold); margin-left: 0.4rem; }

  @media (max-width: 540px) {
    .lb-header, .lb-row { grid-template-columns: 36px 1fr 50px; }
    .lb-bar { display: none; }
    .score-panel { flex-direction: column; gap: 1rem; }
    .nav-tab { padding: 0.4rem 0.6rem; font-size: 0.68rem; }
  }

  /* ── FINAL scoring + modal polish overrides ── */
  .scoring-grid {
    display: grid !important;
    grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
    gap: 0.8rem !important;
    align-items: stretch !important;
  }
  .scoring-item {
    min-height: 132px !important;
    height: 100% !important;
    display: flex !important;
    flex-direction: column !important;
    justify-content: center !important;
    align-items: center !important;
    text-align: center !important;
    padding: 1.15rem !important;
  }
  .scoring-pts,
  .scoring-name {
    text-align: center !important;
  }
  .scoring-note-card {
    max-width: 760px !important;
    margin-left: auto !important;
    margin-right: auto !important;
    padding: 1.25rem 1.45rem !important;
    background:
      linear-gradient(135deg, rgba(255,45,154,0.11), rgba(245,200,66,0.065) 48%, rgba(155,93,229,0.11)),
      rgba(16,6,29,0.82) !important;
    border: 1px solid rgba(245,200,66,0.17) !important;
    border-radius: 8px !important;
    box-shadow: 0 14px 38px rgba(0,0,0,0.22), inset 0 0 0 1px rgba(255,255,255,0.025) !important;
  }
  .scoring-note-card .sec-title,
  .scoring-note-card .sec-desc {
    text-align: left !important;
  }
  .pick-modal-card {
    max-width: 660px !important;
    width: min(92vw, 660px) !important;
    padding: 1.85rem !important;
  }
  .pick-modal-card h3 {
    font-size: 1.45rem !important;
    margin-bottom: 1.25rem !important;
  }
  .pick-modal-grid {
    display: grid !important;
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    gap: 1rem 1.15rem !important;
  }
  .pick-modal-section {
    font-size: 1rem !important;
    line-height: 1.55 !important;
    background: rgba(255,255,255,0.035) !important;
    border: 1px solid rgba(255,255,255,0.07) !important;
    border-radius: 6px !important;
    padding: 0.85rem 0.95rem !important;
    margin-bottom: 0 !important;
  }
  .pick-modal-section strong {
    color: #ffe48a !important;
    font-size: 0.82rem !important;
    letter-spacing: 0.12em !important;
    text-transform: uppercase !important;
    display: block !important;
    margin-bottom: 0.35rem !important;
  }
  .pick-modal-line {
    font-size: 1rem !important;
    color: rgba(237,229,255,0.95) !important;
    line-height: 1.5 !important;
  }
  @media (max-width: 820px) {
    .scoring-grid {
      grid-template-columns: 1fr !important;
    }
    .pick-modal-grid {
      grid-template-columns: 1fr !important;
    }
  }


  /* ── Final scoring-card alignment polish ── */
  .scoring-grid {
    gap: 0.7rem !important;
  }
  .scoring-item {
    min-height: 124px !important;
    padding: 0.95rem 1rem !important;
    display: flex !important;
    flex-direction: column !important;
    justify-content: center !important;
    align-items: center !important;
    text-align: left !important;
  }
  .scoring-item .scoring-pts {
    text-align: center !important;
    margin-bottom: 0.28rem !important;
    line-height: 1 !important;
  }
  .scoring-item > div:nth-child(2) {
    width: 100% !important;
    text-align: center !important;
    margin-bottom: 0.38rem !important;
    line-height: 1.15 !important;
  }
  .scoring-item .scoring-name {
    width: 100% !important;
    max-width: 285px !important;
    text-align: left !important;
    line-height: 1.42 !important;
    margin: 0 auto !important;
  }

  /* ── FINAL Ko-fi centering + premium polish ── */
  .kofi-banner.centered-kofi {
    display: flex !important;
    flex-direction: column !important;
    align-items: center !important;
    justify-content: center !important;
    text-align: center !important;
    margin: 1.5rem auto !important;
    max-width: 560px !important;
    padding: 1.4rem 1.6rem !important;
    background:
      linear-gradient(
        135deg,
        rgba(255,45,154,0.09),
        rgba(245,200,66,0.05),
        rgba(155,93,229,0.09)
      ),
      rgba(16,6,29,0.8) !important;
    border: 1px solid rgba(245,200,66,0.18) !important;
    border-radius: 8px !important;
    box-shadow:
      0 18px 48px rgba(0,0,0,0.28),
      inset 0 0 0 1px rgba(255,255,255,0.03) !important;
  }

  .kofi-banner.centered-kofi .kofi-banner-text,
  .kofi-banner.centered-kofi .kofi-text {
    display: block !important;
    width: 100% !important;
    max-width: 470px !important;
    text-align: center !important;
    margin: 0 auto 0.45rem !important;
    line-height: 1.5 !important;
    color: var(--text) !important;
  }

  .kofi-banner.centered-kofi .kofi-btn {
    margin: 0.8rem auto 0 !important;
    align-self: center !important;
    transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease, background 0.25s ease !important;
    animation: kofiGlowPulse 3.8s ease-in-out infinite;
  }

  .kofi-banner.centered-kofi .kofi-btn:hover {
    transform: translateY(-1px);
    box-shadow:
      0 0 18px rgba(245,200,66,0.25),
      0 0 32px rgba(255,45,154,0.15);
  }

  @keyframes kofiGlowPulse {
    0%, 100% {
      box-shadow: 0 0 0 rgba(245,200,66,0), 0 0 0 rgba(255,45,154,0);
    }
    50% {
      box-shadow: 0 0 16px rgba(245,200,66,0.14), 0 0 28px rgba(255,45,154,0.08);
    }
  }

  @media (max-width: 640px) {
    .kofi-banner.centered-kofi {
      margin: 1.1rem auto !important;
      padding: 1.05rem 1rem !important;
      max-width: 94vw !important;
    }
    .kofi-banner.centered-kofi .kofi-banner-text,
    .kofi-banner.centered-kofi .kofi-text {
      font-size: 0.82rem !important;
      line-height: 1.45 !important;
      margin-bottom: 0.35rem !important;
      max-width: 100% !important;
    }
    .kofi-banner.centered-kofi .kofi-btn {
      margin-top: 0.6rem !important;
      padding: 0.58rem 1.15rem !important;
      font-size: 0.76rem !important;
    }
  }


  /* ── Final safe UX/UI polish bundle ── */

  /* 1. Selection feedback: unselected choices are calmer, selected choices pop */
  .queen-row:not(.q-selected):not(.q-disabled) {
    opacity: 0.78;
    transition: opacity 0.2s ease, transform 0.18s ease, border-color 0.18s ease, background 0.18s ease;
  }
  .queen-row.q-selected {
    opacity: 1;
  }
  .queen-row:not(.q-selected):not(.q-disabled):hover {
    opacity: 0.96;
  }

  /* 4. Submit feels like a milestone when enabled */
  .btn-pink:enabled {
    box-shadow:
      0 0 18px rgba(255,45,154,0.25),
      0 0 36px rgba(255,45,154,0.12),
      0 10px 28px rgba(255,45,154,0.18);
  }

  /* 6. Bracket labels pop more */
  .group-name {
    font-weight: 800 !important;
    letter-spacing: 0.08em !important;
  }

  /* 7 & 8. Leaderboard current user + hover clarity */
  .lb-row.current-user,
  .lb-row.me {
    background: rgba(245,200,66,0.09) !important;
    border-left: 2px solid var(--gold) !important;
    box-shadow: inset 0 0 0 1px rgba(245,200,66,0.16);
  }
  .lb-row:hover {
    background: rgba(255,255,255,0.055) !important;
  }

  /* 9. Scoring descriptions are readable but lower emphasis */
  .scoring-name {
    opacity: 0.84;
  }

  /* 10. Mobile/button tap feedback */
  .btn:active {
    transform: scale(0.98);
  }

  .submit-wrap {
    margin-top: 0.4rem;
  }

  @media (max-width: 640px) {
    /* 5. Sticky submit action area on mobile */
    .submit-wrap {
      position: sticky;
      bottom: 0;
      z-index: 30;
      background: rgba(10,4,18,0.96);
      border-top: 1px solid rgba(255,255,255,0.09);
      box-shadow: 0 -14px 34px rgba(0,0,0,0.32);
      padding: 0.8rem 0.65rem 0.95rem;
      margin-left: -0.65rem;
      margin-right: -0.65rem;
      backdrop-filter: blur(14px);
    }
    .submit-wrap .btn {
      min-width: 145px;
    }
  }


  /* ── Lean private leagues MVP ── */
  .league-panel {
    margin: 1rem auto 0;
    max-width: 680px;
    padding: 0.9rem 1rem;
    background:
      linear-gradient(135deg, rgba(255,45,154,0.08), rgba(245,200,66,0.045), rgba(155,93,229,0.08)),
      rgba(8,3,14,0.62);
    border: 1px solid rgba(245,200,66,0.18);
    border-radius: 4px;
    box-shadow: 0 12px 30px rgba(0,0,0,0.20);
    text-align: center;
  }
  .league-panel-title {
    font-size: 0.68rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--gold);
    font-weight: 800;
    margin-bottom: 0.55rem;
  }
  .league-row {
    display: flex;
    justify-content: center;
    gap: 0.5rem;
    flex-wrap: wrap;
    align-items: center;
  }
  .league-input {
    background: rgba(255,255,255,0.055);
    border: 1px solid rgba(255,255,255,0.12);
    border-radius: 2px;
    color: var(--text);
    font-family: 'DM Sans', sans-serif;
    font-size: 0.82rem;
    outline: none;
    padding: 0.52rem 0.75rem;
    min-width: 150px;
  }
  .league-input:focus {
    border-color: rgba(245,200,66,0.5);
  }
  .league-input::placeholder {
    color: rgba(138,106,170,0.62);
  }
  .league-active {
    display: inline-flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    gap: 0.45rem;
    font-size: 0.82rem;
    color: rgba(237,229,255,0.84);
  }
  .league-pill {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.26rem 0.55rem;
    border-radius: 2px;
    background: rgba(245,200,66,0.10);
    border: 1px solid rgba(245,200,66,0.22);
    color: var(--gold);
    font-weight: 800;
  }
  .league-share {
    width: min(100%, 520px);
    margin: 0.65rem auto 0;
    padding: 0.55rem 0.65rem;
    background: rgba(0,0,0,0.20);
    border: 1px solid rgba(255,255,255,0.07);
    border-radius: 3px;
    color: var(--muted);
    font-size: 0.72rem;
    word-break: break-word;
  }
  .leaderboard-toggle {
    display: flex;
    justify-content: center;
    gap: 0.55rem;
    flex-wrap: wrap;
    margin: 1rem 0 0.2rem;
  }
  .leaderboard-toggle .btn.active {
    color: #08030e;
    background: var(--gold);
    border-color: var(--gold);
  }
  .lb-header.league-mode,
  .lb-row.league-mode {
    grid-template-columns: 44px 1fr 120px 90px 60px;
  }
  .lb-league {
    font-size: 0.76rem;
    color: rgba(237,229,255,0.82);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  @media (max-width: 720px) {
    .lb-header.league-mode,
    .lb-row.league-mode {
      grid-template-columns: 36px 1fr 70px 50px;
    }
    .lb-league {
      display: none;
    }
    .lb-col.league-col {
      display: none;
    }
  }
  @media (max-width: 640px) {
    .league-panel {
      max-width: 94vw;
      padding: 0.8rem 0.75rem;
    }
    .league-input {
      width: 100%;
      min-width: 0;
    }
    .league-row .btn {
      width: 100%;
    }
  }


  /* ── Viral launch polish ── */
  .quick-how-card {
    width: min(100%, 820px);
    margin: 0 auto 1.25rem;
    padding: 0.95rem 1.15rem;
    background:
      linear-gradient(135deg, rgba(255,45,154,0.105), rgba(245,200,66,0.055), rgba(155,93,229,0.105)),
      rgba(16,6,29,0.78);
    border: 1px solid rgba(245,200,66,0.16);
    border-radius: 8px;
    box-shadow: 0 14px 36px rgba(0,0,0,0.20), inset 0 0 0 1px rgba(255,255,255,0.025);
    text-align: center;
  }
  .quick-how-title {
    font-size: 0.72rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--gold);
    font-weight: 800;
    margin-bottom: 0.35rem;
  }
  .quick-how-text {
    color: rgba(237,229,255,0.86);
    font-size: 0.92rem;
    line-height: 1.55;
  }
  .league-launch-line {
    margin-top: 0.55rem;
    font-size: 0.76rem;
    color: rgba(237,229,255,0.78);
    line-height: 1.45;
  }
  .league-launch-line strong {
    color: var(--gold);
  }
  .deadline-nudge {
    margin: 0.9rem auto 0;
    width: fit-content;
    max-width: 94vw;
    padding: 0.45rem 0.85rem;
    border-radius: 2px;
    background: rgba(248,113,113,0.075);
    border: 1px solid rgba(248,113,113,0.22);
    color: #fca5a5;
    font-size: 0.75rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    font-weight: 800;
  }
  .leaderboard-social-proof {
    width: fit-content;
    margin: 0.75rem auto 0;
    padding: 0.45rem 0.9rem;
    border-radius: 2px;
    background: rgba(255,45,154,0.08);
    border: 1px solid rgba(255,45,154,0.20);
    color: rgba(237,229,255,0.9);
    font-size: 0.8rem;
    font-weight: 700;
  }
  .mini-league-tag {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin: 0.15rem auto 0;
    padding: 0.18rem 0.5rem;
    border-radius: 2px;
    background: rgba(245,200,66,0.09);
    border: 1px solid rgba(245,200,66,0.18);
    color: var(--gold);
    font-size: 0.56rem;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    font-weight: 800;
    max-width: 90%;
  }
  .submit-celebration {
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 900;
    overflow: hidden;
  }
  .submit-celebration span {
    position: absolute;
    top: -28px;
    font-size: 1.25rem;
    animation: confettiFall 1.8s ease-out forwards;
  }
  .submit-celebration span:nth-child(1) { left: 10%; animation-delay: 0s; }
  .submit-celebration span:nth-child(2) { left: 22%; animation-delay: 0.08s; }
  .submit-celebration span:nth-child(3) { left: 34%; animation-delay: 0.02s; }
  .submit-celebration span:nth-child(4) { left: 46%; animation-delay: 0.12s; }
  .submit-celebration span:nth-child(5) { left: 58%; animation-delay: 0.04s; }
  .submit-celebration span:nth-child(6) { left: 70%; animation-delay: 0.1s; }
  .submit-celebration span:nth-child(7) { left: 82%; animation-delay: 0.02s; }
  .submit-celebration span:nth-child(8) { left: 92%; animation-delay: 0.14s; }
  @keyframes confettiFall {
    0% { transform: translateY(-30px) rotate(0deg) scale(0.9); opacity: 0; }
    12% { opacity: 1; }
    100% { transform: translateY(105vh) rotate(360deg) scale(1.1); opacity: 0; }
  }
  @media (max-width: 640px) {
    .quick-how-card {
      padding: 0.85rem 0.85rem;
      margin-bottom: 1rem;
    }
    .quick-how-text {
      font-size: 0.84rem;
    }
    .deadline-nudge {
      font-size: 0.68rem;
      line-height: 1.35;
    }
  }


  /* ── Final launch funnel copy + drag-name generator ── */
  .hero-action-line {
    margin: 0.95rem auto 0;
    width: fit-content;
    max-width: 94vw;
    padding: 0.42rem 0.85rem;
    border: 1px solid rgba(255,45,154,0.20);
    background: rgba(255,45,154,0.075);
    color: rgba(237,229,255,0.92);
    border-radius: 2px;
    font-size: 0.78rem;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }
  .name-generate-btn {
    white-space: nowrap;
  }
  .league-optional-line {
    margin-top: 0.5rem;
    color: rgba(237,229,255,0.70);
    font-size: 0.72rem;
    line-height: 1.4;
  }
  .time-to-play {
    text-align: center;
    margin: 0.15rem auto 0.85rem;
    color: rgba(237,229,255,0.76);
    font-size: 0.78rem;
    font-weight: 700;
  }
  .post-submit-prompt {
    width: min(100%, 680px);
    margin: 1.2rem auto 1rem;
    padding: 1rem 1.1rem;
    text-align: center;
    background:
      linear-gradient(135deg, rgba(245,200,66,0.09), rgba(255,45,154,0.08), rgba(155,93,229,0.08)),
      rgba(16,6,29,0.80);
    border: 1px solid rgba(245,200,66,0.18);
    border-radius: 8px;
    box-shadow: 0 14px 34px rgba(0,0,0,0.22);
  }
  .post-submit-prompt strong {
    color: var(--gold);
  }
  .joined-league-callout {
    margin: 0.55rem auto 0;
    width: fit-content;
    max-width: 94vw;
    padding: 0.38rem 0.75rem;
    border-radius: 2px;
    background: rgba(245,200,66,0.09);
    border: 1px solid rgba(245,200,66,0.20);
    color: var(--gold);
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }
  @media (max-width: 640px) {
    .hero-action-line {
      font-size: 0.68rem;
      line-height: 1.35;
      text-align: center;
    }
    .name-generate-btn {
      width: 100%;
    }
    .post-submit-prompt {
      padding: 0.9rem 0.85rem;
      font-size: 0.84rem;
    }
  }


  /* ── Create League double pulse + shimmer ── */
  @keyframes leaguePulse {
    0% { transform: scale(1); box-shadow: 0 0 0 rgba(255,45,154,0); }
    8% {
      transform: scale(1.04);
      box-shadow: 0 0 16px rgba(255,45,154,0.35), 0 0 28px rgba(245,200,66,0.18);
    }
    14% { transform: scale(1); }
    22% {
      transform: scale(1.04);
      box-shadow: 0 0 18px rgba(255,45,154,0.4), 0 0 32px rgba(245,200,66,0.22);
    }
    30%, 100% { transform: scale(1); box-shadow: 0 0 0 rgba(255,45,154,0); }
  }
  .league-pulse-active {
    animation: leaguePulse 7s ease-in-out infinite;
  }
  .league-pulse {
    position: relative;
    overflow: hidden;
  }
  .league-pulse::after {
    content: "";
    position: absolute;
    top: 0;
    left: -120%;
    width: 120%;
    height: 100%;
    background: linear-gradient(120deg, transparent, rgba(255,255,255,0.25), transparent);
    transition: all 0.6s ease;
  }
  .league-pulse:hover::after {
    left: 120%;
  }


  /* ── League panel clarity redesign ── */
  .league-panel-title {
    font-size: 0.95rem !important;
    letter-spacing: 0.16em !important;
    margin-bottom: 0.45rem !important;
  }
  .league-panel-copy {
    color: rgba(237,229,255,0.82);
    font-size: 0.82rem;
    line-height: 1.5;
    max-width: 560px;
    margin: 0.35rem auto 0.9rem;
  }
  .league-action-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.85rem;
    margin-top: 0.85rem;
  }
  .league-action-card {
    background:
      linear-gradient(135deg, rgba(255,45,154,0.08), rgba(245,200,66,0.045), rgba(155,93,229,0.08)),
      rgba(0,0,0,0.16);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 6px;
    padding: 0.85rem;
  }
  .league-action-title {
    color: var(--gold);
    font-size: 0.68rem;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    font-weight: 800;
    margin-bottom: 0.6rem;
  }
  .league-action-card .league-row {
    flex-direction: column;
  }
  .league-action-card .league-input,
  .league-action-card .btn {
    width: 100%;
  }
  @media (max-width: 720px) {
    .league-action-grid {
      grid-template-columns: 1fr;
    }
  }


  .launch-only-hidden {
    display: none !important;
  }


  /* ── Post-Episode-1 leaderboard design: rank, player, league, points, change ── */
  .lb-header.post-ep,
  .lb-row.post-ep {
    grid-template-columns: 56px minmax(180px, 1.6fr) minmax(110px, 0.8fr) 78px 86px !important;
    gap: 0.75rem !important;
    align-items: center !important;
  }
  .lb-header.post-ep {
    color: rgba(237,229,255,0.64) !important;
    font-size: 0.66rem !important;
    letter-spacing: 0.16em !important;
    text-transform: uppercase !important;
  }
  .lb-row.post-ep {
    min-height: 66px !important;
    padding-top: 0.72rem !important;
    padding-bottom: 0.72rem !important;
  }
  .lb-row.post-ep .lb-rank {
    font-size: 1.35rem !important;
    font-weight: 900 !important;
  }
  .lb-row.post-ep .lb-name button {
    font-size: 0.98rem !important;
    font-weight: 800 !important;
  }
  .lb-row.post-ep .lb-name span:not(.badge-you) {
    font-size: 0.72rem !important;
    color: rgba(237,229,255,0.62) !important;
    margin-top: 0.18rem !important;
  }
  .lb-row.post-ep .lb-league {
    font-size: 0.78rem !important;
    color: rgba(237,229,255,0.72) !important;
  }
  .lb-row.post-ep .lb-score {
    font-family: 'Bebas Neue', sans-serif !important;
    font-size: 1.55rem !important;
    color: var(--gold) !important;
    text-align: right !important;
    letter-spacing: 0.03em !important;
  }
  .lb-change {
    text-align: right;
    font-size: 0.82rem;
    font-weight: 900;
    white-space: nowrap;
  }
  .lb-change.up {
    color: var(--green);
  }
  .lb-change.down {
    color: var(--red);
  }
  .lb-change.same {
    color: rgba(237,229,255,0.48);
  }
  .league-context-card {
    width: fit-content;
    max-width: 94vw;
    margin: 0.85rem auto 0;
    padding: 0.55rem 0.95rem;
    border-radius: 3px;
    background:
      linear-gradient(135deg, rgba(245,200,66,0.08), rgba(255,45,154,0.055), rgba(155,93,229,0.08)),
      rgba(16,6,29,0.74);
    border: 1px solid rgba(245,200,66,0.16);
    color: rgba(237,229,255,0.86);
    font-size: 0.78rem;
    font-weight: 700;
    text-align: center;
  }
  @media (max-width: 780px) {
    .lb-header.post-ep,
    .lb-row.post-ep {
      grid-template-columns: 42px minmax(0, 1fr) 70px 66px !important;
      gap: 0.5rem !important;
    }
    .lb-header.post-ep .league-col,
    .lb-row.post-ep .lb-league {
      display: none !important;
    }
    .lb-row.post-ep .lb-name button {
      font-size: 0.9rem !important;
    }
    .lb-row.post-ep .lb-score {
      font-size: 1.35rem !important;
    }
    .lb-change {
      font-size: 0.74rem;
    }
  }


  /* ── Prominent email signup + centered support polish ── */
  .email-prominent {
    margin: 2rem auto !important;
    padding: 1.5rem 1.3rem !important;
    max-width: 720px !important;
    background:
      linear-gradient(135deg, rgba(245,200,66,0.12), rgba(255,45,154,0.10), rgba(155,93,229,0.12)),
      rgba(16,6,29,0.9) !important;
    border: 1px solid rgba(245,200,66,0.3) !important;
    border-radius: 12px !important;
    box-shadow:
      0 20px 60px rgba(0,0,0,0.4),
      inset 0 0 0 1px rgba(255,255,255,0.04) !important;
    text-align: center !important;
  }
  .email-title {
    font-size: 1.2rem;
    font-weight: 900;
    color: var(--gold);
    margin-bottom: 0.5rem;
  }
  .email-sub {
    font-size: 0.9rem;
    color: rgba(237,229,255,0.8);
    margin-bottom: 1rem;
    line-height: 1.5;
  }
  .email-input-row {
    display: flex;
    gap: 0.6rem;
    justify-content: center;
    flex-wrap: wrap;
  }
  .email-trust {
    margin-top: 0.7rem;
    font-size: 0.72rem;
    color: rgba(237,229,255,0.6);
  }
  .support-section {
    text-align: center !important;
    margin: 2rem auto !important;
    max-width: 600px !important;
  }
  .support-text {
    color: rgba(237,229,255,0.85) !important;
    font-size: 0.9rem !important;
    line-height: 1.6 !important;
    margin-bottom: 0.9rem !important;
    text-align: center !important;
  }
  .support-button {
    display: inline-block;
    padding: 0.65rem 1.3rem;
    background: linear-gradient(135deg, #ff2d9a, #9b5de5);
    color: white;
    font-weight: 900;
    font-size: 0.95rem;
    border-radius: 8px;
    text-decoration: none;
    transition: transform 0.15s ease, box-shadow 0.15s ease;
  }
  .support-button:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 24px rgba(255,45,154,0.35);
  }


  .share-league {
    margin-top: 0.25rem;
    font-size: 0.75rem;
    font-weight: 700;
    color: var(--gold);
    letter-spacing: 0.05em;
    text-transform: uppercase;
    opacity: 0.95;
  }


  /* ── Scoring card row alignment polish ── */
  .scoring-grid {
    align-items: stretch !important;
  }
  .scoring-item {
    justify-content: flex-start !important;
    display: grid !important;
    grid-template-rows: 2.35rem 1.65rem 1fr !important;
    align-items: start !important;
    min-height: 150px !important;
  }
  .scoring-item .scoring-pts {
    height: 2.35rem !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    font-size: 1.75rem !important;
    line-height: 1 !important;
    margin: 0 !important;
    width: 100% !important;
  }
  .scoring-item > div:nth-child(2) {
    height: 1.65rem !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    text-align: center !important;
    width: 100% !important;
    margin: 0 !important;
    line-height: 1.15 !important;
  }
  .scoring-item .scoring-name {
    align-self: start !important;
    margin-top: 0.35rem !important;
  }


  .browser-edit-notice-backdrop {
    position: fixed;
    inset: 0;
    z-index: 950;
    background: rgba(0,0,0,0.58);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
  }
  .browser-edit-notice-card {
    width: min(92vw, 460px);
    background:
      linear-gradient(135deg, rgba(255,45,154,0.10), rgba(245,200,66,0.06), rgba(155,93,229,0.10)),
      rgba(16,6,29,0.96);
    border: 1px solid rgba(245,200,66,0.24);
    border-radius: 10px;
    box-shadow: 0 22px 70px rgba(0,0,0,0.48);
    padding: 1.35rem;
    text-align: center;
  }
  .browser-edit-notice-title {
    color: var(--gold);
    font-weight: 900;
    font-size: 1rem;
    margin-bottom: 0.55rem;
  }
  .browser-edit-notice-text {
    color: rgba(237,229,255,0.82);
    font-size: 0.86rem;
    line-height: 1.55;
    margin-bottom: 1rem;
  }


  /* ── Share card long-name fit polish ── */
  .share-overall {
    margin-top: 0.46rem !important;
    gap: 0.46rem !important;
  }
  .share-overall-card {
    padding: 0.38rem 0.54rem !important;
    min-height: 68px !important;
  }
  .share-overall-label {
    font-size: clamp(0.48rem, 1.35vw, 0.62rem) !important;
    letter-spacing: 0.16em !important;
    margin-bottom: 0.14rem !important;
  }
  .share-overall-pick {
    grid-template-columns: 1.08rem 1fr !important;
    gap: 0.18rem !important;
    font-size: clamp(0.62rem, 1.78vw, 0.78rem) !important;
    line-height: 1.08 !important;
    align-items: start !important;
  }
  .share-overall-pick span:last-child {
    overflow-wrap: anywhere !important;
    word-break: normal !important;
  }
  .share-card-brand {
    background: rgba(2,1,6,0.78) !important;
  }


  /* ── Analysis tab + You vs Crowd share card ── */
  .analysis-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.9rem;
    margin-top: 1.15rem;
  }
  .analysis-card {
    background:
      linear-gradient(135deg, rgba(255,45,154,0.09), rgba(245,200,66,0.045), rgba(155,93,229,0.09)),
      rgba(16,6,29,0.84);
    border: 1px solid rgba(255,255,255,0.10);
    border-radius: 9px;
    padding: 1rem;
    box-shadow: 0 14px 36px rgba(0,0,0,0.22), inset 0 0 0 1px rgba(255,255,255,0.025);
  }
  .analysis-card-title {
    color: var(--gold);
    font-size: 0.72rem;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    font-weight: 900;
    margin-bottom: 0.65rem;
  }
  .analysis-big-pick {
    font-family: 'Bebas Neue', sans-serif;
    font-size: clamp(1.4rem, 4vw, 2.1rem);
    line-height: 0.95;
    color: #fff;
    text-shadow: 0 2px 18px rgba(0,0,0,0.35);
  }
  .analysis-subtle {
    color: rgba(237,229,255,0.68);
    font-size: 0.78rem;
    line-height: 1.5;
    margin-top: 0.4rem;
  }
  .analysis-list {
    display: grid;
    gap: 0.5rem;
  }
  .analysis-row {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 0.75rem;
    align-items: center;
    color: rgba(237,229,255,0.9);
    font-size: 0.86rem;
  }
  .analysis-row-name {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .analysis-row-count {
    color: var(--gold);
    font-weight: 900;
    font-size: 0.82rem;
  }
  .analysis-bar {
    grid-column: 1 / -1;
    height: 6px;
    background: rgba(255,255,255,0.07);
    border-radius: 999px;
    overflow: hidden;
    margin-top: -0.25rem;
  }
  .analysis-bar-fill {
    height: 100%;
    border-radius: 999px;
    background: linear-gradient(90deg, var(--pink), var(--gold));
  }
  .crowd-compare {
    margin-top: 1.3rem;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.9rem;
  }
  .underdog-pill {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.22rem 0.5rem;
    margin: 0.18rem;
    border: 1px solid rgba(245,200,66,0.18);
    border-radius: 999px;
    background: rgba(245,200,66,0.075);
    color: rgba(255,236,160,0.96);
    font-size: 0.76rem;
    font-weight: 800;
  }

  .crowd-share-wrap {
    margin: 1.5rem auto 0;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  .crowd-share-card {
    width: min(94vw, 500px);
    aspect-ratio: 4 / 5;
    border-radius: 22px;
    overflow: hidden;
    position: relative;
    color: var(--text);
    border: 3px solid transparent;
    background:
      radial-gradient(circle at 12% 10%, rgba(245,200,66,0.25), transparent 26%),
      radial-gradient(circle at 90% 12%, rgba(255,45,154,0.24), transparent 30%),
      radial-gradient(circle at 75% 82%, rgba(155,93,229,0.22), transparent 34%),
      linear-gradient(180deg, #f5c842 0%, #ff7b39 3%, #1a0827 16%, #10051c 62%, #06020c 100%);
    box-shadow: 0 20px 70px rgba(0,0,0,0.55), inset 0 0 0 2px rgba(255,255,255,0.04);
    padding: 1.2rem;
    display: flex;
    flex-direction: column;
  }
  .crowd-share-inner {
    flex: 1;
    border-radius: 18px;
    background:
      linear-gradient(180deg, rgba(255,255,255,0.065), rgba(255,255,255,0.02)),
      rgba(5,2,10,0.70);
    border: 1px solid rgba(255,255,255,0.12);
    padding: 1.1rem;
    display: flex;
    flex-direction: column;
  }
  .crowd-share-kicker {
    text-align: center;
    color: var(--gold);
    font-weight: 900;
    font-size: 0.72rem;
    letter-spacing: 0.24em;
    text-transform: uppercase;
  }
  .crowd-share-title {
    font-family: 'Bebas Neue', sans-serif;
    text-align: center;
    font-size: clamp(2.2rem, 8vw, 3.4rem);
    line-height: 0.92;
    margin: 0.45rem 0 0.2rem;
    background: linear-gradient(90deg, #fff 0%, #ffe48a 30%, #ff7b39 55%, #ff2d9a 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    filter: drop-shadow(0 3px 12px rgba(0,0,0,0.35));
  }
  .crowd-share-sub {
    text-align: center;
    color: rgba(237,229,255,0.78);
    text-transform: uppercase;
    letter-spacing: 0.16em;
    font-size: 0.68rem;
    font-weight: 900;
    margin-bottom: 0.9rem;
  }
  .crowd-share-versus {
    display: grid;
    gap: 0.55rem;
    margin-top: 0.25rem;
  }
  .crowd-share-box {
    background: rgba(255,255,255,0.055);
    border: 1px solid rgba(255,255,255,0.10);
    border-left: 6px solid var(--gold);
    border-radius: 10px;
    padding: 0.75rem 0.85rem;
  }
  .crowd-share-label {
    color: var(--gold);
    font-size: 0.62rem;
    letter-spacing: 0.16em;
    font-weight: 900;
    text-transform: uppercase;
    margin-bottom: 0.25rem;
  }
  .crowd-share-pick {
    font-size: clamp(0.92rem, 3.1vw, 1.15rem);
    font-weight: 900;
    color: #fff;
    line-height: 1.18;
  }
  .crowd-share-underdogs {
    margin-top: 0.75rem;
    padding: 0.75rem 0.85rem;
    border-radius: 10px;
    background: rgba(245,200,66,0.08);
    border: 1px solid rgba(245,200,66,0.16);
  }
  .crowd-share-footer {
    margin-top: auto;
    text-align: center;
    padding-top: 0.9rem;
  }
  .crowd-share-footer-small {
    color: var(--gold);
    font-size: 0.62rem;
    letter-spacing: 0.18em;
    font-weight: 900;
    text-transform: uppercase;
  }
  .crowd-share-url {
    font-family: 'Bebas Neue', sans-serif;
    color: #fff;
    font-size: 1.5rem;
    letter-spacing: 0.06em;
    line-height: 1;
    margin-top: 0.12rem;
  }
  .crowd-share-actions {
    display: flex;
    gap: 0.65rem;
    justify-content: center;
    flex-wrap: wrap;
    margin-top: 0.9rem;
  }

  @media (max-width: 900px) {
    .analysis-grid,
    .crowd-compare {
      grid-template-columns: 1fr;
    }
  }


  /* ── Crowd share card: original-card style match ── */
  .crowd-share-card {
    width: min(94vw, 520px) !important;
    aspect-ratio: 4 / 5 !important;
    border-radius: 26px !important;
    padding: 0 !important;
    border: 3px solid transparent !important;
    background:
      linear-gradient(#12051d, #12051d) padding-box,
      linear-gradient(135deg, #f5c842 0%, #ff7b39 28%, #ff2d9a 68%, #9b5de5 100%) border-box !important;
    box-shadow: 0 22px 70px rgba(0,0,0,0.52) !important;
  }
  .crowd-share-inner {
    width: 100% !important;
    height: 100% !important;
    border-radius: 22px !important;
    padding: 1.7rem 1.5rem 1.25rem !important;
    border: none !important;
    background:
      radial-gradient(circle at 10% 0%, rgba(245,200,66,0.16), transparent 30%),
      radial-gradient(circle at 92% 6%, rgba(255,45,154,0.17), transparent 34%),
      radial-gradient(circle at 50% 78%, rgba(155,93,229,0.18), transparent 36%),
      linear-gradient(180deg, #261026 0%, #15081e 42%, #07020c 100%) !important;
    box-shadow: inset 0 0 0 1px rgba(255,255,255,0.08) !important;
  }
  .crowd-share-kicker {
    color: var(--gold) !important;
    font-size: clamp(0.7rem, 2.2vw, 0.88rem) !important;
    letter-spacing: 0.24em !important;
    margin-top: 0.2rem !important;
  }
  .crowd-share-title {
    font-size: clamp(2.7rem, 10.5vw, 4.15rem) !important;
    line-height: 0.92 !important;
    margin: 0.45rem auto 0.25rem !important;
    max-width: 96% !important;
    background: linear-gradient(90deg, #fff4c8 0%, #ffd15c 33%, #ff7b39 58%, #ff2d9a 100%) !important;
    -webkit-background-clip: text !important;
    -webkit-text-fill-color: transparent !important;
  }
  .crowd-share-sub {
    color: rgba(210,166,255,0.88) !important;
    font-size: clamp(0.68rem, 2.05vw, 0.82rem) !important;
    letter-spacing: 0.24em !important;
    margin-bottom: 1rem !important;
  }
  .crowd-share-versus {
    gap: 0.62rem !important;
    margin-top: 0.35rem !important;
  }
  .crowd-share-box {
    background: rgba(255,255,255,0.045) !important;
    border: 1px solid rgba(255,255,255,0.12) !important;
    border-left: 7px solid var(--gold) !important;
    border-radius: 12px !important;
    padding: 0.82rem 0.95rem !important;
    min-height: 78px !important;
  }
  .crowd-share-label {
    color: var(--gold) !important;
    font-size: clamp(0.58rem, 1.75vw, 0.7rem) !important;
    letter-spacing: 0.18em !important;
    margin-bottom: 0.32rem !important;
  }
  .crowd-share-pick {
    font-size: clamp(0.95rem, 3.25vw, 1.18rem) !important;
    line-height: 1.14 !important;
  }
  .crowd-share-underdogs {
    margin-top: 0.65rem !important;
    padding: 0.78rem 0.95rem !important;
    border-radius: 12px !important;
    background: rgba(245,200,66,0.075) !important;
    border: 1px solid rgba(245,200,66,0.18) !important;
  }
  .crowd-share-footer {
    margin-top: auto !important;
    padding-top: 1rem !important;
    text-align: center !important;
  }
  .crowd-share-footer-small {
    color: var(--gold) !important;
    font-size: clamp(0.58rem, 1.8vw, 0.7rem) !important;
    letter-spacing: 0.18em !important;
  }
  .crowd-share-url {
    font-size: clamp(1.65rem, 5.2vw, 2.1rem) !important;
    color: #fff !important;
    letter-spacing: 0.045em !important;
  }


  /* ── Centered support block on utility pages ── */
  .support-section {
    text-align: center !important;
    margin: 2rem auto !important;
    max-width: 600px !important;
  }
  .support-text {
    color: rgba(237,229,255,0.85) !important;
    font-size: 0.9rem !important;
    line-height: 1.6 !important;
    margin-bottom: 0.9rem !important;
    text-align: center !important;
  }
  .support-button {
    display: inline-block;
    padding: 0.65rem 1.3rem;
    background: linear-gradient(135deg, #ff2d9a, #9b5de5);
    color: white;
    font-weight: 900;
    font-size: 0.95rem;
    border-radius: 8px;
    text-decoration: none;
    transition: transform 0.15s ease, box-shadow 0.15s ease;
  }
  .support-button:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 24px rgba(255,45,154,0.35);
  }


  /* ── Analysis share-card subtle animation + no-points polish ── */
  @keyframes crowdShareSoftGlow {
    0%, 100% {
      box-shadow:
        0 22px 70px rgba(0,0,0,0.52),
        0 0 0 rgba(245,200,66,0);
      transform: translateY(0);
    }
    50% {
      box-shadow:
        0 24px 78px rgba(0,0,0,0.56),
        0 0 26px rgba(245,200,66,0.10),
        0 0 42px rgba(255,45,154,0.08);
      transform: translateY(-2px);
    }
  }
  .crowd-share-card {
    animation: crowdShareSoftGlow 5.8s ease-in-out infinite;
  }
  .share-results-label {
    text-align: center;
    margin: 0 auto 0.7rem;
    font-size: 0.72rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    font-weight: 900;
    color: rgba(237,229,255,0.62);
  }
  .no-points-message {
    color: rgba(237,229,255,0.88) !important;
    font-size: 1rem !important;
    line-height: 1.5 !important;
    align-self: center !important;
    justify-self: center !important;
    text-align: center !important;
    max-width: 360px !important;
    padding: 0.9rem 1rem !important;
    border-radius: 8px !important;
    background: rgba(245,200,66,0.075) !important;
    border: 1px solid rgba(245,200,66,0.16) !important;
  }


  /* ── Bracket advancement analysis columns ── */
  .bracket-advance-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.9rem;
    margin-top: 0.9rem;
  }
  .bracket-advance-card {
    background: rgba(255,255,255,0.035);
    border: 1px solid rgba(255,255,255,0.09);
    border-radius: 9px;
    padding: 0.85rem;
  }
  .bracket-advance-card.orange {
    background:
      linear-gradient(145deg, rgba(255,107,53,0.14), rgba(255,107,53,0.04) 45%, rgba(255,255,255,0.025)),
      rgba(255,255,255,0.025);
    border-color: rgba(255,107,53,0.18);
  }
  .bracket-advance-card.pink {
    background:
      linear-gradient(145deg, rgba(255,45,154,0.14), rgba(255,45,154,0.04) 45%, rgba(255,255,255,0.025)),
      rgba(255,255,255,0.025);
    border-color: rgba(255,45,154,0.18);
  }
  .bracket-advance-card.purple {
    background:
      linear-gradient(145deg, rgba(155,93,229,0.16), rgba(155,93,229,0.045) 45%, rgba(255,255,255,0.025)),
      rgba(255,255,255,0.025);
    border-color: rgba(155,93,229,0.20);
  }
  .bracket-advance-title {
    font-size: 0.72rem;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    font-weight: 900;
    color: var(--gold);
    margin-bottom: 0.7rem;
    text-align: center;
  }
  .bracket-advance-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 0.55rem;
    align-items: center;
    margin-bottom: 0.48rem;
    font-size: 0.82rem;
  }
  .bracket-advance-name {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: rgba(237,229,255,0.9);
  }
  .bracket-advance-pct {
    color: var(--gold);
    font-weight: 900;
    font-size: 0.8rem;
  }
  .bracket-advance-bar {
    grid-column: 1 / -1;
    height: 6px;
    background: rgba(255,255,255,0.07);
    border-radius: 999px;
    overflow: hidden;
    margin-top: -0.22rem;
  }
  .bracket-advance-bar-fill {
    height: 100%;
    border-radius: 999px;
    background: linear-gradient(90deg, var(--pink), var(--gold));
  }
  @media (max-width: 900px) {
    .bracket-advance-grid {
      grid-template-columns: 1fr;
    }
  }


  /* ── Community / subreddit hub ── */
  .community-hero {
    width: min(100%, 860px);
    margin: 0 auto;
    padding: 1.55rem 1.35rem;
    text-align: center;
    background:
      radial-gradient(circle at 10% 0%, rgba(245,200,66,0.16), transparent 32%),
      radial-gradient(circle at 92% 10%, rgba(255,45,154,0.16), transparent 34%),
      linear-gradient(135deg, rgba(255,45,154,0.10), rgba(245,200,66,0.06), rgba(155,93,229,0.11)),
      rgba(16,6,29,0.86);
    border: 1px solid rgba(245,200,66,0.22);
    border-radius: 12px;
    box-shadow: 0 18px 54px rgba(0,0,0,0.32), inset 0 0 0 1px rgba(255,255,255,0.035);
  }
  .community-title {
    font-family: 'Bebas Neue', sans-serif;
    font-size: clamp(2.2rem, 8vw, 3.8rem);
    line-height: 0.95;
    color: var(--gold);
    letter-spacing: 0.03em;
    margin-bottom: 0.45rem;
    text-shadow: 0 4px 22px rgba(0,0,0,0.35);
  }
  .community-copy {
    max-width: 620px;
    margin: 0 auto 1rem;
    color: rgba(237,229,255,0.82);
    font-size: 0.92rem;
    line-height: 1.65;
  }
  .community-post-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.9rem;
    margin-top: 1rem;
  }
  .community-post-card {
    display: block;
    text-decoration: none;
    color: var(--text);
    padding: 1rem;
    border-radius: 10px;
    background:
      linear-gradient(145deg, rgba(255,45,154,0.08), rgba(245,200,66,0.04), rgba(155,93,229,0.08)),
      rgba(255,255,255,0.035);
    border: 1px solid rgba(255,255,255,0.09);
    box-shadow: 0 12px 32px rgba(0,0,0,0.20);
    transition: transform 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;
  }
  .community-post-card:hover {
    transform: translateY(-2px);
    border-color: rgba(245,200,66,0.28);
    box-shadow: 0 16px 40px rgba(0,0,0,0.28), 0 0 24px rgba(245,200,66,0.08);
  }
  .community-post-kicker {
    color: var(--gold);
    font-size: 0.64rem;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    font-weight: 900;
    margin-bottom: 0.45rem;
  }
  .community-post-title {
    font-weight: 900;
    line-height: 1.25;
    font-size: 0.92rem;
  }
  .community-bottom-cta {
    width: min(100%, 760px);
    margin: 1.25rem auto 0;
    text-align: center;
    padding: 1rem;
    border-radius: 10px;
    background: rgba(0,0,0,0.18);
    border: 1px solid rgba(255,255,255,0.08);
  }
  .community-inline-link {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.3rem;
    margin: 0.9rem auto 0;
    padding: 0.55rem 0.85rem;
    border-radius: 999px;
    background: rgba(255,45,154,0.08);
    border: 1px solid rgba(255,45,154,0.18);
    color: rgba(237,229,255,0.88);
    font-size: 0.78rem;
    font-weight: 800;
    text-decoration: none;
    transition: transform 0.16s ease, box-shadow 0.16s ease;
  }
  .community-inline-link:hover {
    transform: translateY(-1px);
    box-shadow: 0 0 18px rgba(255,45,154,0.13);
  }
  @media (max-width: 860px) {
    .community-post-grid {
      grid-template-columns: 1fr;
    }
  }


  .community-page-clean {
    padding-top: 1.2rem;
  }


  /* ── Premium dashboard polish for Scoring + Analysis ── */
  .dashboard-kpi-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 0.85rem;
    margin: 1rem 0 1.15rem;
  }
  .dashboard-kpi-card,
  .dashboard-insight-card {
    position: relative;
    overflow: hidden;
    background:
      linear-gradient(145deg, rgba(255,45,154,0.085), rgba(245,200,66,0.045), rgba(155,93,229,0.085)),
      rgba(16,6,29,0.86);
    border: 1px solid rgba(255,255,255,0.10);
    border-radius: 12px;
    box-shadow:
      0 18px 50px rgba(0,0,0,0.28),
      inset 0 0 0 1px rgba(255,255,255,0.035);
    padding: 1rem;
    transition: transform 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;
  }
  .dashboard-kpi-card:hover,
  .dashboard-insight-card:hover,
  .analysis-card:hover,
  .scoring-item:hover,
  .scoring-note-card:hover {
    transform: translateY(-2px);
    border-color: rgba(245,200,66,0.24);
    box-shadow:
      0 22px 58px rgba(0,0,0,0.34),
      0 0 26px rgba(245,200,66,0.055),
      inset 0 0 0 1px rgba(255,255,255,0.04);
  }
  .dashboard-kpi-label {
    font-size: 0.64rem;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: rgba(237,229,255,0.62);
    font-weight: 900;
    margin-bottom: 0.35rem;
  }
  .dashboard-kpi-value {
    font-family: 'Bebas Neue', sans-serif;
    color: var(--gold);
    font-size: clamp(1.8rem, 5vw, 2.6rem);
    line-height: 0.95;
    letter-spacing: 0.03em;
    text-shadow: 0 4px 20px rgba(0,0,0,0.32);
  }
  .dashboard-kpi-sub {
    color: rgba(237,229,255,0.68);
    font-size: 0.76rem;
    line-height: 1.4;
    margin-top: 0.3rem;
  }
  .dashboard-insight-card {
    margin: 1rem auto 1.1rem;
    text-align: center;
    width: min(100%, 900px);
    padding: 1.15rem;
  }
  .dashboard-insight-label {
    color: rgba(237,229,255,0.62);
    font-size: 0.68rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    font-weight: 900;
    margin-bottom: 0.38rem;
  }
  .dashboard-insight-main {
    font-family: 'Bebas Neue', sans-serif;
    font-size: clamp(2rem, 7vw, 3.2rem);
    line-height: 0.98;
    color: #fff;
    text-shadow: 0 6px 26px rgba(0,0,0,0.36);
  }
  .dashboard-insight-sub {
    margin-top: 0.45rem;
    color: rgba(237,229,255,0.74);
    font-size: 0.85rem;
  }
  .dashboard-section-header {
    display: flex;
    align-items: end;
    justify-content: space-between;
    gap: 0.75rem;
    border-bottom: 1px solid rgba(255,255,255,0.07);
    padding-bottom: 0.65rem;
    margin: 1.25rem 0 0.85rem;
  }
  .dashboard-section-title {
    color: var(--gold);
    font-size: 0.76rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    font-weight: 900;
  }
  .dashboard-section-note {
    color: rgba(237,229,255,0.54);
    font-size: 0.74rem;
    text-align: right;
  }
  .dashboard-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    padding: 0.32rem 0.62rem;
    border-radius: 999px;
    border: 1px solid rgba(245,200,66,0.20);
    background: rgba(245,200,66,0.08);
    color: var(--gold);
    font-weight: 900;
    letter-spacing: 0.08em;
    font-size: 0.68rem;
    text-transform: uppercase;
  }
  .analysis-card,
  .scoring-note-card,
  .scoring-item {
    transition: transform 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;
  }
  .analysis-bar-fill,
  .bracket-advance-bar-fill,
  .progress-fill,
  .lb-bar-fill {
    animation: barGrowPremium 0.7s ease-out both;
    transform-origin: left center;
  }
  @keyframes barGrowPremium {
    from { transform: scaleX(0); }
    to { transform: scaleX(1); }
  }
  .analysis-row:first-child .analysis-row-name,
  .bracket-advance-row:first-of-type .bracket-advance-name {
    color: #fff;
    font-weight: 900;
  }
  .analysis-row:first-child .analysis-row-count,
  .bracket-advance-row:first-of-type .bracket-advance-pct {
    text-shadow: 0 0 18px rgba(245,200,66,0.28);
  }
  @media (max-width: 920px) {
    .dashboard-kpi-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }
  @media (max-width: 560px) {
    .dashboard-kpi-grid {
      grid-template-columns: 1fr;
    }
    .dashboard-section-header {
      align-items: start;
      flex-direction: column;
    }
    .dashboard-section-note {
      text-align: left;
    }
  }


  /* ── Premium leaderboard dashboard polish ── */
  .leaderboard-dashboard-top {
    margin: 1rem 0 1.15rem;
  }
  .leaderboard-insight-card {
    position: relative;
    overflow: hidden;
    text-align: center;
    margin: 1rem auto 1.15rem;
    width: min(100%, 900px);
    padding: 1.1rem 1rem;
    background:
      radial-gradient(circle at 15% 0%, rgba(245,200,66,0.14), transparent 35%),
      radial-gradient(circle at 90% 10%, rgba(255,45,154,0.12), transparent 35%),
      linear-gradient(145deg, rgba(255,45,154,0.085), rgba(245,200,66,0.045), rgba(155,93,229,0.085)),
      rgba(16,6,29,0.86);
    border: 1px solid rgba(245,200,66,0.18);
    border-radius: 12px;
    box-shadow:
      0 18px 50px rgba(0,0,0,0.30),
      inset 0 0 0 1px rgba(255,255,255,0.035);
  }
  .leaderboard-insight-label {
    color: rgba(237,229,255,0.62);
    font-size: 0.68rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    font-weight: 900;
    margin-bottom: 0.35rem;
  }
  .leaderboard-insight-main {
    font-family: 'Bebas Neue', sans-serif;
    font-size: clamp(2rem, 7vw, 3.25rem);
    line-height: 0.98;
    color: #fff;
    text-shadow: 0 6px 26px rgba(0,0,0,0.36);
  }
  .leaderboard-insight-sub {
    margin-top: 0.42rem;
    color: rgba(237,229,255,0.72);
    font-size: 0.84rem;
  }
  .leaderboard-table-card {
    margin-top: 1rem;
    background:
      linear-gradient(145deg, rgba(255,45,154,0.045), rgba(245,200,66,0.025), rgba(155,93,229,0.05)),
      rgba(16,6,29,0.72);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 12px;
    padding: 0.8rem;
    box-shadow: 0 16px 42px rgba(0,0,0,0.24), inset 0 0 0 1px rgba(255,255,255,0.025);
  }
  .lb-row.post-ep {
    transition: transform 0.18s ease, background 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;
  }
  .lb-row.post-ep:hover {
    transform: translateY(-1px);
    box-shadow: 0 12px 30px rgba(0,0,0,0.22);
  }


  .dashboard-kpi-grid {
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)) !important;
  }


  /* ── Bracket setup card cleanup ── */
  .bracket-setup-card {
    width: min(100%, 860px);
    margin: 1rem auto 0;
    padding: 1rem 1.05rem;
    background:
      linear-gradient(135deg, rgba(255,45,154,0.08), rgba(245,200,66,0.045), rgba(155,93,229,0.08)),
      rgba(16,6,29,0.84);
    border: 1px solid rgba(245,200,66,0.16);
    border-radius: 10px;
    box-shadow: 0 14px 38px rgba(0,0,0,0.24), inset 0 0 0 1px rgba(255,255,255,0.03);
  }
  .bracket-setup-title {
    color: var(--gold);
    font-size: 0.72rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    font-weight: 900;
    text-align: center;
    margin-bottom: 0.8rem;
  }
  .bracket-setup-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1.15fr);
    gap: 0.85rem;
    align-items: stretch;
  }
  .bracket-setup-section {
    border-radius: 8px;
    background: rgba(0,0,0,0.18);
    border: 1px solid rgba(255,255,255,0.075);
    padding: 0.8rem;
  }
  .bracket-setup-label {
    color: rgba(237,229,255,0.58);
    font-size: 0.62rem;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    font-weight: 900;
    margin-bottom: 0.45rem;
  }
  .bracket-setup-current {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.6rem;
    flex-wrap: wrap;
    color: rgba(237,229,255,0.82);
    font-size: 0.85rem;
  }
  .bracket-setup-current strong {
    color: var(--gold);
  }
  .bracket-setup-actions {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
    align-items: center;
  }
  .bracket-setup-actions .username-input,
  .bracket-setup-actions .league-input {
    flex: 1 1 190px;
    min-width: 0;
  }
  .bracket-setup-helper {
    margin-top: 0.5rem;
    color: rgba(237,229,255,0.62);
    font-size: 0.72rem;
    line-height: 1.45;
  }
  @media (max-width: 780px) {
    .bracket-setup-grid {
      grid-template-columns: 1fr;
    }
    .bracket-setup-card {
      padding: 0.85rem;
    }
  }


  /* ── Guided bracket stepper + progressive setup ── */
  .bracket-stepper-card {
    width: min(100%, 900px);
    margin: 1rem auto 0;
    padding: 0.95rem 1rem;
    background:
      linear-gradient(135deg, rgba(255,45,154,0.07), rgba(245,200,66,0.04), rgba(155,93,229,0.08)),
      rgba(16,6,29,0.80);
    border: 1px solid rgba(245,200,66,0.14);
    border-radius: 10px;
    box-shadow: 0 14px 38px rgba(0,0,0,0.22), inset 0 0 0 1px rgba(255,255,255,0.03);
  }
  .bracket-stepper-title {
    text-align: center;
    color: var(--gold);
    font-size: 0.68rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    font-weight: 900;
    margin-bottom: 0.8rem;
  }
  .bracket-stepper-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 34px minmax(0, 1fr) 34px minmax(0, 1fr);
    align-items: center;
    gap: 0.35rem;
  }
  .bracket-step {
    min-height: 54px;
    border-radius: 9px;
    display: flex;
    align-items: center;
    gap: 0.55rem;
    padding: 0.62rem 0.7rem;
    border: 1px solid rgba(255,255,255,0.07);
    background: rgba(0,0,0,0.16);
    color: rgba(237,229,255,0.58);
    transition: transform 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
  }
  .bracket-step.done {
    color: rgba(237,229,255,0.72);
    background: rgba(34,197,94,0.055);
    border-color: rgba(34,197,94,0.16);
  }
  .bracket-step.active {
    color: var(--gold);
    background:
      radial-gradient(circle at 10% 0%, rgba(245,200,66,0.18), transparent 35%),
      rgba(245,200,66,0.07);
    border-color: rgba(245,200,66,0.30);
    box-shadow:
      0 0 18px rgba(245,200,66,0.10),
      0 0 32px rgba(255,45,154,0.07);
    transform: translateY(-1px);
  }
  .bracket-step.active .bracket-step-text {
    animation: stepSubtleGlow 2.4s ease-in-out infinite;
    text-shadow: 0 0 12px rgba(245,200,66,0.45);
  }
  @keyframes stepSubtleGlow {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.74; }
  }
  .bracket-step-icon {
    width: 1.55rem;
    height: 1.55rem;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    font-size: 0.82rem;
    font-weight: 900;
    background: rgba(255,255,255,0.08);
    color: inherit;
  }
  .bracket-step.done .bracket-step-icon {
    background: rgba(34,197,94,0.18);
    color: var(--green);
  }
  .bracket-step.active .bracket-step-icon {
    background: rgba(245,200,66,0.18);
    color: var(--gold);
  }
  .bracket-step-text {
    min-width: 0;
    font-size: 0.78rem;
    line-height: 1.25;
    font-weight: 900;
  }
  .bracket-step-sub {
    display: block;
    color: rgba(237,229,255,0.54);
    font-size: 0.66rem;
    font-weight: 600;
    margin-top: 0.12rem;
  }
  .bracket-step-connector {
    height: 2px;
    border-radius: 999px;
    background: linear-gradient(90deg, rgba(245,200,66,0.55), rgba(255,255,255,0.08));
    opacity: 0.35;
  }
  .bracket-stepper-progress-line {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.75rem;
    margin-top: 0.78rem;
    color: rgba(237,229,255,0.62);
    font-size: 0.72rem;
  }
  .bracket-stepper-mini-bar {
    flex: 1;
    height: 5px;
    border-radius: 999px;
    background: rgba(255,255,255,0.08);
    overflow: hidden;
  }
  .bracket-stepper-mini-fill {
    height: 100%;
    border-radius: 999px;
    background: linear-gradient(90deg, var(--pink), var(--gold));
    transition: width 0.35s ease;
  }
  .bracket-setup-section.required-focus {
    border-color: rgba(245,200,66,0.24);
    background:
      radial-gradient(circle at 0% 0%, rgba(245,200,66,0.11), transparent 32%),
      rgba(0,0,0,0.20);
  }
  .bracket-setup-section.optional-soft {
    background: rgba(0,0,0,0.13);
    border-color: rgba(255,255,255,0.065);
  }
  .bracket-next-glow {
    animation: nextSectionGlow 3s ease-in-out infinite;
  }
  @keyframes nextSectionGlow {
    0%, 100% {
      box-shadow: 0 0 0 rgba(245,200,66,0);
      border-color: rgba(245,200,66,0.16);
    }
    50% {
      box-shadow: 0 0 24px rgba(245,200,66,0.10), 0 0 36px rgba(255,45,154,0.06);
      border-color: rgba(245,200,66,0.30);
    }
  }
  @media (max-width: 760px) {
    .bracket-stepper-row {
      grid-template-columns: 1fr;
    }
    .bracket-step-connector {
      display: none;
    }
    .bracket-step {
      min-height: 48px;
    }
  }


  .bracket-stepper-card .analysis-card-title,
  .bracket-setup-card .analysis-card-title {
    color: var(--gold);
    letter-spacing: 0.18em;
  }


  /* ── Bracket step text alignment polish ── */
  .bracket-stepper-row {
    align-items: stretch !important;
  }
  .bracket-step {
    justify-content: center !important;
    text-align: center !important;
    flex-direction: column !important;
    gap: 0.35rem !important;
    padding: 0.78rem 0.72rem !important;
  }
  .bracket-step-icon {
    margin: 0 auto !important;
  }
  .bracket-step-text {
    text-align: center !important;
    font-size: 0.82rem !important;
    line-height: 1.28 !important;
  }
  .bracket-step-sub {
    text-align: center !important;
    margin-top: 0.22rem !important;
    font-size: 0.67rem !important;
  }
  .bracket-setup-label {
    text-align: center;
    line-height: 1.45;
  }
  .bracket-setup-helper {
    text-align: center;
    max-width: 620px;
    margin-left: auto;
    margin-right: auto;
  }
  .bracket-stage-title {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    color: var(--gold);
    font-size: 0.72rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    font-weight: 900;
    text-align: center;
    margin-bottom: 0.8rem;
  }
  .bracket-stage-number {
    width: 1.5rem;
    height: 1.5rem;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: rgba(245,200,66,0.16);
    border: 1px solid rgba(245,200,66,0.32);
    color: var(--gold);
    font-size: 0.78rem;
    letter-spacing: 0;
    line-height: 1;
    box-shadow: 0 0 16px rgba(245,200,66,0.10);
  }


  /* ── Stage 1 title matches bracket section title styling ── */
  .bracket-stage-title {
    font-family: Georgia, 'Times New Roman', serif !important;
    font-size: clamp(1.35rem, 4vw, 2rem) !important;
    letter-spacing: 0.01em !important;
    text-transform: none !important;
    color: var(--text) !important;
    font-weight: 700 !important;
    margin-bottom: 0.9rem !important;
  }
  .bracket-stage-number {
    width: 1.7rem !important;
    height: 1.7rem !important;
    font-size: 0.85rem !important;
    font-family: inherit !important;
  }


  /* ── Final bracket page stage cohesion ── */
  .setup-stage-card {
    width: 100% !important;
    max-width: none !important;
    margin: 1rem 0 1.2rem !important;
    padding: 1.35rem !important;
  }
  .setup-stage-card .stage-header {
    margin-bottom: 1.05rem !important;
  }
  .setup-stage-card .stage-header h2 {
    font-family: Georgia, 'Times New Roman', serif !important;
    font-size: clamp(2rem, 4.6vw, 3.1rem) !important;
    letter-spacing: 0.08em !important;
    text-transform: uppercase !important;
    line-height: 1.05 !important;
    color: var(--text) !important;
    margin: 0 !important;
  }
  .setup-stage-card .stage-header p {
    margin: 0.35rem 0 0 !important;
    max-width: 780px !important;
  }
  .setup-stage-card .bracket-setup-section {
    margin-left: 0 !important;
    margin-right: 0 !important;
  }
  .setup-stage-card .bracket-setup-label {
    text-align: left !important;
  }
  .setup-stage-card .bracket-setup-helper {
    text-align: left !important;
    max-width: none !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
  }
  .bracket-stepper-card {
    margin-left: 0 !important;
    margin-right: 0 !important;
    width: 100% !important;
    max-width: none !important;
  }
  @media (max-width: 700px) {
    .setup-stage-card .stage-header h2 {
      font-size: clamp(1.7rem, 8vw, 2.25rem) !important;
    }
  }


  /* ── Match bracket top sections to stage-card width ── */
  .bracket-flow-container {
    width: min(100%, 980px);
    margin-left: auto;
    margin-right: auto;
  }
  .bracket-flow-container .bracket-stepper-card,
  .bracket-flow-container .setup-stage-card {
    width: 100% !important;
    max-width: none !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
  }


  /* ── Force top bracket flow to match stage card width ── */
  .bracket-flow-container {
    width: min(100%, 980px) !important;
    max-width: 980px !important;
    margin-left: auto !important;
    margin-right: auto !important;
  }
  .bracket-flow-container .bracket-stepper-card,
  .bracket-flow-container .setup-stage-card {
    width: 100% !important;
    max-width: 980px !important;
    margin-left: auto !important;
    margin-right: auto !important;
  }


  /* ── Final Stage 1 title/badge cleanup ── */
  .setup-stage-card .stage-header h2 {
    font-weight: 400 !important;
  }
  .setup-stage-card .stage-kicker,
  .setup-stage-card .stage-badge {
    display: inline-block !important;
    background: rgba(245,200,66,0.16) !important;
    color: var(--gold) !important;
    border: 1px solid rgba(245,200,66,0.24) !important;
    border-radius: 3px !important;
    padding: 0.26rem 0.62rem !important;
    font-size: 0.68rem !important;
    letter-spacing: 0.16em !important;
    text-transform: uppercase !important;
    font-weight: 900 !important;
    margin-bottom: 0.5rem !important;
  }


  /* ── Verified Stage 1 visual match fixes ── */
  .setup-stage-card .stage-header h2,
  .setup-stage-card h2 {
    font-weight: 400 !important;
  }
  .setup-stage-card .stage-badge {
    display: inline-block !important;
    background: rgba(245,200,66,0.16) !important;
    color: var(--gold) !important;
    border: 1px solid rgba(245,200,66,0.24) !important;
    border-radius: 3px !important;
    padding: 0.26rem 0.62rem !important;
    font-size: 0.68rem !important;
    letter-spacing: 0.16em !important;
    text-transform: uppercase !important;
    font-weight: 900 !important;
    margin-bottom: 0.5rem !important;
  }


  /* ── Stage 1 / bracket-page alignment final pass ── */
  .bracket-flow-container {
    width: min(100%, 930px) !important;
    max-width: 930px !important;
    margin-left: auto !important;
    margin-right: auto !important;
  }

  .bracket-flow-container .bracket-stepper-card,
  .bracket-flow-container .setup-stage-card {
    width: 100% !important;
    max-width: 930px !important;
    margin-left: auto !important;
    margin-right: auto !important;
  }

  .setup-stage-card .stage-header {
    display: grid !important;
    grid-template-columns: auto minmax(0, 1fr) !important;
    align-items: start !important;
    gap: 1rem !important;
    margin-bottom: 1.05rem !important;
  }

  .setup-stage-card .stage-header > div:last-child {
    display: block !important;
    text-align: left !important;
  }

  .setup-stage-card .stage-badge {
    margin-left: 0 !important;
    margin-right: 0 !important;
    margin-bottom: 0.55rem !important;
  }

  .setup-stage-card .stage-header h2 {
    font-family: Georgia, 'Times New Roman', serif !important;
    font-size: clamp(1.75rem, 3.6vw, 2.55rem) !important;
    font-weight: 400 !important;
    letter-spacing: 0.08em !important;
    text-transform: uppercase !important;
    line-height: 1.05 !important;
    margin: 0 !important;
    color: var(--text) !important;
  }

  .setup-stage-card .stage-header p {
    margin-top: 0.35rem !important;
    max-width: 760px !important;
    text-align: left !important;
  }

  .setup-stage-card .bracket-setup-helper {
    max-width: 760px !important;
    text-align: left !important;
    line-height: 1.5 !important;
  }

  .setup-stage-card .bracket-setup-actions {
    display: grid !important;
    grid-template-columns: minmax(0, 1fr) 112px !important;
    gap: 0.55rem !important;
    align-items: center !important;
  }

  .setup-stage-card .bracket-setup-actions .league-input,
  .setup-stage-card .bracket-setup-actions .username-input {
    width: 100% !important;
    min-width: 0 !important;
  }

  .setup-stage-card .bracket-setup-actions .btn {
    width: 112px !important;
    justify-content: center !important;
    text-align: center !important;
  }

  @media (max-width: 760px) {
    .bracket-flow-container {
      max-width: 100% !important;
    }

    .setup-stage-card .stage-header {
      grid-template-columns: 1fr !important;
      gap: 0.65rem !important;
      text-align: center !important;
    }

    .setup-stage-card .stage-header > div:last-child,
    .setup-stage-card .stage-header p,
    .setup-stage-card .bracket-setup-helper {
      text-align: center !important;
    }

    .setup-stage-card .stage-num {
      margin: 0 auto !important;
    }

    .setup-stage-card .bracket-setup-actions {
      grid-template-columns: 1fr !important;
    }

    .setup-stage-card .bracket-setup-actions .btn {
      width: 100% !important;
    }
  }


  /* ── Actual final alignment fixes for bracket page ── */
  .bracket-flow-container {
    width: min(100%, 930px) !important;
    max-width: 930px !important;
    margin-left: auto !important;
    margin-right: auto !important;
  }

  .bracket-flow-container .bracket-stepper-card,
  .bracket-flow-container .setup-stage-card {
    width: 100% !important;
    max-width: 930px !important;
    margin-left: auto !important;
    margin-right: auto !important;
  }

  .setup-stage-card {
    padding: 1.35rem !important;
  }

  .setup-stage-card .stage-header {
    display: flex !important;
    align-items: flex-start !important;
    gap: 1.1rem !important;
    text-align: left !important;
  }

  .setup-stage-card .stage-info {
    text-align: left !important;
  }

  .setup-stage-card .sec-title {
    font-size: clamp(1.65rem, 3.2vw, 2.4rem) !important;
    line-height: 1.08 !important;
    margin-top: 0.45rem !important;
  }

  .setup-stage-card .sec-desc {
    margin-top: 0.35rem !important;
    max-width: 760px !important;
  }

  .setup-stage-card .stage-badge {
    margin-left: 0 !important;
    margin-right: 0 !important;
  }

  .league-row-clean {
    display: grid !important;
    grid-template-columns: minmax(0, 1fr) 112px !important;
    gap: 0.55rem !important;
    align-items: center !important;
  }

  .league-row-clean .league-input {
    width: 100% !important;
    min-width: 0 !important;
  }

  .league-row-clean .btn {
    width: 112px !important;
    min-width: 112px !important;
    justify-content: center !important;
    text-align: center !important;
  }

  @media (max-width: 760px) {
    .bracket-flow-container {
      width: 100% !important;
      max-width: 100% !important;
    }
    .league-row-clean {
      grid-template-columns: 1fr !important;
    }
    .league-row-clean .btn {
      width: 100% !important;
      min-width: 0 !important;
    }
  }


  /* ── Mobile bracket-page alignment + spacing polish ── */
  .setup-stage-card .stage-header {
    align-items: center !important;
  }
  .setup-stage-card .stage-info,
  .setup-stage-card .sec-desc,
  .setup-stage-card .bracket-setup-section,
  .setup-stage-card .bracket-setup-label,
  .setup-stage-card .bracket-setup-helper {
    text-align: left !important;
  }
  .setup-stage-card .bracket-setup-actions {
    justify-content: flex-start !important;
  }

  @media (max-width: 600px) {
    .stage-wrap,
    .setup-stage-card {
      padding: 1rem !important;
      margin-bottom: 1rem !important;
    }

    .stage-header,
    .setup-stage-card .stage-header {
      gap: 0.6rem !important;
      align-items: center !important;
    }

    .stage-num {
      width: 2.1rem !important;
      height: 2.1rem !important;
      font-size: 1rem !important;
      flex: 0 0 2.1rem !important;
    }

    .sec-title {
      margin-top: 0.25rem !important;
      margin-bottom: 0.25rem !important;
    }

    .sec-desc {
      margin-top: 0.15rem !important;
      line-height: 1.4 !important;
    }

    .stage-badge {
      margin-bottom: 0.3rem !important;
    }

    .bracket-setup-section {
      padding: 0.75rem 0.85rem !important;
    }

    .username-input,
    .league-input {
      height: 42px !important;
      font-size: 0.9rem !important;
    }

    .btn {
      min-height: 42px !important;
    }

    .queen-row {
      border-radius: 10px !important;
      padding: 0.72rem 0.8rem !important;
      transition: transform 0.12s ease, background 0.12s ease, border-color 0.12s ease, box-shadow 0.12s ease !important;
    }

    .queen-row:active {
      transform: scale(0.985) !important;
      background: rgba(255,255,255,0.07) !important;
    }

    .queen-row.q-selected {
      background: rgba(245,200,66,0.10) !important;
      border: 1px solid rgba(245,200,66,0.28) !important;
      box-shadow: 0 0 18px rgba(245,200,66,0.08) !important;
    }

    .q-rank {
      min-width: 42px !important;
      height: 30px !important;
      font-size: 0.78rem !important;
      border-radius: 999px !important;
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
    }
  }


  /* ── Stage 1 premium input + confirmation polish ── */
  .stage1-name-row {
    width: 100%;
  }

  .setup-stage-card .username-input {
    height: 48px !important;
    font-size: 1rem !important;
    border: 1px solid rgba(255,255,255,0.18) !important;
    transition: border-color 0.16s ease, box-shadow 0.16s ease, background 0.16s ease !important;
  }

  .setup-stage-card .username-input:focus {
    border-color: rgba(245,200,66,0.62) !important;
    box-shadow: 0 0 0 2px rgba(245,200,66,0.14), 0 0 22px rgba(245,200,66,0.08) !important;
    background: rgba(255,255,255,0.055) !important;
    outline: none !important;
  }

  .stage1-confirmation {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.7rem;
    flex-wrap: wrap;
  }

  .stage1-confirmation-main {
    color: rgba(237,229,255,0.86);
    font-size: 0.92rem;
    line-height: 1.35;
  }

  .stage1-confirmation-main strong {
    color: var(--gold);
    font-weight: 900;
  }

  .stage1-complete-pill {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    margin-top: 0.45rem;
    padding: 0.24rem 0.55rem;
    border-radius: 999px;
    background: rgba(34,197,94,0.09);
    border: 1px solid rgba(34,197,94,0.18);
    color: var(--green);
    font-size: 0.68rem;
    font-weight: 900;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .league-section-reveal {
    animation: leagueReveal 0.26s ease-out both;
  }

  @keyframes leagueReveal {
    from { opacity: 0; transform: translateY(7px); }
    to { opacity: 1; transform: translateY(0); }
  }

  @media (min-width: 768px) {
    .setup-stage-card .stage1-name-row {
      display: flex !important;
      justify-content: center !important;
      align-items: stretch !important;
      gap: 0 !important;
    }

    .setup-stage-card .stage1-name-row .username-input {
      max-width: 430px !important;
      border-radius: 10px 0 0 10px !important;
      border-right: 0 !important;
    }

    .setup-stage-card .stage1-name-row .name-generate-btn {
      border-radius: 0 !important;
      min-width: 116px !important;
      width: auto !important;
    }

    .setup-stage-card .stage1-name-row .btn-gold {
      border-radius: 0 10px 10px 0 !important;
      min-width: 120px !important;
      width: auto !important;
    }
  }

  @media (max-width: 767px) {
    .setup-stage-card .stage1-name-row {
      display: grid !important;
      grid-template-columns: 1fr !important;
      gap: 0.5rem !important;
    }
  }


  /* ── Viral/share + interaction polish ── */
  .post-submit-card {
    margin: 1rem auto 1.1rem;
    padding: 1rem;
    border-radius: 12px;
    background:
      radial-gradient(circle at 12% 0%, rgba(245,200,66,0.15), transparent 34%),
      linear-gradient(135deg, rgba(255,45,154,0.075), rgba(245,200,66,0.07), rgba(155,93,229,0.075)),
      rgba(16,6,29,0.82);
    border: 1px solid rgba(245,200,66,0.24);
    box-shadow: 0 14px 38px rgba(0,0,0,0.24), inset 0 0 0 1px rgba(255,255,255,0.035);
    text-align: center;
    width: min(100%, 930px);
  }
  .post-submit-title {
    font-family: 'Bebas Neue', sans-serif;
    font-size: clamp(1.75rem, 5vw, 2.45rem);
    line-height: 1;
    color: var(--gold);
    letter-spacing: 0.04em;
  }
  .post-submit-sub {
    margin-top: 0.35rem;
    font-size: 0.9rem;
    color: rgba(237,229,255,0.78);
  }
  .post-submit-actions {
    margin-top: 0.85rem;
    display: flex;
    justify-content: center;
    gap: 0.6rem;
    flex-wrap: wrap;
  }
  .share-winner-rate {
    margin-top: 0.55rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.34rem 0.68rem;
    border-radius: 999px;
    background: rgba(245,200,66,0.08);
    border: 1px solid rgba(245,200,66,0.18);
    color: var(--gold);
    font-size: 0.74rem;
    font-weight: 900;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }
  .btn {
    transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease !important;
  }
  .btn:hover { transform: translateY(-1px); }
  .btn:active { transform: scale(0.97); }
  .stage-wrap,
  .setup-stage-card,
  .bracket-stepper-card,
  .analysis-card,
  .leaderboard-table-card {
    transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
  }
  .queen-row { position: relative; }
  .queen-row.flash-check::after {
    content: "✓";
    position: absolute;
    right: 0.65rem;
    top: 50%;
    transform: translateY(-50%);
    color: var(--green);
    font-weight: 900;
    font-size: 1.05rem;
    animation: flashCheck 0.46s ease;
    pointer-events: none;
  }
  @keyframes flashCheck {
    0% { opacity: 0; transform: translateY(-50%) scale(0.55); }
    45% { opacity: 1; transform: translateY(-50%) scale(1.25); }
    100% { opacity: 0; transform: translateY(-50%) scale(1); }
  }
  .lb-change.up { color: var(--green); }
  .lb-change.down { color: var(--red); }
  .lb-change.same { color: var(--muted); }
  @media (max-width: 600px) {
    .post-submit-card { padding: 0.9rem; }
    .post-submit-actions .btn { width: 100%; }
  }


  /* ── Mobile selection reliability fix ── */
  .q-rank {
    pointer-events: none !important;
  }
  .queen-row:not(.q-selected) .q-rank {
    opacity: 0 !important;
  }
  .queen-row.q-selected .q-rank {
    opacity: 1 !important;
  }
  .queen-row.q-disabled {
    pointer-events: auto !important;
  }


  /* ── Mobile Stage 1 left-alignment correction ── */
  @media (max-width: 760px) {
    .setup-stage-card .stage-header {
      display: flex !important;
      flex-direction: row !important;
      align-items: center !important;
      justify-content: flex-start !important;
      text-align: left !important;
      gap: 0.75rem !important;
    }

    .setup-stage-card .stage-header > div:last-child,
    .setup-stage-card .stage-info {
      text-align: left !important;
      align-items: flex-start !important;
      justify-content: flex-start !important;
      width: 100% !important;
    }

    .setup-stage-card .stage-num {
      margin: 0 !important;
      align-self: center !important;
      flex: 0 0 auto !important;
    }

    .setup-stage-card .stage-badge {
      display: inline-block !important;
      margin-left: 0 !important;
      margin-right: auto !important;
      text-align: left !important;
    }

    .setup-stage-card .sec-title,
    .setup-stage-card .stage-header h2 {
      text-align: left !important;
      margin-left: 0 !important;
      margin-right: 0 !important;
    }

    .setup-stage-card .sec-desc,
    .setup-stage-card .stage-header p {
      text-align: left !important;
      margin-left: 0 !important;
      margin-right: 0 !important;
    }

    .setup-stage-card .bracket-setup-section,
    .setup-stage-card .bracket-setup-label,
    .setup-stage-card .bracket-setup-helper,
    .setup-stage-card .stage1-confirmation-main {
      text-align: left !important;
    }

    .setup-stage-card .stage1-confirmation {
      justify-content: flex-start !important;
      align-items: center !important;
      text-align: left !important;
    }

    .setup-stage-card .stage1-complete-pill {
      margin-left: 0 !important;
      margin-right: auto !important;
    }
  }


  /* ── Share card redesign: shorter sections, stacked overall picks, reserved footer ── */
  .share-card {
    grid-template-rows: minmax(0, 1fr) 96px !important;
  }
  .share-card-main {
    padding: 1.05rem 1.18rem 0.58rem !important;
    justify-content: start !important;
    gap: 0.55rem !important;
  }
  .share-card-kicker {
    font-size: clamp(0.58rem, 1.75vw, 0.78rem) !important;
    letter-spacing: 0.23em !important;
    margin-bottom: 0.1rem !important;
  }
  .share-card-name {
    font-size: clamp(2.35rem, 9.2vw, 4.0rem) !important;
    margin-bottom: 0.22rem !important;
    line-height: 0.88 !important;
  }
  .share-card-sub {
    font-size: clamp(0.62rem, 1.9vw, 0.82rem) !important;
    margin-bottom: 0.32rem !important;
    letter-spacing: 0.22em !important;
  }
  .mini-league-tag {
    margin: 0 auto 0.25rem !important;
    font-size: clamp(0.48rem, 1.45vw, 0.62rem) !important;
    line-height: 1.1 !important;
  }
  .share-card-brackets {
    gap: 0.32rem !important;
  }
  .share-bracket-row {
    padding: 0.38rem 0.58rem 0.36rem !important;
    border-left-width: 6px !important;
    border-radius: 8px !important;
  }
  .share-bracket-label {
    font-size: clamp(0.58rem, 1.75vw, 0.74rem) !important;
    margin-bottom: 0.18rem !important;
    letter-spacing: 0.22em !important;
  }
  .share-pick-line {
    grid-template-columns: 1.78rem 1.42rem 1fr !important;
    font-size: clamp(0.74rem, 2.15vw, 0.98rem) !important;
    line-height: 1.16 !important;
    gap: 0.18rem !important;
    min-width: 0 !important;
  }
  .share-pick-line span:last-child,
  .share-overall-pick span:last-child {
    min-width: 0 !important;
    overflow-wrap: anywhere !important;
  }
  .share-pick-rank {
    font-size: clamp(0.98rem, 2.85vw, 1.26rem) !important;
  }
  .share-pick-emoji {
    font-size: clamp(0.9rem, 2.45vw, 1.14rem) !important;
  }
  .share-overall {
    margin-top: 0.42rem !important;
    display: block !important;
  }
  .share-overall-stacked .share-overall-card {
    width: 100% !important;
  }
  .share-overall-card {
    padding: 0.42rem 0.58rem !important;
    border-radius: 8px !important;
  }
  .share-overall-label {
    font-size: clamp(0.5rem, 1.38vw, 0.62rem) !important;
    margin-bottom: 0.16rem !important;
  }
  .share-overall-pick {
    grid-template-columns: 3.0rem minmax(0, 1fr) !important;
    font-size: clamp(0.74rem, 2.1vw, 0.96rem) !important;
    line-height: 1.12 !important;
    gap: 0.3rem !important;
    align-items: baseline !important;
  }
  .overall-runner-line {
    margin-top: 0.18rem !important;
    color: rgba(237,229,255,0.94) !important;
  }
  .share-card-brand {
    min-height: 96px !important;
    background: rgba(2,1,6,0.92) !important;
    border-top: 1px solid rgba(255,255,255,0.06) !important;
    padding: 0.7rem 0.55rem !important;
    position: relative !important;
  }
  .share-card-brand-small {
    font-size: clamp(0.44rem, 1.35vw, 0.58rem) !important;
    margin-bottom: 0.18rem !important;
  }
  .share-card-brand-url {
    font-size: clamp(1.08rem, 3.95vw, 1.48rem) !important;
  }


  /* ── Share card rebalance: less blank space, larger text, safe footer ── */
  .share-card {
    grid-template-rows: minmax(0, 1fr) 78px !important;
  }

  .share-card-main {
    padding: 1.1rem 1.18rem 0.62rem !important;
    display: flex !important;
    flex-direction: column !important;
    justify-content: space-between !important;
    gap: 0.5rem !important;
  }

  .share-card-name {
    font-size: clamp(2.65rem, 10.2vw, 4.55rem) !important;
    line-height: 0.88 !important;
    margin-bottom: 0.28rem !important;
  }

  .share-card-sub {
    margin-bottom: 0.48rem !important;
  }

  .share-card-brackets {
    gap: 0.44rem !important;
  }

  .share-bracket-row {
    padding: 0.48rem 0.66rem 0.46rem !important;
    min-height: 82px !important;
  }

  .share-bracket-label {
    font-size: clamp(0.64rem, 1.95vw, 0.84rem) !important;
    margin-bottom: 0.24rem !important;
  }

  .share-pick-line {
    grid-template-columns: 2.0rem 1.55rem minmax(0, 1fr) !important;
    font-size: clamp(0.88rem, 2.55vw, 1.15rem) !important;
    line-height: 1.18 !important;
    gap: 0.22rem !important;
  }

  .share-pick-rank {
    font-size: clamp(1.15rem, 3.35vw, 1.48rem) !important;
  }

  .share-pick-emoji {
    font-size: clamp(1.0rem, 2.8vw, 1.25rem) !important;
  }

  .share-overall {
    margin-top: 0.52rem !important;
  }

  .share-overall-card {
    padding: 0.5rem 0.66rem !important;
  }

  .share-overall-label {
    font-size: clamp(0.58rem, 1.65vw, 0.74rem) !important;
    margin-bottom: 0.2rem !important;
  }

  .share-overall-pick {
    grid-template-columns: 3.35rem minmax(0, 1fr) !important;
    font-size: clamp(0.9rem, 2.5vw, 1.14rem) !important;
    line-height: 1.16 !important;
  }

  .overall-runner-line {
    margin-top: 0.16rem !important;
  }

  .share-card-brand {
    min-height: 78px !important;
    padding: 0.48rem 0.55rem !important;
  }

  .share-card-brand-small {
    font-size: clamp(0.42rem, 1.25vw, 0.54rem) !important;
    margin-bottom: 0.12rem !important;
  }

  .share-card-brand-url {
    font-size: clamp(1.0rem, 3.55vw, 1.34rem) !important;
  }


  /* ── Final share card fit tuning: prevent cutoffs while keeping density ── */
  .share-card {
    grid-template-rows: minmax(0, 1fr) 84px !important;
  }
  .share-card-main {
    padding: 1rem 1.06rem 0.54rem !important;
    gap: 0.42rem !important;
  }
  .share-card-kicker {
    font-size: clamp(0.56rem, 1.65vw, 0.76rem) !important;
    letter-spacing: 0.2em !important;
    margin-bottom: 0.12rem !important;
  }
  .share-card-name {
    max-width: 100% !important;
    white-space: nowrap !important;
    line-height: 0.9 !important;
    letter-spacing: -0.02em !important;
    margin-bottom: 0.22rem !important;
  }
  .share-card-sub {
    font-size: clamp(0.76rem, 2vw, 0.96rem) !important;
    letter-spacing: 0.18em !important;
    margin-bottom: 0.36rem !important;
  }
  .mini-league-tag {
    margin-bottom: 0.22rem !important;
  }
  .share-card-brackets {
    gap: 0.38rem !important;
  }
  .share-bracket-row {
    padding: 0.42rem 0.54rem 0.4rem !important;
    min-height: 74px !important;
    border-left-width: 7px !important;
  }
  .share-bracket-label {
    font-size: clamp(0.64rem, 1.8vw, 0.8rem) !important;
    margin-bottom: 0.18rem !important;
    letter-spacing: 0.19em !important;
  }
  .share-pick-line {
    grid-template-columns: 1.72rem 1.22rem minmax(0, 1fr) !important;
    font-size: clamp(0.8rem, 2.18vw, 1.02rem) !important;
    line-height: 1.14 !important;
    gap: 0.18rem !important;
  }
  .share-pick-line span:last-child {
    overflow-wrap: anywhere !important;
  }
  .share-pick-rank {
    font-size: clamp(1.1rem, 3vw, 1.34rem) !important;
  }
  .share-pick-emoji {
    font-size: clamp(0.96rem, 2.5vw, 1.18rem) !important;
  }
  .share-overall {
    margin-top: 0.44rem !important;
  }
  .share-overall-card {
    padding: 0.44rem 0.54rem !important;
  }
  .share-overall-label {
    font-size: clamp(0.62rem, 1.7vw, 0.78rem) !important;
    margin-bottom: 0.14rem !important;
    letter-spacing: 0.16em !important;
  }
  .share-overall-pick {
    grid-template-columns: 2.8rem minmax(0, 1fr) !important;
    font-size: clamp(0.86rem, 2.3vw, 1.06rem) !important;
    gap: 0.22rem !important;
    line-height: 1.12 !important;
  }
  .overall-runner-line {
    margin-top: 0.1rem !important;
  }
  .share-card-brand {
    min-height: 84px !important;
    padding: 0.56rem 0.52rem 0.5rem !important;
  }
  .share-card-brand-small {
    font-size: clamp(0.52rem, 1.42vw, 0.68rem) !important;
    letter-spacing: 0.16em !important;
    margin-bottom: 0.08rem !important;
  }
  .share-card-brand-url {
    font-size: clamp(1.38rem, 4.1vw, 1.84rem) !important;
    letter-spacing: 0.03em !important;
  }


  /* ── Final share card tuning v2 ── */
  .share-card-main {
    padding: 0.94rem 1.02rem 0.52rem !important;
    gap: 0.34rem !important;
  }
  .share-card-kicker {
    margin-bottom: 0.06rem !important;
  }
  .share-card-name {
    margin-bottom: 0.14rem !important;
    max-width: 100% !important;
    white-space: nowrap !important;
    overflow: hidden !important;
  }
  .share-card-sub {
    margin-bottom: 0.28rem !important;
  }
  .share-card-brackets {
    gap: 0.34rem !important;
  }
  .share-bracket-row {
    padding: 0.4rem 0.52rem 0.38rem !important;
    min-height: 72px !important;
  }
  .share-pick-line {
    grid-template-columns: 1.64rem 1.1rem minmax(0, 1fr) !important;
    font-size: clamp(0.8rem, 2.12vw, 1rem) !important;
    gap: 0.16rem !important;
  }
  .share-overall-card {
    padding: 0.46rem 0.58rem !important;
  }
  .share-overall-pick {
    display: grid !important;
    grid-template-columns: 1rem 2.25rem minmax(0, 1fr) !important;
    align-items: start !important;
    gap: 0.16rem !important;
    font-size: clamp(0.88rem, 2.34vw, 1.08rem) !important;
    line-height: 1.08 !important;
  }
  .share-overall-emoji {
    font-size: clamp(0.8rem, 2vw, 0.98rem) !important;
    line-height: 1.05 !important;
    transform: translateY(0.02rem);
  }
  .share-overall-rank {
    font-weight: 900 !important;
    white-space: nowrap !important;
    color: var(--gold) !important;
  }
  .share-overall-name {
    min-width: 0 !important;
    overflow-wrap: anywhere !important;
  }
  .overall-runner-line {
    margin-top: 0.08rem !important;
  }
  .share-card-brand {
    min-height: 88px !important;
    padding: 0.62rem 0.56rem 0.56rem !important;
  }
  .share-card-brand-small {
    font-size: clamp(0.56rem, 1.52vw, 0.72rem) !important;
    margin-bottom: 0.1rem !important;
  }
  .share-card-brand-url {
    font-size: clamp(1.56rem, 4.55vw, 2.06rem) !important;
    letter-spacing: 0.035em !important;
  }

`;

// ── COMPONENT ─────────────────────────────────────────────────────────────────
export default function DragBracket() {
  const [tab, setTab] = useState("bracket");
  const [username, setUsername] = useState("");
  const [usernameInput, setUsernameInput] = useState("");
  const [picks, setPicks] = useState(EMPTY_PICKS);
  const [submitted, setSubmitted] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const [toast, setToast] = useState("");
  const [toastVisible, setToastVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resultsMode, setResultsMode] = useState(false);
  const [email, setEmail] = useState("");
  const [emailSaved, setEmailSaved] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [flashQueen, setFlashQueen] = useState(null);
  const [leagueCode, setLeagueCode] = useState("");
  const [leagueName, setLeagueName] = useState("");
  const [leagueCodeInput, setLeagueCodeInput] = useState("");
  const [leagueNameInput, setLeagueNameInput] = useState("");
  const [leaderboardView, setLeaderboardView] = useState("global");
  const [browserEditNoticeOpen, setBrowserEditNoticeOpen] = useState(false);
  const [celebrating, setCelebrating] = useState(false);
  const shareCardRef = useRef(null);
  const crowdShareCardRef = useRef(null);
  // Live official results — loaded from shared storage, updated by admin panel
  const [liveResults, setLiveResults] = useState(OFFICIAL_RESULTS);
  // Submission lock — auto-locks at LOCK_DATE
  const [now, setNow] = useState(new Date());
  const isLocked = now >= LOCK_DATE;



  const getSubmissionId = () => {
    try {
      let id = localStorage.getItem("drag-submission-id-v1");
      if (!id) {
        id = (window.crypto?.randomUUID?.() || `drag-${Date.now()}-${Math.random().toString(36).slice(2)}`);
        localStorage.setItem("drag-submission-id-v1", id);
      }
      return id;
    } catch (_) {
      return `drag-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }
  };

  const cleanLeagueCode = (value) => String(value || "")
    .toUpperCase()
    .replace(/[^A-Z0-9-]/g, "")
    .slice(0, 12);

  const cleanLeagueName = (value) => String(value || "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 16);

  const generateLeagueCode = () => {
    const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    let raw = "";
    try {
      const arr = new Uint32Array(6);
      window.crypto.getRandomValues(arr);
      raw = Array.from(arr).map(n => alphabet[n % alphabet.length]).join("");
    } catch (_) {
      raw = Math.random().toString(36).slice(2, 8).toUpperCase();
    }
    return `DRAG-${raw}`;
  };

  const leagueShareUrl = leagueCode
    ? `${window.location.origin}${window.location.pathname}?league=${encodeURIComponent(leagueCode)}${leagueName ? `&leagueName=${encodeURIComponent(leagueName)}` : ""}`
    : "";

  const leagueInviteText = leagueShareUrl
    ? `${username || "Someone"} just challenged you to Drag Madness 👀\n\nPick your queens, talk your trash, and prove you’ve got the best taste 💅\n\n${leagueShareUrl}`
    : "";

  // Tick every second so the countdown stays live
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Human-readable countdown to lock
  const countdownStr = (() => {
    const diff = LOCK_DATE - now;
    if (diff <= 0) return null;
    const days    = Math.floor(diff / 86400000);
    const hours   = Math.floor((diff % 86400000) / 3600000);
    const minutes = Math.floor((diff % 3600000)  / 60000);
    const seconds = Math.floor((diff % 60000)    / 1000);
    if (days > 0)  return `${days}d ${hours}h ${minutes}m ${seconds}s`;
    if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
    return `${minutes}m ${seconds}s`;
  })();

  // Load official results from shared storage on mount
  useEffect(() => {
    (async () => {
      try {
        const r = await window.storage.get("drag-official-results-v1", true);
        if (r?.value) setLiveResults(JSON.parse(r.value));
      } catch (_) { /* fall back to hardcoded OFFICIAL_RESULTS */ }
    })();
  }, []);

  const loadLeaderboard = useCallback(async () => {
    try {
      const r = await window.storage.get("drag-lb-v3", true);

      if (!r?.value) {
        setLeaderboard([]);
        return;
      }

      if (Array.isArray(r.value)) {
        setLeaderboard(r.value);
      } else if (typeof r.value === "string") {
        setLeaderboard(JSON.parse(r.value));
      } else {
        setLeaderboard([]);
      }
    } catch (_) {
      setLeaderboard([]);
    }
  }, []);

  useEffect(() => { loadLeaderboard(); }, [loadLeaderboard]);

  useEffect(() => {
    const interval = setInterval(() => {
      loadLeaderboard();
    }, 15 * 60 * 1000);

    return () => clearInterval(interval);
  }, [loadLeaderboard]);

  useEffect(() => {
    if (tab === "leaderboard") {
      loadLeaderboard();
    }
  }, [tab, loadLeaderboard]);

  useEffect(() => {
    try {
      const saved = localStorage.getItem("drag-bracket-v3");
      const params = new URLSearchParams(window.location.search);
      const urlLeagueCode = cleanLeagueCode(params.get("league") || "");
      const urlLeagueName = cleanLeagueName(params.get("leagueName") || "");

      if (saved) {
        const parsed = JSON.parse(saved);
        const { username: u, picks: p, submitted: s } = parsed;
        if (u) setUsername(u);
        if (p) setPicks({
          groupAdvances: p.groupAdvances || { orange: [], pink: [], purple: [] },
          runnerUp: p.runnerUp || null,
          winner: p.winner || null,
        });
        if (s) setSubmitted(s);
        if (parsed.email) { setEmail(parsed.email); setEmailSaved(true); }
        if (!urlLeagueCode && parsed.leagueCode) setLeagueCode(cleanLeagueCode(parsed.leagueCode));
        if (!urlLeagueName && parsed.leagueName) setLeagueName(cleanLeagueName(parsed.leagueName));
      }

      if (urlLeagueCode) {
        setLeagueCode(urlLeagueCode);
        setLeagueCodeInput(urlLeagueCode);
        setLeaderboardView("league");
      }
      if (urlLeagueName) {
        setLeagueName(urlLeagueName);
        setLeagueNameInput(urlLeagueName);
      }
    } catch (_) {}
  }, []);

  const save = (u, p, s, em, lc = leagueCode, ln = leagueName) => {
    try {
      localStorage.setItem("drag-bracket-v3", JSON.stringify({
        username: u,
        picks: p,
        submitted: s,
        email: em ?? email,
        leagueCode: cleanLeagueCode(lc),
        leagueName: cleanLeagueName(ln),
        submissionId: getSubmissionId(),
      }));
    } catch (_) {}
  };

  const showToast = (msg) => {
    setToast(msg); setToastVisible(true);
    setTimeout(() => setToastVisible(false), 2800);
  };


  const createLeague = async () => {
    const name = cleanLeagueName(leagueNameInput);
    if (!name) {
      showToast("Give your league a name first!");
      return;
    }
    const code = generateLeagueCode();
    const shareUrl = `${window.location.origin}${window.location.pathname}?league=${encodeURIComponent(code)}&leagueName=${encodeURIComponent(name)}`;
    setLeagueName(name);
    setLeagueCode(code);
    setLeagueCodeInput(code);
    setLeaderboardView("league");
    save(username, picks, submitted, email, code, name);

    try {
      const inviteText = `${username || "Someone"} just challenged you to Drag Madness 👀\n\nPick your queens, talk your trash, and prove you’ve got the best taste 💅\n\n${shareUrl}`;
      await navigator.clipboard.writeText(inviteText);
      showToast("Invite message copied — send it to your group chat 📲");
    } catch (_) {
      showToast(`League created: ${name} ✨ Copy the link below.`);
    }
  };

  const joinLeague = () => {
    const code = cleanLeagueCode(leagueCodeInput);
    if (!code) {
      showToast("Enter a league code first!");
      return;
    }
    const name = cleanLeagueName(leagueNameInput) || leagueName || "";
    setLeagueCode(code);
    setLeagueName(name);
    setLeaderboardView("league");
    save(username, picks, submitted, email, code, name);
    showToast(`Joined league ${name || code}!`);
  };

  const leaveLeague = () => {
    setLeagueCode("");
    setLeagueName("");
    setLeagueCodeInput("");
    setLeagueNameInput("");
    setLeaderboardView("global");
    save(username, picks, submitted, email, "", "");
    showToast("League cleared");
  };

  const copyLeagueLink = async () => {
    if (!leagueInviteText) return;
    try {
      await navigator.clipboard.writeText(leagueInviteText);
      showToast("Invite message copied — send it to your group chat 📲");
    } catch (_) {
      showToast("Copy failed — copy the link manually");
    }
  };


  const generateDragName = () => {
    const idx = Math.floor(Math.random() * DRAG_NAME_CATALOG.length);
    const generated = DRAG_NAME_CATALOG[idx].slice(0, 22);
    setUsernameInput(generated);
    showToast(`Category is: ${generated}!`);
  };

  const handleSetUsername = async () => {
    const u = usernameInput.trim().slice(0, 22);
    if (!u) return;
    // Check uniqueness against the leaderboard
    const savedSession = (() => { try { const s = localStorage.getItem("drag-bracket-v3"); return s ? JSON.parse(s).username : null; } catch(_) { return null; } })();
    try {
      const r = await window.storage.get("drag-lb-v3", true);
      const lb = r?.value ? JSON.parse(r.value) : [];
      const isTaken = lb.some(e => e.username.toLowerCase() === u.toLowerCase() && e.username !== savedSession);
      if (isTaken) { showToast(`"${u}" is already taken — try another name!`); return; }
    } catch(_) { /* storage unavailable — allow the name */ }
    setUsername(u); save(u, picks, submitted);
    showToast(`Welcome, ${u}! 👑`);
  };

  const sanitizeGroupPicks = (bracket, group = []) => {
    const validQueens = new Set(BRACKETS[bracket]?.queens || []);
    return [...new Set(Array.isArray(group) ? group : [])]
      .filter(q => validQueens.has(q))
      .slice(0, 2);
  };
  // Stage 1: toggle a queen in/out of a group's top-2 advances.
  // Order matters: index 0 = predicted #1 in bracket (+2 bonus),
  //                index 1 = predicted #2 in bracket (+1 bonus).
  const toggleGroupAdvance = (bracket, queen) => {
    if (submitted) return;
    const current = sanitizeGroupPicks(bracket, picks.groupAdvances[bracket] || []);
    let newGroup;
    if (current.includes(queen)) {
      newGroup = current.filter(q => q !== queen);
      const np = {
        ...picks,
        groupAdvances: { ...picks.groupAdvances, [bracket]: newGroup },
        runnerUp: picks.runnerUp === queen ? null : picks.runnerUp,
        winner: picks.winner === queen ? null : picks.winner,
      };
      setFlashQueen(queen);
    window.setTimeout(() => setFlashQueen(null), 460);
    setPicks(np); save(username, np, submitted);
    } else {
      if (current.length >= 2) { showToast("Pick exactly 2 queens per group!"); return; }
      newGroup = [...current, queen];
      const np = { ...picks, groupAdvances: { ...picks.groupAdvances, [bracket]: newGroup } };
      setPicks(np); save(username, np, submitted);
    }
  };

  // Swap the order of the 2 picks in a bracket (i.e. flip which one is #1 vs #2).
  const swapGroupOrder = (bracket) => {
    if (submitted) return;
    const current = sanitizeGroupPicks(bracket, picks.groupAdvances[bracket] || []);
    if (current.length !== 2) return;
    const swapped = [current[1], current[0]];
    const np = { ...picks, groupAdvances: { ...picks.groupAdvances, [bracket]: swapped } };
    setPicks(np); save(username, np, submitted);
  };

  // Stage 2: pick winner and runner-up directly from the 6 semi-finalists
  const pickPodium = (queen, place) => {
    if (submitted) return;
    const allSemis = Object.values(picks.groupAdvances).flat();
    if (!allSemis.includes(queen)) { showToast("Pick this queen in Stage 1 first!"); return; }
    let np = { ...picks };
    if (place === "winner") {
      np.winner = picks.winner === queen ? null : queen;
      if (np.winner && picks.runnerUp === queen) np.runnerUp = null;
    } else {
      np.runnerUp = picks.runnerUp === queen ? null : queen;
      if (np.runnerUp && picks.winner === queen) np.winner = null;
    }
    setPicks(np); save(username, np, submitted);
  };

  // Derived
  const allSemiFinalists = Object.keys(BRACKETS).flatMap(b => sanitizeGroupPicks(b, picks.groupAdvances[b] || []));
  const groupComplete = (b) => sanitizeGroupPicks(b, picks.groupAdvances[b] || []).length === 2;
  const allGroupsComplete = Object.keys(BRACKETS).every(groupComplete);
  const totalGroupSelections = Object.keys(BRACKETS).reduce((a, b) => a + sanitizeGroupPicks(b, picks.groupAdvances[b] || []).length, 0);
  const picksComplete = () => allGroupsComplete && picks.winner && picks.runnerUp && picks.winner !== picks.runnerUp;
  // 6 group advances (5pts) + runner-up (15pts) + winner (25pts) = 70pts max
  const totalPossible = SCORING.groupAdvance * 6
                      + SCORING.bracketWinner * 3
                      + SCORING.bracketRunnerUp * 3
                      + SCORING.runnerUp + SCORING.winner;
  const { score: myScore, breakdown: myBreakdown } = calcScore(picks, liveResults);
  const myRank = leaderboard.findIndex(e => e.username === username) + 1;
  const activeLeagueCode = cleanLeagueCode(leagueCode || leagueCodeInput);
  const shownLeaderboard = leaderboardView === "league" && activeLeagueCode
    ? leaderboard.filter(e => cleanLeagueCode(e?.leagueCode) === activeLeagueCode)
    : leaderboard;
  const shownRank = shownLeaderboard.findIndex(e => e.username === username) + 1;
  const topLeaderboardEntry = shownLeaderboard[0] || null;
  const topScore = topLeaderboardEntry?.score || 0;
  const myShownEntry = shownLeaderboard.find(e => e.username === username);
  const myShownScore = myShownEntry?.score ?? myScore;
  const pointsBehindLeader = topLeaderboardEntry && username && topLeaderboardEntry.username !== username
    ? Math.max(0, topScore - (myShownScore || 0))
    : 0;


  // Leaderboard insight should follow the actual leaderboard scores.
  // This avoids staying in the pre-scoring state if liveResults has not hydrated yet.
  const currentTopScore = Math.max(0, ...shownLeaderboard.map(entry => Number(entry?.score || 0)));
  const perfectEntries = currentTopScore > 0
    ? shownLeaderboard.filter(entry => Number(entry?.score || 0) === currentTopScore)
    : [];
  const perfectPct = shownLeaderboard.length
    ? Math.round((perfectEntries.length / shownLeaderboard.length) * 1000) / 10
    : 0;
  const leaderboardInsightMain = !shownLeaderboard.length
    ? "No brackets yet"
    : currentTopScore > 0 && perfectEntries.length > 0
      ? `${perfectPct}% of players still have perfect brackets 👑`
      : `${shownLeaderboard.length} ${shownLeaderboard.length === 1 ? "player has" : "players have"} entered the werkroom 👀`;
  const leaderboardInsightSub = currentTopScore > 0
    ? `${perfectEntries.length} of ${shownLeaderboard.length} ${shownLeaderboard.length === 1 ? "bracket is" : "brackets are"} perfect after the latest results.`
    : leaderboardView === "league" && activeLeagueCode
      ? `${leagueName || "Your league"} has ${shownLeaderboard.length} ${shownLeaderboard.length === 1 ? "player" : "players"} competing.`
      : `${leaderboard.length} total ${leaderboard.length === 1 ? "player" : "players"} competing globally.`;


  const showGlobalLeaderboard = () => {
    setLeaderboardView("global");
  };

  const showLeagueLeaderboard = () => {
    const code = cleanLeagueCode(leagueCode || leagueCodeInput);
    if (!code) {
      showToast("Join or create a league first!");
      return;
    }
    if (!leagueCode) setLeagueCode(code);
    setLeaderboardView("league");
  };


  const analysis = useMemo(() => {
    const entries = Array.isArray(leaderboard) ? leaderboard : [];
    const totalEntries = entries.length;

    const add = (obj, key) => {
      if (!key || typeof key !== "string") return;
      obj[key] = (obj[key] || 0) + 1;
    };

    const winnerCounts = {};
    const runnerCounts = {};
    const overallPodiumCounts = {};
    const bracketFirstCounts = {};
    const bracketSecondCounts = {};
    const bracketTopTwoCounts = {};
    const topTwoCounts = {};
    const allPickCounts = {};

    Object.keys(BRACKETS).forEach(key => {
      bracketFirstCounts[key] = {};
      bracketSecondCounts[key] = {};
      bracketTopTwoCounts[key] = {};
    });

    entries.forEach(entry => {
      const p = entry?.picks || {};

      add(winnerCounts, p.winner);
      add(runnerCounts, p.runnerUp);
      add(overallPodiumCounts, p.winner);
      add(overallPodiumCounts, p.runnerUp);

      Object.entries(p.groupAdvances || {}).forEach(([bracket, group]) => {
        if (!Array.isArray(group)) return;

        const first = group[0];
        const second = group[1];

        if (!bracketFirstCounts[bracket]) bracketFirstCounts[bracket] = {};
        if (!bracketSecondCounts[bracket]) bracketSecondCounts[bracket] = {};
        if (!bracketTopTwoCounts[bracket]) bracketTopTwoCounts[bracket] = {};

        add(bracketFirstCounts[bracket], first);
        add(bracketSecondCounts[bracket], second);

        group.forEach(q => {
          add(bracketTopTwoCounts[bracket], q);
          add(topTwoCounts, q);
          add(allPickCounts, q);
        });
      });

      add(allPickCounts, p.winner);
      add(allPickCounts, p.runnerUp);
    });

    const sortCounts = (obj) => Object.entries(obj || {}).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));

    const winners = sortCounts(winnerCounts);
    const runners = sortCounts(runnerCounts);
    const topTwos = sortCounts(topTwoCounts);
    const allPicks = sortCounts(allPickCounts);
    const bracketAdvancesByGroup = Object.fromEntries(
      Object.keys(BRACKETS).map(key => [
        key,
        BRACKETS[key].queens.map(queen => [
          queen,
          bracketTopTwoCounts[key]?.[queen] || 0
        ]).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      ])
    );

    const pctFromObj = (obj, queen, denominator = totalEntries) => {
      if (!queen || !denominator) return 0;
      return ((obj?.[queen] || 0) / denominator) * 100;
    };

    const countFor = (list, queen) => {
      const found = list.find(([name]) => name === queen);
      return found ? found[1] : 0;
    };

    const pct = (count, denominator = totalEntries) => {
      if (!denominator) return 0;
      return Math.round((count / denominator) * 100);
    };

    const yourWinnerCount = countFor(winners, picks.winner);
    const crowdWinner = winners[0]?.[0] || null;
    const crowdRunnerUp = runners[0]?.[0] || null;
    const crowdTopSet = new Set(allPicks.slice(0, 6).map(([name]) => name));
    const yourSelectedQueens = [
      ...Object.values(picks.groupAdvances || {}).flat(),
      picks.winner,
      picks.runnerUp,
    ].filter(Boolean);

    const uniqueYourSelectedQueens = [...new Set(yourSelectedQueens)];

    // Weighted crowd-similarity score.
    // Exact overall winner matters most; exact runner-up is next.
    // Podium frequency adds partial credit when your overall picks are popular but in a different order.
    // Bracket #1/#2 picks are then scored by exact placement plus bracket top-2 popularity.
    let weightedScore = 0;
    let possibleWeight = 0;

    const addWeighted = (valuePct, weight) => {
      if (!Number.isFinite(valuePct)) return;
      weightedScore += valuePct * weight;
      possibleWeight += weight;
    };

    if (picks.winner) {
      addWeighted(pctFromObj(winnerCounts, picks.winner), 30);
      addWeighted(pctFromObj(overallPodiumCounts, picks.winner), 10);
    }

    if (picks.runnerUp) {
      addWeighted(pctFromObj(runnerCounts, picks.runnerUp), 22);
      addWeighted(pctFromObj(overallPodiumCounts, picks.runnerUp), 8);
    }

    Object.entries(picks.groupAdvances || {}).forEach(([bracket, group]) => {
      if (!Array.isArray(group)) return;
      const first = group[0];
      const second = group[1];

      if (first) {
        addWeighted(pctFromObj(bracketFirstCounts[bracket], first), 6);
        addWeighted(pctFromObj(bracketTopTwoCounts[bracket], first), 2);
      }

      if (second) {
        addWeighted(pctFromObj(bracketSecondCounts[bracket], second), 4);
        addWeighted(pctFromObj(bracketTopTwoCounts[bracket], second), 1.5);
      }
    });

    const similarityPct = possibleWeight
      ? Math.round(weightedScore / possibleWeight)
      : 0;

    const underdogs = uniqueYourSelectedQueens
      .filter(q => !crowdTopSet.has(q))
      .slice(0, 5);

    return {
      totalEntries,
      winners,
      runners,
      topTwos,
      allPicks,
      bracketAdvancesByGroup,
      crowdWinner,
      crowdRunnerUp,
      yourWinnerCount,
      yourWinnerPct: pct(yourWinnerCount),
      winnerPickRate: pct(yourWinnerCount),
      alignmentPct: similarityPct,
      underdogs,
    };
  }, [leaderboard, picks]);

  const analysisPercent = (count, denominator = analysis.totalEntries) => {
    if (!denominator) return 0;
    return Math.round((count / denominator) * 100);
  };

  const topWinnerPct = analysisPercent(analysis.winners?.[0]?.[1] || 0);
  const myRankLabel = myRank > 0 ? `#${myRank}` : "—";
  const myRankSub = myRank > 0 ? `of ${leaderboard.length} players` : "Submit to rank";

  const renderAnalysisRows = (rows, denominator = analysis.totalEntries, limit = 8) => (
    <div className="analysis-list">
      {rows.slice(0, limit).map(([name, count], i) => {
        const pct = analysisPercent(count, denominator);
        return (
          <div className="analysis-row" key={name}>
            <div className="analysis-row-name">{i + 1}. {QUEEN_EMOJI[name] || "✨"} {name}</div>
            <div className="analysis-row-count">{count} · {pct}%</div>
            <div className="analysis-bar"><div className="analysis-bar-fill" style={{width:`${pct}%`}}/></div>
          </div>
        );
      })}
    </div>
  );

  const renderBracketAdvanceRows = (bracketKey) => (
    <div className={`bracket-advance-card ${bracketKey}`}>
      <div className="bracket-advance-title">{BRACKETS[bracketKey].label}</div>
      {(analysis.bracketAdvancesByGroup?.[bracketKey] || []).map(([name, count]) => {
        const pct = analysisPercent(count, analysis.totalEntries);
        return (
          <div className="bracket-advance-row" key={name}>
            <div className="bracket-advance-name">{QUEEN_EMOJI[name] || "✨"} {name}</div>
            <div className="bracket-advance-pct">{pct}%</div>
            <div className="bracket-advance-bar">
              <div className="bracket-advance-bar-fill" style={{width:`${pct}%`}}/>
            </div>
          </div>
        );
      })}
    </div>
  );


  const renderRankChange = (entry) => {
    const change = Number(entry?.rankChange || 0);
    if (!change) return <span className="lb-change same">•</span>;
    if (change > 0) return <span className="lb-change up">▲ {change}</span>;
    return <span className="lb-change down">▼ {Math.abs(change)}</span>;
  };

  const shareNameSize = (() => {
    const len = (username || "MY").trim().length;
    if (len <= 6) return "clamp(2.8rem, 9.2vw, 3.95rem)";
    if (len <= 9) return "clamp(2.35rem, 8.1vw, 3.3rem)";
    if (len <= 12) return "clamp(1.98rem, 6.8vw, 2.72rem)";
    if (len <= 16) return "clamp(1.68rem, 5.8vw, 2.22rem)";
    return "clamp(1.42rem, 4.85vw, 1.9rem)";
  })();
  const shareHeaderNeedsDrop = ((username || "").trim().length < 12);


  const saveEmail = async () => {
    if (!email.trim() || !email.includes("@")) { showToast("Please enter a valid email address"); return; }
    if (!submitted) { showToast("Submit your bracket first!"); return; }
    setEmailLoading(true);

    try {
      const r = await fetch("/api/send-confirmation", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          to_email: email.trim(),
          to_name: username,
        }),
      });

      if (!r.ok) throw new Error("Email send failed: " + r.status);

      setEmailSaved(true);
      save(username, picks, submitted, email.trim());
      // Persist email on the leaderboard entry via the safe submit endpoint.
      // The server upserts atomically and only touches the entry for `username`.
      try {
        const { score, breakdown } = calcScore(picks, liveResults);
        const entry = {
          username, score, breakdown, picks,
          leagueCode: cleanLeagueCode(leagueCode),
          leagueName: cleanLeagueName(leagueName),
          submissionId: getSubmissionId(),
          email: email.trim(),
          submittedAt: new Date().toISOString(),
        };
        const result = await window.storage.submit(entry);
        if (result?.leaderboard) setLeaderboard(result.leaderboard);
      } catch (_) {}

      showToast("✅ Confirmation sent — check your inbox!");
    } catch (err) {
      showToast("Couldn't send email — please try again");
      console.error("Resend error:", err);
    }
    setEmailLoading(false);
  };

  const submitBracket = async () => {
    if (!username) { showToast("Set a username first!"); return; }
    if (!picksComplete()) { showToast("Complete all picks before submitting!"); return; }
    setLoading(true);
    try {
      // If locked, only allow updates to an already-existing entry. Read the
      // current leaderboard (public read) just to check whether this username
      // already has a slot.
      if (isLocked) {
        let lb = [];
        try { const r = await window.storage.get("drag-lb-v3", true); if (r?.value) lb = JSON.parse(r.value); } catch (_) {}
        const exists = lb.some(e => e?.username === username);
        if (!exists) {
          showToast("She done already done had hers! ✦ The leaderboard is closed — but you can still tally up your scorecard for fun!");
          setLoading(false); return;
        }
      }
      const { score, breakdown } = calcScore(picks, liveResults);
      const entry = {
        username,
        score,
        breakdown,
        picks,
        leagueCode: cleanLeagueCode(leagueCode),
        leagueName: cleanLeagueName(leagueName),
        submissionId: getSubmissionId(),
        submittedAt: new Date().toISOString()
      };
      const result = await window.storage.submit(entry);
      if (!result?.ok) {
        if (result?.error === "browser_mismatch") {
          setBrowserEditNoticeOpen(true);
        } else if (result?.error === "duplicate") {
          showToast("You done already done has your's!");
        } else {
          showToast("Submission rejected — please try again");
        }
        setLoading(false);
        return;
      }
      if (result.leaderboard) setLeaderboard(result.leaderboard);
      setSubmitted(true); save(username, picks, true);
      setCelebrating(true);
      setTimeout(() => setCelebrating(false), 1900);
      if (result.updated) {
        showToast("Updated your picks 💅");
      } else {
        showToast("You done already done submitted! 👑");
      }
    } catch (_) { showToast("Error saving — please try again"); }
    setLoading(false);
  };


  const shareCardToBlob = async () => {
    const node = shareCardRef.current;
    if (!node) throw new Error("Share card not found");

    const blob = await toBlob(node, {
      cacheBust: true,
      pixelRatio: 3,
      backgroundColor: "#08030e",
      filter: (el) => !el.classList?.contains("share-card-actions"),
    });

    if (!blob) throw new Error("Could not create image");
    return blob;
  };

  const downloadShareCard = async () => {
    try {
      const node = shareCardRef.current;
      if (!node) throw new Error("Share card not found");

      const dataUrl = await toPng(node, {
        cacheBust: true,
        pixelRatio: 3,
        backgroundColor: "#08030e",
        filter: (el) => !el.classList?.contains("share-card-actions"),
      });

      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `drag-madness-${(username || "bracket").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-picks.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();

      showToast("Share image downloaded ✨");
    } catch (err) {
      console.error("Download share card error:", err);
      showToast("Couldn't download image — screenshot the card instead");
    }
  };

  const copyShareCard = async () => {
    try {
      const blob = await shareCardToBlob();

      if (navigator.clipboard && window.ClipboardItem) {
        await navigator.clipboard.write([
          new ClipboardItem({ "image/png": blob })
        ]);
        showToast("Share image copied to clipboard ✨");
      } else {
        showToast("Clipboard image copy isn't supported here — use Download");
      }
    } catch (err) {
      console.error("Copy share card error:", err);
      showToast("Couldn't copy image — use Download instead");
    }
  };


  const crowdShareCardToBlob = async () => {
    const node = crowdShareCardRef.current;
    if (!node) throw new Error("Crowd share card not found");

    const blob = await toBlob(node, {
      cacheBust: true,
      pixelRatio: 3,
      backgroundColor: "#10051c",
      filter: (el) => !el.classList?.contains("crowd-share-actions"),
    });

    if (!blob) throw new Error("Could not create image");
    return blob;
  };

  const downloadCrowdShareCard = async () => {
    try {
      const node = crowdShareCardRef.current;
      if (!node) throw new Error("Crowd share card not found");

      const dataUrl = await toPng(node, {
        cacheBust: true,
        pixelRatio: 3,
        backgroundColor: "#10051c",
        filter: (el) => !el.classList?.contains("crowd-share-actions"),
      });

      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `drag-madness-${(username || "crowd").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-vs-crowd.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();

      showToast("Crowd comparison image downloaded ✨");
    } catch (err) {
      console.error("Download crowd share card error:", err);
      showToast("Couldn't download image — screenshot the card instead");
    }
  };

  const copyCrowdShareCard = async () => {
    try {
      const blob = await crowdShareCardToBlob();

      if (navigator.clipboard && window.ClipboardItem) {
        await navigator.clipboard.write([
          new ClipboardItem({ "image/png": blob })
        ]);
        showToast("Crowd comparison copied to clipboard ✨");
      } else {
        showToast("Clipboard image copy isn't supported here — use Download");
      }
    } catch (err) {
      console.error("Copy crowd share card error:", err);
      showToast("Couldn't copy image — use Download instead");
    }
  };


  const resetBracket = () => {
    setPicks(EMPTY_PICKS); setSubmitted(false);
    save(username, EMPTY_PICKS, false);
    showToast("Bracket reset! Start fresh 💅");
  };

  const showBracketSetupHero = tab === "bracket";
  const stepNameDone = !!username;
  const stepQueensDone = totalGroupSelections === 6;
  const stepFinalsDone = !!picks.winner && !!picks.runnerUp;
  const stepsComplete = [stepNameDone, stepQueensDone, stepFinalsDone].filter(Boolean).length;
  const currentBracketStep = !stepNameDone ? 1 : !stepQueensDone ? 2 : !stepFinalsDone ? 3 : 0;
  const stepProgressPct = Math.round((stepsComplete / 3) * 100);


  // ── RENDER ────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{css}</style>
      <div className="app">

        {/* NAV */}
        <nav className="nav">
          <div className="nav-logo">✦ DRAG<br/>MADNESS</div>
          <div className="nav-tabs">
            {[["bracket","🏆 Bracket"],["scoring","📊 Scoring"],["leaderboard","🌍 Leaderboard"],["analysis","📈 Analysis"],["community","💬 Community"]].map(([t,label]) => (
              <button key={t} className={`nav-tab${tab===t?" active":""}`} onClick={()=>setTab(t)}>{label}</button>
            ))}
            <a className="nav-tab" href="/bracket" target="_blank" rel="noopener noreferrer">🖨️ Print</a>
          </div>
          <a className="nav-donate" href="https://ko-fi.com/playdragmadness" target="_blank" rel="noopener noreferrer">☕ Donate</a>
        </nav>

        {tab !== "community" && (
          <>
        {/* HERO */}
        <div className="hero-strip">
          <span className="hero-eyebrow">All Stars 11 · Premieres May 8</span>
          <div className="hero-title">
            <span className="shimmer">All Stars 11</span><br/>
            <span style={{color:"var(--text)"}}>Drag Madness</span>
          </div>
          
          {showBracketSetupHero && !isLocked && countdownStr && (
            <div className="hero-countdown-pill" style={{marginTop:"0.7rem",display:"block",background:"rgba(245,200,66,0.1)",border:"1px solid rgba(245,200,66,0.25)",borderRadius:"2px",padding:"0.3rem 0.9rem",fontSize:"0.75rem",color:"var(--gold)",letterSpacing:"0.1em"}}>
              ⏳ LEADERBOARD CLOSES IN {countdownStr}
            </div>
          )}

          {showBracketSetupHero && isLocked && (
            <div className="hero-countdown-pill" style={{marginTop:"0.7rem",display:"block",background:"rgba(248,113,113,0.08)",border:"1px solid rgba(248,113,113,0.3)",borderRadius:"2px",padding:"0.3rem 0.9rem",fontSize:"0.75rem",color:"#f87171",letterSpacing:"0.1em"}}>
              🔒 LEADERBOARD CLOSED · SEASON UNDERWAY
            </div>
          )}
          {showBracketSetupHero && !submitted && (
            <>
              <div className="bracket-flow-container">
              <div className="bracket-stepper-card">
                <div className="analysis-card-title" style={{textAlign:"center",marginBottom:"0.8rem"}}>How to Play</div>
                <div className="bracket-stepper-row">
                  <div className={`bracket-step ${stepNameDone ? "done" : currentBracketStep === 1 ? "active" : ""}`}>
                    <span className="bracket-step-icon">{stepNameDone ? "✓" : currentBracketStep === 1 ? "🔥" : "○"}</span>
                    <span className="bracket-step-text">
                      1. Set your username (Required)
                      <span className="bracket-step-sub">League play optional</span>
                    </span>
                  </div>
                  <div className="bracket-step-connector" />
                  <div className={`bracket-step ${stepQueensDone ? "done" : currentBracketStep === 2 ? "active" : ""}`}>
                    <span className="bracket-step-icon">{stepQueensDone ? "✓" : currentBracketStep === 2 ? "🔥" : "○"}</span>
                    <span className="bracket-step-text">
                      2. Pick two queens from each bracket
                    </span>
                  </div>
                  <div className="bracket-step-connector" />
                  <div className={`bracket-step ${stepFinalsDone ? "done" : currentBracketStep === 3 ? "active" : ""}`}>
                    <span className="bracket-step-icon">{stepFinalsDone ? "✓" : currentBracketStep === 3 ? "🔥" : "○"}</span>
                    <span className="bracket-step-text">
                      3. Pick your winner + runner-up.
                    </span>
                  </div>
                </div>
                <div className="bracket-stepper-progress-line">
                  <span>Progress: {stepsComplete} / 3 complete</span>
                  <div className="bracket-stepper-mini-bar">
                    <div className="bracket-stepper-mini-fill" style={{width:`${stepProgressPct}%`}} />
                  </div>
                </div>
              </div>

              <div className="bracket-setup-card stage-card setup-stage-card">
                <div className="stage-header">
                  <div className="stage-num">1</div>
                  <div className="stage-info">
                    <span className="stage-badge" style={{background:"rgba(245,200,66,0.16)",color:"var(--gold)"}}>Stage 1 · Setup</span>
                    <div className="sec-title">Set Your Username</div>
                    <div className="sec-desc">Choose a name before submitting. League play is optional and can be added anytime.</div>
                  </div>
                </div>

                {!username ? (
                  <div className="bracket-setup-section required-focus bracket-next-glow">
                    <div className="bracket-setup-label">Required before submitting</div>
                    <div className="bracket-setup-actions stage1-name-row">
                      <input className="username-input" placeholder="Add your real / drag name…" value={usernameInput}
                        onChange={e=>setUsernameInput(e.target.value)} onKeyDown={e=>e.key==="Enter"&&handleSetUsername()}/>
                      <button className="btn btn-ghost name-generate-btn" onClick={generateDragName}>Generate</button>
                      <button className="btn btn-gold" onClick={handleSetUsername}>Set Name</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="bracket-setup-section required-focus">
                      <div className="stage1-confirmation">
                        <div>
                          <div className="stage1-confirmation-main">You’re in as <strong>{username}</strong> 👑</div>
                          <div className="stage1-complete-pill">✓ Step 1 complete</div>
                        </div>
                        <button className="btn btn-ghost" style={{padding:"0.3rem 0.7rem",fontSize:"0.68rem"}} onClick={()=>{setUsername("");setUsernameInput("");}}>Change</button>
                      </div>
                    </div>

                    <div className="bracket-setup-section optional-soft league-section-reveal" style={{marginTop:"0.65rem"}}>
                      <div className="bracket-setup-label">
                        League play is optional
                      </div>
                      <div className="bracket-setup-helper">
                        Skip this to play solo against the world 🌍. Create or join a league to compete with friends 👀. You can join or create a league anytime.
                      </div>

                      {leagueCode ? (
                        <div className="bracket-setup-current" style={{marginTop:"0.55rem"}}>
                          <span><strong>{leagueName || "League"}</strong> · {leagueCode}</span>
                          <div className="bracket-setup-actions">
                            <button className="btn btn-ghost" style={{fontSize:"0.68rem",padding:"0.3rem 0.65rem",minHeight:"30px"}} onClick={copyLeagueLink}>Copy Invite</button>
                            <button className="btn btn-ghost" style={{fontSize:"0.68rem",padding:"0.3rem 0.65rem",minHeight:"30px"}} onClick={leaveLeague}>Change</button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="bracket-setup-actions league-row-clean" style={{marginTop:"0.55rem"}}>
                            <input
                              className="league-input"
                              placeholder="League name (max 16)"
                              maxLength={16}
                              value={leagueNameInput}
                              onChange={e => setLeagueNameInput(cleanLeagueName(e.target.value))}
                            />
                            <button className={`btn btn-gold league-pulse ${!leagueCode ? "league-pulse-active" : ""}`} onClick={createLeague}>Create</button>
                          </div>
                          <div className="bracket-setup-actions league-row-clean" style={{marginTop:"0.5rem"}}>
                            <input
                              className="league-input"
                              placeholder="Enter league code"
                              value={leagueCodeInput}
                              onChange={e => setLeagueCodeInput(cleanLeagueCode(e.target.value))}
                            />
                            <button className="btn btn-ghost" onClick={joinLeague}>Join</button>
                          </div>
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>

              </div>            </>
          )}
        </div>
          </>
        )}

        {/* ══ BRACKET TAB ══ */}
        {tab==="bracket" && (
          <div className="page">
{/* Score panel */}
            {submitted && (
              <div className="score-panel">
                <div>
                  <div className="score-label">Your Score</div>
                  <div className="score-big">{myScore}</div>
                  <div className="score-max">of {totalPossible} possible pts</div>
                </div>
                {myBreakdown.length > 0 ? (
                  <div className="breakdown-list">
                    <div style={{fontSize:"0.65rem",letterSpacing:"0.2em",color:"var(--muted)",textTransform:"uppercase",marginBottom:"0.5rem"}}>Correct Picks</div>
                    {myBreakdown.map((b,i)=>(
                      <div key={i} className="breakdown-item">
                        <div><div>{b.label}</div><div className="breakdown-sub">{QUEEN_EMOJI[b.queen]} {b.queen}</div></div>
                        <div className="breakdown-pts">+{b.pts}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-points-message">No points yet — scores update after elimination episodes air 📅</div>
                )}
                <div style={{marginLeft:"auto",alignSelf:"flex-start"}}>
                  <button className="btn btn-ghost" onClick={resetBracket}>Reset</button>
                </div>
              </div>
            )}

            {/* ── STAGE 2 ── */}
            <div className="stage-wrap">
              <div className="stage-header">
                <div className="stage-num">2</div>
                <div className="stage-info">
                  <span className="stage-badge" style={{background:"rgba(255,45,154,0.12)",color:"var(--pink)"}}>Stage 2 · Group Phase</span>
                  <div className="sec-title">Pick the <em>Top 2</em> from Each Group</div>
                  <div className="sec-desc">
                    6 queens compete in each bracket. Select the 2 who advance to the semi-finals (6 total). <strong style={{color:"var(--text)"}}>Order matters:</strong> your first pick is your <strong style={{color:"var(--gold)"}}>predicted bracket winner</strong> (+{SCORING.bracketWinner} bonus), your second is your <strong style={{color:"var(--gold)"}}>predicted bracket runner-up</strong> (+{SCORING.bracketRunnerUp} bonus). Tap Swap to reorder.
                    &nbsp;{Object.keys(BRACKETS).filter(groupComplete).length}/3 groups complete
                    {allGroupsComplete && <span style={{color:"var(--green)",marginLeft:"0.4rem"}}>✓</span>}
                  </div>
                </div>
              </div>
              <div className="groups-row">
                {Object.entries(BRACKETS).map(([key, bracket]) => {
                  const gp = sanitizeGroupPicks(key, picks.groupAdvances[key] || []);
                  const done = groupComplete(key);
                  return (
                    <div className={`group-card ${key} ${currentBracketStep === 2 ? "bracket-next-glow" : ""}`} key={key}>
                      <div className="group-header">
                        <div className="group-dot" style={{background:bracket.color,boxShadow:`0 0 8px ${bracket.glow}`}}/>
                        <div className="group-name">{bracket.label}</div>
                        <div className="group-count"><span className="num">{gp.length}</span>/2 selected{done&&<span style={{color:"var(--green)",marginLeft:"0.3rem"}}>✓</span>}</div>
                      </div>
                      <div className="queens-list">
                        {bracket.queens.map(queen => {
                          const isSel = gp.includes(queen);
                          const rank = isSel ? gp.indexOf(queen)+1 : null;
                          const officialList = liveResults.semiFinalists[key] || [];
                          const officialAdv = officialList.includes(queen);
                          const isCorrect = resultsMode && isSel && officialAdv;
                          const isWrong = resultsMode && isSel && !officialAdv;
                          const isDisabled = !isSel && done;
                          // Bracket-position bonus chips (hidden if bracket declared tied)
                          const tied = liveResults.tiedBrackets?.[key];
                          const isBracketWinMatch = resultsMode && !tied && isSel && rank===1 && officialList[0]===queen;
                          const isBracketRunMatch = resultsMode && !tied && isSel && rank===2 && officialList[1]===queen;
                          return (
                            <div key={queen}
                              className={`queen-row${isSel?" q-selected":""}${isCorrect?" q-correct":""}${isWrong?" q-wrong":""}${isDisabled?" q-disabled":""}${flashQueen === queen ? " flash-check" : ""}`}
                              style={{"--sel-color":bracket.color}}
                              onClick={()=>!submitted&&toggleGroupAdvance(key,queen)}>
                              <span className="q-emoji">{QUEEN_EMOJI[queen]}</span>
                              <span className="q-name">{queen}</span>
                              {isCorrect&&<span className="q-pts">+{SCORING.groupAdvance}</span>}
                              {isBracketWinMatch&&<span className="q-pts" style={{color:"var(--gold)"}}>+{SCORING.bracketWinner}</span>}
                              {isBracketRunMatch&&<span className="q-pts" style={{color:"var(--gold)"}}>+{SCORING.bracketRunnerUp}</span>}
                              <span className="q-rank" style={{background:bracket.color}}>{rank?`#${rank}`:"ADV"}</span>
                            </div>
                          );
                        })}
                      </div>
                      {gp.length === 2 && !submitted && (
                        <div className="group-swap" onClick={()=>swapGroupOrder(key)}>
                          <span style={{fontSize:"0.85rem"}}>↕</span> Swap #1 ↔ #2
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="divider"/>

            {/* ── STAGE 3 ── */}
            <div className="stage-wrap">
              <div className="stage-header">
                <div className="stage-num">3</div>
                <div className="stage-info">
                  <span className="stage-badge" style={{background:"rgba(245,200,66,0.16)",color:"var(--gold)"}}>Stage 3 · Lip Sync Smackdown</span>
                  <div className="sec-title">Pick the <em>Winner & Runner-Up</em></div>
                  <div className="sec-desc">
                    From your 6 semi-finalists, who wins the crown and who finishes as runner-up?
                    {picks.winner&&picks.runnerUp&&picks.winner!==picks.runnerUp&&<span style={{color:"var(--green)",marginLeft:"0.4rem"}}>✓ Complete</span>}
                  </div>
                </div>
              </div>

              {allSemiFinalists.length === 0 ? (
                <p style={{color:"var(--muted)",fontSize:"0.85rem",fontStyle:"italic"}}>Complete Stage 2 first to unlock your podium picks…</p>
              ) : (
                <div className="podium-row">
                  {/* 1st Place */}
                  <div className="podium-card" style={{borderColor:picks.winner?"rgba(245,200,66,0.3)":"rgba(255,255,255,0.07)"}}>
                    <div className="podium-header">
                      <span className="podium-medal">🥇</span>
                      <span className="podium-label">1st Place · Winner</span>
                      <span className="podium-pts">+{SCORING.winner} pts</span>
                    </div>
                    <div className="podium-options">
                      {Object.entries(BRACKETS).map(([key, bracket]) => {
                        const advancers = picks.groupAdvances[key] || [];
                        if (advancers.length === 0) return null;
                        return advancers.map(queen => {
                          const isWinner = picks.winner === queen;
                          const officialW = liveResults.winner === queen;
                          const officialRU = liveResults.runnerUp === queen;
                          const isCorrect = resultsMode && isWinner && officialW;
                          const isPartial = resultsMode && isWinner && officialRU;
                          const isDisabled = !isWinner && picks.runnerUp === queen;
                          return (
                            <div key={queen}
                              className={`podium-opt${isWinner?" po-winner":""}${isCorrect?" po-correct":""}${isPartial?" po-partial":""}${resultsMode&&isWinner&&!officialW&&!isPartial?" po-wrong":""}${isDisabled?" po-disabled":""}`}
                              onClick={()=>!isDisabled&&pickPodium(queen,"winner")}
                              style={{"--bracket-accent": bracket.color}}>
                              <div className="podium-bracket-dot" style={{background:bracket.color}}/>
                              <span className="p-emoji">{QUEEN_EMOJI[queen]}</span>
                              <span className="p-name">{queen}</span>
                              {isWinner&&<span className="p-tag" style={{color:"var(--gold)"}}>WINNER</span>}
                              {isCorrect&&<span className="q-pts" style={{fontSize:"0.78rem"}}>+{SCORING.winner}</span>}
                              {isPartial&&<span className="q-pts" style={{fontSize:"0.78rem",color:"var(--gold)"}}>+{SCORING.podiumSwap}</span>}
                            </div>
                          );
                        });
                      })}
                    </div>
                  </div>

                  {/* 2nd Place */}
                  <div className="podium-card" style={{borderColor:picks.runnerUp?"rgba(192,192,192,0.25)":"rgba(255,255,255,0.07)"}}>
                    <div className="podium-header">
                      <span className="podium-medal">🥈</span>
                      <span className="podium-label">2nd Place · Runner-Up</span>
                      <span className="podium-pts">+{SCORING.runnerUp} pts</span>
                    </div>
                    <div className="podium-options">
                      {Object.entries(BRACKETS).map(([key, bracket]) => {
                        const advancers = picks.groupAdvances[key] || [];
                        if (advancers.length === 0) return null;
                        return advancers.map(queen => {
                          const isRU = picks.runnerUp === queen;
                          const officialRU = liveResults.runnerUp === queen;
                          const officialW = liveResults.winner === queen;
                          const isCorrect = resultsMode && isRU && officialRU;
                          const isPartial = resultsMode && isRU && officialW;
                          const isDisabled = !isRU && picks.winner === queen;
                          return (
                            <div key={queen}
                              className={`podium-opt${isRU?" po-runner":""}${isCorrect?" po-correct":""}${isPartial?" po-partial":""}${resultsMode&&isRU&&!officialRU&&!isPartial?" po-wrong":""}${isDisabled?" po-disabled":""}`}
                              onClick={()=>!isDisabled&&pickPodium(queen,"runnerUp")}
                              style={{"--bracket-accent": bracket.color}}>
                              <div className="podium-bracket-dot" style={{background:bracket.color}}/>
                              <span className="p-emoji">{QUEEN_EMOJI[queen]}</span>
                              <span className="p-name">{queen}</span>
                              {isRU&&<span className="p-tag" style={{color:"#c0c0c0"}}>RUNNER-UP</span>}
                              {isCorrect&&<span className="q-pts" style={{fontSize:"0.78rem"}}>+{SCORING.runnerUp}</span>}
                              {isPartial&&<span className="q-pts" style={{fontSize:"0.78rem",color:"var(--gold)"}}>+{SCORING.podiumSwap}</span>}
                            </div>
                          );
                        });
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Lock banner */}
            {!submitted && (
              <div className={`lock-banner ${isLocked ? "closed" : "open"}`} style={{marginTop:"1.5rem"}}>
                <span className="lock-banner-icon">{isLocked ? "🔒" : "⏳"}</span>
                <div className="lock-banner-text">
                  {isLocked ? (
                    <><strong style={{color:"var(--red)"}}>Submissions are closed.</strong> The season has started — the leaderboard is locked to new entries. You can still fill out your bracket and use the printable graphic, but scores won't be recorded.</>
                  ) : (
                    <><strong style={{color:"var(--gold)"}}>Leaderboard closes when Episode 1 drops.</strong> Submit your bracket before May 8 to lock in your score and compete on the global leaderboard.</>
                  )}
                </div>
                {!isLocked && countdownStr && (
                  <div className="lock-countdown">{countdownStr}</div>
                )}
              </div>
            )}

            {!submitted && (
              <div style={{textAlign:"center",color:"rgba(237,229,255,0.74)",fontSize:"0.82rem",margin:"0.35rem 0 1rem"}}>
                Finish Stage 1 and Stage 2, then submit once to lock your bracket.
              </div>
            )}

            {/* Submit */}
            <div className="submit-wrap" style={{textAlign:"center",display:"flex",gap:"0.8rem",justifyContent:"center",flexWrap:"wrap"}}>
              {!submitted ? (
                <button
                  className="btn btn-pink"
                  style={{padding:"0.8rem 2.5rem",fontSize:"0.85rem", opacity: picksComplete() && username && !isLocked ? 1 : 0.4}}
                  onClick={submitBracket}
                  disabled={loading || isLocked}
                >
                  {loading ? "Saving…" : isLocked ? "🔒 Submissions Closed" : "✦ Submit Bracket"}
                </button>
              ) : (
                <>
                  <button className="btn btn-ghost" onClick={resetBracket}>Reset & Re-pick</button>
                  <button className="btn btn-gold" onClick={()=>setTab("leaderboard")}>See Leaderboard →</button>
                </>
              )}
              <button className="btn btn-ghost" style={{fontSize:"0.7rem"}} onClick={()=>{setResultsMode(r=>!r);showToast(resultsMode?"Results hidden":"Showing simulated scoring 🔮");}}>
                {resultsMode?"Hide":"Preview"} Scoring
              </button>
            </div>
            {!username&&<div style={{textAlign:"center",color:"var(--muted)",fontSize:"0.78rem",marginTop:"0.8rem"}}>Set a username above to submit</div>}
            {isLocked && !submitted && <div style={{textAlign:"center",color:"var(--muted)",fontSize:"0.78rem",marginTop:"0.5rem"}}>You can still fill out your bracket — it just won't count on the leaderboard.</div>}

            {/* Just-for-fun score — shown to post-lock users who complete all picks */}
            {isLocked && !submitted && picksComplete() && (
              <div className="score-panel" style={{marginTop:"1.5rem", borderColor:"rgba(255,255,255,0.1)"}}>
                <div>
                  <div className="score-label" style={{color:"var(--muted)"}}>Just for Fun Score</div>
                  <div className="score-big" style={{fontSize:"3rem"}}>{myScore}</div>
                  <div className="score-max">of {totalPossible} possible pts</div>
                  <div style={{marginTop:"0.5rem",fontSize:"0.72rem",background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:"2px",padding:"0.35rem 0.6rem",color:"var(--muted)",lineHeight:1.5}}>
                    🎭 Not on the leaderboard — just for fun!
                  </div>
                </div>
                {myBreakdown.length > 0 ? (
                  <div className="breakdown-list">
                    <div style={{fontSize:"0.65rem",letterSpacing:"0.2em",color:"var(--muted)",textTransform:"uppercase",marginBottom:"0.5rem"}}>Correct Picks So Far</div>
                    {myBreakdown.map((b,i) => (
                      <div key={i} className="breakdown-item">
                        <div><div>{b.label}</div><div className="breakdown-sub">{QUEEN_EMOJI[b.queen]} {b.queen}</div></div>
                        <div className="breakdown-pts">+{b.pts}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{color:"var(--muted)",fontSize:"0.85rem",alignSelf:"center"}}>
                    No correct picks yet — scores update as episodes air 📅
                  </div>
                )}
              </div>
            )}



            {submitted && (
              <div className="post-submit-prompt">
                <div><strong>You’re now competing globally 🌍</strong></div>
                <div style={{marginTop:"0.35rem",color:"rgba(237,229,255,0.78)",lineHeight:1.5}}>
                  🏆 Want to compete with friends? Create a league and invite friends 👀
                </div>
              </div>
            )}

            {submitted && (
              <div style={{textAlign:"center"}}>
                <a className="community-inline-link" href="https://www.reddit.com/r/DragMadness/" target="_blank" rel="noopener noreferrer">
                  💬 Post your bracket + talk your trash in r/DragMadness
                </a>
              </div>
            )}

            {submitted && (
              <div className="post-submit-card">
                <div className="post-submit-title">🎉 You’re in!</div>
                <div className="post-submit-sub">Share your bracket and see how you stack up 👀</div>
                {picks.winner && (
                  <div className="share-winner-rate">{analysis.winnerPickRate}% picked your winner 👀</div>
                )}
                <div className="post-submit-actions">
                  <button className="btn btn-gold" onClick={downloadShareCard}>Download Share Image</button>
                  <button className="btn btn-ghost" onClick={() => setTab("leaderboard")}>View Leaderboard</button>
                </div>
              </div>
            )}

            {/* Instagram-style share square — shown after submission */}
            {submitted && (
              <div className="share-card-wrap">
                <div className="share-card-title">Screenshot & share your bracket</div>
                <div className="share-card" ref={shareCardRef} aria-label="Shareable Drag Madness bracket summary">
                  <div className="share-card-main">
                    <div style={shareHeaderNeedsDrop ? {paddingTop: "0.34rem"} : undefined}>
                      <div className="share-card-kicker">All Stars 11 · Drag Madness</div>
                      <div className="share-card-name" style={{fontSize: shareNameSize}}>{username || "My"}’s Picks</div>
                      <div className="share-card-sub">Bracket Predictions</div>
                      {leagueName && <div className="mini-league-tag">League: {leagueName}</div>}
                    </div>

                    <div>
                      <div className="share-card-brackets">
                        {Object.entries(BRACKETS).map(([key, bracket]) => {
                          const bracketPicks = picks.groupAdvances?.[key] || [];
                          return (
                            <div className="share-bracket-row" key={key} style={{"--bracket-color": bracket.color}}>
                              <div className="share-bracket-label">{bracket.label}</div>
                              <div className="share-pick-line">
                                <span className="share-pick-rank">#1</span>
                                <span className="share-pick-emoji">{bracketPicks[0] ? QUEEN_EMOJI[bracketPicks[0]] : ""}</span>
                                <span>{bracketPicks[0] || "—"}</span>
                              </div>
                              <div className="share-pick-line">
                                <span className="share-pick-rank">#2</span>
                                <span className="share-pick-emoji">{bracketPicks[1] ? QUEEN_EMOJI[bracketPicks[1]] : ""}</span>
                                <span>{bracketPicks[1] || "—"}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      <div className="share-overall share-overall-stacked">
                        <div className="share-overall-card overall-winner">
                          <div className="share-overall-label">Overall Picks</div>
                          <div className="share-overall-pick">
                            <span className="share-overall-emoji">👑</span>
                            <span className="share-overall-rank">#1</span>
                            <span className="share-overall-name">{picks.winner || "—"}</span>
                          </div>
                          <div className="share-overall-pick overall-runner-line">
                            <span className="share-overall-emoji">🥈</span>
                            <span className="share-overall-rank">#2</span>
                            <span className="share-overall-name">{picks.runnerUp || "—"}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="share-card-brand">
                    <div className="share-card-brand-small">Make your picks now for FREE at</div>
                    <div className="share-card-brand-url">playdragmadness.com</div>
                  </div>
                </div>

                <div className="share-card-actions">
                  <button className="btn btn-gold" onClick={downloadShareCard}>
                    Download as Image
                  </button>
                  <button className="btn btn-ghost" onClick={copyShareCard}>
                    Copy to Clipboard
                  </button>
                </div>
              </div>
            )}

            {/* Email notification — shown only after submission */}
            {submitted && (
              <div className="email-prominent">
                {!emailSaved ? (
                  <>
                    <div className="email-title">👀 See if your queens slayed this week</div>
                    <div className="email-sub">
                      We’ll email you after each elimination episode with your score, rank, and all the leaderboard drama 💅
                    </div>
                    <div className="email-input-row">
                      <input
                        className="email-input"
                        type="email"
                        placeholder="yourname@example.com"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        onKeyDown={e => e.key === "Enter" && saveEmail()}
                        disabled={emailLoading}
                      />
                      <button
                        className="btn btn-gold"
                        style={{fontSize:"0.8rem", opacity: email.includes("@") ? 1 : 0.55, whiteSpace:"nowrap"}}
                        onClick={saveEmail}
                        disabled={emailLoading}
                      >
                        {emailLoading ? "Saving…" : "Send me the tea ☕"}
                      </button>
                    </div>
                    <div className="email-trust">
                      No spam. Just results after each elimination episode.
                    </div>
                  </>
                ) : (
                  <div className="email-saved-msg" style={{justifyContent:"center"}}>
                    <span>✅</span>
                    <span>We'll email <strong style={{color:"var(--text)"}}>{email}</strong> after each elimination episode.</span>
                  </div>
                )}
              </div>
            )}

            {/* Ko-fi banner — primary CTA */}
            <div className="kofi-banner scoring-note-card support-section">
              <div className="support-text">
                Love the site? It's free to play and always will be.<br/>
                If you'd like to help keep the lights on, a coffee (or a cocktail ! 🍹) goes a long way. ✦
              </div>
              <a className="support-button" href="https://ko-fi.com/playdragmadness" target="_blank" rel="noopener noreferrer">
                ☕ Buy me a coffee
              </a>
            </div>

          </div>
        )}

        {/* ══ SCORING TAB ══ */}
        {tab==="scoring" && (
          <div className="page">
            <div><span className="sec-label">Points System</span><div className="sec-title">How to <em>Score</em></div></div>

            <div className="scoring-grid" style={{marginTop:"1.2rem"}}>
              <div className="scoring-item">
                <div className="scoring-pts">+{SCORING.groupAdvance}</div>
                <div style={{fontWeight:500}}>Group Advancer</div>
                <div className="scoring-name">Each correct pick to advance from a group — 6 possible (2 per bracket), worth {SCORING.groupAdvance * 6} pts total</div>
              </div>
              <div className="scoring-item" style={{borderColor:"rgba(245,200,66,0.18)"}}>
                <div className="scoring-pts">+{SCORING.bracketWinner}</div>
                <div style={{fontWeight:500}}>Bracket #1 Bonus</div>
                <div className="scoring-name">Your <strong style={{color:"var(--text)"}}>first pick</strong> from a bracket finishes as that bracket's #1 (top points across the season's episodes). Up to {SCORING.bracketWinner * 3} pts total.</div>
              </div>
              <div className="scoring-item" style={{borderColor:"rgba(245,200,66,0.18)"}}>
                <div className="scoring-pts">+{SCORING.bracketRunnerUp}</div>
                <div style={{fontWeight:500}}>Bracket #2 Bonus</div>
                <div className="scoring-name">Your <strong style={{color:"var(--text)"}}>second pick</strong> from a bracket finishes as that bracket's #2. Up to {SCORING.bracketRunnerUp * 3} pts total.</div>
              </div>
              <div className="scoring-item" style={{borderColor:"rgba(245,200,66,0.3)"}}>
                <div className="scoring-pts">+{SCORING.winner}</div>
                <div style={{fontWeight:500}}>👑 All Stars Winner 👑</div>
                <div className="scoring-name">Correctly predict the Hall of Fame queen — worth the most points</div>
              </div>
              <div className="scoring-item">
                <div className="scoring-pts">+{SCORING.runnerUp}</div>
                <div style={{fontWeight:500}}>All Stars Runner-Up</div>
                <div className="scoring-name">Correctly predict who finishes as the runner-up in the Lip Sync Smackdown</div>
              </div>
              <div className="scoring-item" style={{borderColor:"rgba(245,200,66,0.18)"}}>
                <div className="scoring-pts">+{SCORING.podiumSwap}</div>
                <div style={{fontWeight:500}}>Podium Swap · Partial Credit</div>
                <div className="scoring-name">If your overall winner pick lands as runner-up — or your runner-up pick takes the crown — you still bank some points for picking the right podium queen</div>
              </div>
            </div>
            <div className="divider"/>
            <div className="scoring-note-card" style={{marginTop:"0.2rem"}}>
              <div><span className="sec-label">Max Possible</span><div className="sec-title">Best Case: <em>{totalPossible} Points</em></div></div>
              <div style={{color:"var(--muted)",fontSize:"0.88rem",lineHeight:1.9,marginTop:"0.8rem"}}>
                <div>6× Group advancers · {SCORING.groupAdvance} pts = <strong style={{color:"var(--text)"}}>{SCORING.groupAdvance*6} pts</strong></div>
                <div>3× Bracket #1 finisher · {SCORING.bracketWinner} pts = <strong style={{color:"var(--text)"}}>{SCORING.bracketWinner*3} pts</strong></div>
                <div>3× Bracket #2 finisher · {SCORING.bracketRunnerUp} pts = <strong style={{color:"var(--text)"}}>{SCORING.bracketRunnerUp*3} pts</strong></div>
                <div>1× All Stars Runner-Up · {SCORING.runnerUp} pts = <strong style={{color:"var(--text)"}}>{SCORING.runnerUp} pts</strong></div>
                <div>1× All Stars Winner · {SCORING.winner} pts = <strong style={{color:"var(--gold)"}}>{SCORING.winner} pts</strong></div>
                <div style={{marginTop:"0.8rem",fontStyle:"italic"}}>Podium swap (+{SCORING.podiumSwap} pts) is partial credit when you miss the exact spot — it doesn't add to the max, it just softens the blow.</div>
              </div>
            </div>
            <div className="divider"/>
            <div className="scoring-note-card" style={{marginTop:"0.2rem"}}>
              <div><span className="sec-label">When Scores Update</span><div className="sec-title">Live <em>Episode Scoring</em></div></div>
              <div style={{color:"var(--muted)",fontSize:"0.88rem",lineHeight:1.7,marginTop:"0.8rem"}}>
                Scores are calculated against official results updated after each episode airs on Paramount+.
                Season premieres <strong style={{color:"var(--text)"}}>May 8, 2026</strong> with a two-episode drop.
              </div>
            </div>
            <div className="kofi-banner scoring-note-card support-section">
              <div className="support-text">
                Love the site? It's free to play and always will be.<br/>
                If you'd like to help keep the lights on, a coffee (or a cocktail ! 🍹) goes a long way. ✦
              </div>
              <a className="support-button" href="https://ko-fi.com/playdragmadness" target="_blank" rel="noopener noreferrer">
                ☕ Buy me a coffee
              </a>
            </div>
          </div>
        )}

        {/* ══ LEADERBOARD TAB ══ */}
        {tab==="leaderboard" && (
          <div className="page">
            <div><span className="sec-label">Global Rankings</span><div className="sec-title">The <em>Leaderboard</em></div></div>

            <div className="dashboard-kpi-grid leaderboard-dashboard-top">
              <div className="dashboard-kpi-card">
                <div className="dashboard-kpi-label">Players</div>
                <div className="dashboard-kpi-value">{shownLeaderboard.length}</div>
                <div className="dashboard-kpi-sub">{leaderboardView === "league" && activeLeagueCode ? "in this league" : "on the global board"}</div>
              </div>
              <div className="dashboard-kpi-card">
                <div className="dashboard-kpi-label">Leader</div>
                <div className="dashboard-kpi-value" style={{fontSize:"1.85rem"}}>{topLeaderboardEntry?.username || "—"}</div>
                <div className="dashboard-kpi-sub">{topScore} pts</div>
              </div>
              <div className="dashboard-kpi-card">
                <div className="dashboard-kpi-label">Your Rank</div>
                <div className="dashboard-kpi-value">{shownRank > 0 ? `#${shownRank}` : "—"}</div>
                <div className="dashboard-kpi-sub">{shownRank > 0 ? `of ${shownLeaderboard.length}` : "Submit to rank"}</div>
              </div>
              <div className="dashboard-kpi-card">
                <div className="dashboard-kpi-label">Your Score</div>
                <div className="dashboard-kpi-value">{myShownScore || 0}</div>
                <div className="dashboard-kpi-sub">{pointsBehindLeader ? `${pointsBehindLeader} behind the leader` : shownRank === 1 ? "currently leading 👑" : "points update after episodes"}</div>
              </div>
            </div>

            <div className="leaderboard-insight-card">
              <div className="leaderboard-insight-label">Standout Insight</div>
              <div className="leaderboard-insight-main">
                {leaderboardInsightMain}
              </div>
              <div className="leaderboard-insight-sub">
                {leaderboardInsightSub}
              </div>
            </div>

            <div className="dashboard-section-header">
              <div className="dashboard-section-title">🏆 Rankings</div>
              <div className="dashboard-section-note">Click a username to view their full bracket</div>
            </div>
<div className="leaderboard-toggle">
              <button
                type="button"
                className={`btn btn-ghost${leaderboardView === "global" ? " active" : ""}`}
                onClick={showGlobalLeaderboard}
              >
                🌍 Global
              </button>
              <button
                type="button"
                className={`btn btn-ghost${leaderboardView === "league" ? " active" : ""}`}
                onClick={showLeagueLeaderboard}
                disabled={!activeLeagueCode}
                style={{opacity: activeLeagueCode ? 1 : 0.45}}
              >
                👥 My League
              </button>
            </div>

            <div className="league-context-card">
              {leaderboardView === "league" && activeLeagueCode
                ? `👥 ${leagueName || "My League"} — ${shownLeaderboard.length} ${shownLeaderboard.length === 1 ? "player" : "players"}`
                : `🌍 Global Leaderboard — ${leaderboard.length} ${leaderboard.length === 1 ? "player" : "players"}`}
            </div>

            {shownLeaderboard.length===0 ? (
              <div style={{textAlign:"center",padding:"4rem 1rem",color:"var(--muted)"}}>
                <div style={{fontSize:"2.5rem",marginBottom:"0.8rem"}}>👑</div>
                <div style={{fontSize:"0.9rem"}}>{leaderboardView === "league" ? "Be the first to submit in this league 👀" : "Be the first to submit a bracket 👀"}</div>
                <button className="btn btn-pink" style={{marginTop:"1.5rem"}} onClick={()=>setTab("bracket")}>Fill Out Bracket</button>
              </div>
            ) : (
              <div className="lb-wrap" style={{marginTop:"1.2rem"}}>
                <div className="leaderboard-table-card">
                <div className="lb-header post-ep">
                  <div className="lb-col">#</div><div className="lb-col">Player</div>
                  <div className="lb-col league-col">League</div>
                  <div className="lb-col right">Pts</div><div className="lb-col right">Change</div>
                </div>
                {shownLeaderboard.map((entry,i)=>{
                  const isMe = entry.username===username;
                  const medals=["🥇","🥈","🥉"];
                  return (
                    <div key={entry.username} className={`lb-row post-ep${isMe?" me current-user":""}`}>
                      <div className={`lb-rank${i<3?" top":""}`}>{i<3?medals[i]:i+1}</div>
                      <div className="lb-name">
                        <button
                          type="button"
                          onClick={() => setSelectedEntry(entry)}
                          style={{
                            background: "none",
                            border: "none",
                            padding: 0,
                            color: "var(--text)",
                            cursor: "pointer",
                            font: "inherit",
                            fontWeight: 600,
                            textDecoration: "underline",
                            textDecorationColor: "rgba(245,200,66,0.45)",
                            textUnderlineOffset: "3px",
                          }}
                        >
                          {entry.username}
                        </button>{isMe&&<span className="badge-you">You</span>}
                        <span>Winner: {entry.picks?.winner?`${QUEEN_EMOJI[entry.picks.winner]} ${entry.picks.winner}`:"—"}</span>
                      </div>
                      <div className="lb-league" title={entry.leagueName || "No league"}>{entry.leagueName || "—"}</div>
                      <div className="lb-score">{entry.score}</div>
                      {renderRankChange(entry)}
                    </div>
                  );
                })}
                </div>
              </div>
            )}
            <div style={{textAlign:"center",marginTop:"1.5rem",color:"var(--muted)",fontSize:"0.78rem"}}>📅 Scores update after each episode airs</div>
            <div style={{textAlign:"center"}}>
              <a className="community-inline-link" href="https://www.reddit.com/r/DragMadness/" target="_blank" rel="noopener noreferrer">
                💬 Debate this ranking in r/DragMadness
              </a>
            </div>
            <div style={{textAlign:"center",marginTop:"0.8rem"}}>
              <button className="btn btn-ghost" onClick={loadLeaderboard} style={{fontSize:"0.72rem"}}>↺ Refresh</button>
            </div>
            <div className="kofi-banner scoring-note-card support-section">
              <div className="support-text">
                Love the site? It's free to play and always will be.<br/>
                If you'd like to help keep the lights on, a coffee (or a cocktail ! 🍹) goes a long way. ✦
              </div>
              <a className="support-button" href="https://ko-fi.com/playdragmadness" target="_blank" rel="noopener noreferrer">
                ☕ Buy me a coffee
              </a>
            </div>
          </div>
        )}

        {/* ══ ANALYSIS TAB ══ */}
        {tab==="analysis" && (
          <div className="page">
            <div style={{textAlign:"center"}}>
              <span className="sec-label">Crowd Picks</span>
              <div className="sec-title">Drag Madness <em>Analysis</em></div>
              <div className="sec-desc" style={{margin:"0.55rem auto 0",maxWidth:720}}>
                See who the crowd thinks will win, which queens are popping across brackets, and whether your picks are serving favorite or underdog energy.
              </div>
            </div>

            {analysis.totalEntries > 0 && (
              <>
                <div className="dashboard-kpi-grid">
                  <div className="dashboard-kpi-card">
                    <div className="dashboard-kpi-label">Crowd Favorite</div>
                    <div className="dashboard-kpi-value">{analysis.crowdWinner ? (QUEEN_EMOJI[analysis.crowdWinner] || "👑") : "—"}</div>
                    <div className="dashboard-kpi-sub">{analysis.crowdWinner || "No picks yet"} · {topWinnerPct}%</div>
                  </div>
                  <div className="dashboard-kpi-card">
                    <div className="dashboard-kpi-label">Your Rank</div>
                    <div className="dashboard-kpi-value">{myRankLabel}</div>
                    <div className="dashboard-kpi-sub">{myRankSub}</div>
                  </div>
                  <div className="dashboard-kpi-card">
                    <div className="dashboard-kpi-label">Crowd Similarity</div>
                    <div className="dashboard-kpi-value">{analysis.alignmentPct}%</div>
                    <div className="dashboard-kpi-sub">Your bracket weighted against all others.</div>
                  </div>
                </div>

                <div className="dashboard-insight-card">
                  <div className="dashboard-insight-label">Standout Insight</div>
                  <div className="dashboard-insight-main">
                    {picks.winner
                      ? `${analysis.yourWinnerPct}% picked your winner 👀`
                      : `${analysis.totalEntries} players competing`}
                  </div>
                  <div className="dashboard-insight-sub">
                    {picks.winner
                      ? analysis.yourWinnerPct <= 20
                        ? "That’s underdog energy. Bold and risky moves, mama."
                        : "Your winner pick has real crowd support."
                      : "Submit your bracket to unlock your personal crowd comparison."}
                  </div>
                </div>
              </>
            )}

            {analysis.totalEntries === 0 ? (
              <div className="analysis-card" style={{marginTop:"1.2rem",textAlign:"center"}}>
                <div className="analysis-card-title">No data yet</div>
                <div className="analysis-subtle">Submit your picks first, then check back to see the crowd trends 👀</div>
              </div>
            ) : (
              <>
                <div className="analysis-grid">
                  <div className="analysis-card">
                    <div className="analysis-card-title">🏆 Most Picked Winner</div>
                    <div className="analysis-big-pick">
                      {analysis.crowdWinner ? `${QUEEN_EMOJI[analysis.crowdWinner] || "👑"} ${analysis.crowdWinner}` : "—"}
                    </div>
                  </div>

                  <div className="analysis-card">
                    <div className="analysis-card-title">🥈 Most Picked Runner-Up</div>
                    <div className="analysis-big-pick">
                      {analysis.crowdRunnerUp ? `${QUEEN_EMOJI[analysis.crowdRunnerUp] || "🥈"} ${analysis.crowdRunnerUp}` : "—"}
                    </div>
                  </div>

                  <div className="analysis-card">
                    <div className="analysis-card-title">🔥 Players Analyzed</div>
                    <div className="analysis-big-pick">{analysis.totalEntries}</div>
                  </div>
                </div>

                <div className="dashboard-section-header">
                  <div className="dashboard-section-title">📊 Crowd Trends</div>
                  <div className="dashboard-section-note">Raw vote data from submitted brackets</div>
                </div>

                <div className="crowd-compare">
                  <div className="analysis-card">
                    <div className="analysis-card-title">👑 Winner Picks</div>
                    {renderAnalysisRows(analysis.winners, analysis.totalEntries, 8)}
                  </div>

                  <div className="analysis-card">
                    <div className="analysis-card-title">🥈 Runner-Up Picks</div>
                    {renderAnalysisRows(analysis.runners, analysis.totalEntries, 8)}
                  </div>
                </div>

                <div className="dashboard-section-header">
                  <div className="dashboard-section-title">🎯 Bracket Breakdown</div>
                  <div className="dashboard-section-note">Semi-final advancer percentages by group</div>
                </div>

                <div className="analysis-card" style={{marginTop:"0.9rem"}}>
                  <div className="analysis-card-title">📊 Semi-Final Advancer Picks by Bracket</div>
                  <div className="analysis-subtle" style={{marginBottom:"0.8rem"}}>
                    Percentage of users who selected each queen to advance from her color bracket.
                  </div>
                  <div className="bracket-advance-grid">
                    {renderBracketAdvanceRows("orange")}
                    {renderBracketAdvanceRows("pink")}
                    {renderBracketAdvanceRows("purple")}
                  </div>
                </div>

                <div style={{textAlign:"center",marginTop:"1rem"}}>
                  <a className="community-inline-link" href="https://www.reddit.com/r/DragMadness/" target="_blank" rel="noopener noreferrer">
                    💬 Think the crowd is wrong? Discuss it in r/DragMadness
                  </a>
                </div>

                {submitted && (
                  <div className="crowd-share-wrap">
                    <div className="share-results-label">Share your results</div>
                    <div className="crowd-share-card" ref={crowdShareCardRef}>
                      <div className="crowd-share-inner">
                        <div className="crowd-share-kicker">All Stars 11 · Drag Madness</div>
                        <div className="crowd-share-title">{username || "My"} vs The Crowd</div>
                        <div className="crowd-share-sub">Bracket Analysis</div>

                        <div className="crowd-share-versus">
                          <div className="crowd-share-box">
                            <div className="crowd-share-label">My Winner Pick</div>
                            <div className="crowd-share-pick">
                              {picks.winner ? `${QUEEN_EMOJI[picks.winner] || "👑"} ${picks.winner}` : "Not picked"}
                            </div>
                          </div>
                          <div className="crowd-share-box" style={{borderLeftColor:"var(--pink)"}}>
                            <div className="crowd-share-label">Crowd Favorite</div>
                            <div className="crowd-share-pick">
                              {analysis.crowdWinner ? `${QUEEN_EMOJI[analysis.crowdWinner] || "👑"} ${analysis.crowdWinner}` : "—"}
                            </div>
                          </div>
                          <div className="crowd-share-box" style={{borderLeftColor:"#fff4c8"}}>
                            <div className="crowd-share-label">Crowd Alignment</div>
                            <div className="crowd-share-pick">
                              {picks.winner ? `I agree with ${analysis.alignmentPct}% of players 👀` : "Submit picks first"}
                            </div>
                          </div>
                          <div className="crowd-share-box" style={{borderLeftColor:"var(--gold)"}}>
                            <div className="crowd-share-label">Winner Pick</div>
                            <div className="crowd-share-pick">
                              {picks.winner ? `${analysis.winnerPickRate}% picked my winner 👀` : "Submit picks first"}
                            </div>
                          </div>
                        </div>

                        <div className="crowd-share-underdogs">
                          <div className="crowd-share-label">Underdog Energy</div>
                          <div className="crowd-share-pick" style={{fontSize:"0.95rem"}}>
                            {analysis.underdogs.length
                              ? `${analysis.underdogs.length} underdog ${analysis.underdogs.length === 1 ? "pick" : "picks"} 💅 · ${analysis.underdogs.map(q => `${QUEEN_EMOJI[q] || "✨"} ${q}`).join(" · ")}`
                              : "No major underdogs — I’m with the crowd 👀"}
                          </div>
                        </div>

                        <div className="crowd-share-footer">
                          <div className="crowd-share-footer-small">Make your picks now for free at</div>
                          <div className="crowd-share-url">PLAYDRAGMADNESS.COM</div>
                        </div>
                      </div>
                    </div>

                    <div className="crowd-share-actions">
                      <button className="btn btn-gold" onClick={downloadCrowdShareCard}>Download Analysis Image</button>
                      <button className="btn btn-ghost" onClick={copyCrowdShareCard}>Copy to Clipboard</button>
                    </div>
                  </div>
                )}
              </>
            )}

            <div className="kofi-banner scoring-note-card support-section">
              <div className="support-text">
                Love the site? It's free to play and always will be.<br/>
                If you'd like to help keep the lights on, a coffee (or a cocktail ! 🍹) goes a long way. ✦
              </div>
              <a className="support-button" href="https://ko-fi.com/playdragmadness" target="_blank" rel="noopener noreferrer">
                ☕ Buy me a coffee
              </a>
            </div>
          </div>
        )}



        {/* ══ COMMUNITY TAB ══ */}
        {tab==="community" && (
          <div className="page community-page-clean">
            <div className="community-hero">
              <div className="community-title">Join the Drama 👀</div>
              <div className="community-copy">
                Debate eliminations, defend your picks, and see how others are playing Drag Madness.
                The real chaos happens here 💅
              </div>
              <a className="btn btn-gold" href="https://www.reddit.com/r/DragMadness/" target="_blank" rel="noopener noreferrer">
                🔥 Join r/DragMadness
              </a>
            </div>

            <div className="analysis-card" style={{marginTop:"1.2rem"}}>
              <div className="analysis-card-title">🔥 Featured Discussions</div>
              <div className="community-post-grid">
                <a className="community-post-card" href="https://www.reddit.com/r/DragMadness/" target="_blank" rel="noopener noreferrer">
                  <div className="community-post-kicker">Predictions Thread</div>
                  <div className="community-post-title">Drop your winner + top 2 predictions 👀</div>
                </a>
                <a className="community-post-card" href="https://www.reddit.com/r/DragMadness/" target="_blank" rel="noopener noreferrer">
                  <div className="community-post-kicker">Analysis</div>
                  <div className="community-post-title">Early data: here’s who the crowd thinks will win</div>
                </a>
                <a className="community-post-card" href="https://www.reddit.com/r/DragMadness/" target="_blank" rel="noopener noreferrer">
                  <div className="community-post-kicker">Weekly Superthreads</div>
                  <div className="community-post-title">Elimination discussion + bracket fallout 💀</div>
                </a>
              </div>
            </div>

            <div className="kofi-banner scoring-note-card support-section">
              <div className="support-text">
                Love the site? It's free to play and always will be.<br/>
                If you'd like to help keep the lights on, a coffee (or a cocktail ! 🍹) goes a long way. ✦
              </div>
              <a className="support-button" href="https://ko-fi.com/playdragmadness" target="_blank" rel="noopener noreferrer">
                ☕ Buy me a coffee
              </a>
            </div>
          </div>
        )}


      </div>

      {celebrating && (
        <div className="submit-celebration" aria-hidden="true">
          <span>👑</span><span>✨</span><span>💅</span><span>🏁</span>
          <span>💖</span><span>⭐</span><span>🍸</span><span>🎉</span>
        </div>
      )}

      {/* Site-wide disclaimer footer */}
      <div style={{
        textAlign:"center", padding:"1.5rem 1rem",
        borderTop:"1px solid rgba(255,255,255,0.04)",
        marginTop:"2rem"
      }}>
        <div style={{fontSize:"0.72rem", color:"rgba(138,106,170,0.5)", lineHeight:1.7, maxWidth:"560px", margin:"0 auto"}}>
          ✦ Fan-made project · Not affiliated with, endorsed by, or connected to VH1, Paramount+, or World of Wonder Productions · RuPaul's Drag Race All Stars is a trademark of World of Wonder Productions, Inc.
        </div>
      </div>


      {browserEditNoticeOpen && (
        <div className="browser-edit-notice-backdrop" role="dialog" aria-modal="true">
          <div className="browser-edit-notice-card">
            <div className="browser-edit-notice-title">This bracket belongs to another browser</div>
            <div className="browser-edit-notice-text">
              To protect everyone’s picks, you can only edit a submitted bracket from the original web browser used to submit it.
              Open Drag Madness on that same browser/device to re-pick and re-submit.
            </div>
            <button className="btn btn-gold" onClick={() => setBrowserEditNoticeOpen(false)}>
              Got it
            </button>
          </div>
        </div>
      )}





      {selectedEntry && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.72)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 900,
            padding: "1rem",
          }}
          onClick={() => setSelectedEntry(null)}
        >
          <div
      className="pick-modal-card"
      style={{
        background: "var(--card)",
              border: "1px solid rgba(245,200,66,0.22)",
              borderRadius: "6px",
              padding: "1.4rem",
              maxWidth: "520px",
              width: "100%",
              boxShadow: "0 20px 60px rgba(0,0,0,0.45)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:"1rem",marginBottom:"1rem"}}>
              <div>
                <div style={{fontSize:"0.65rem",letterSpacing:"0.22em",textTransform:"uppercase",color:"var(--gold)",marginBottom:"0.25rem"}}>Bracket Picks</div>
                <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:"1.6rem",fontWeight:700,lineHeight:1.1}}>
                  {selectedEntry.username}
                </div>
              </div>
              <button
                type="button"
                className="btn btn-ghost"
                style={{padding:"0.35rem 0.65rem",fontSize:"0.7rem"}}
                onClick={() => setSelectedEntry(null)}
              >
                Close
              </button>
            </div>

            <div style={{display:"grid",gap:"0.75rem"}}>
              {Object.entries(BRACKETS).map(([key, bracket]) => {
                const queens = selectedEntry.picks?.groupAdvances?.[key] || [];
                return (
                  <div
                    key={key}
                    style={{
                      background:"rgba(255,255,255,0.035)",
                      border:"1px solid rgba(255,255,255,0.07)",
                      borderLeft:`3px solid ${bracket.color}`,
                      borderRadius:"4px",
                      padding:"0.75rem 0.9rem",
                    }}
                  >
                    <div style={{fontSize:"0.68rem",letterSpacing:"0.16em",textTransform:"uppercase",color:"var(--muted)",marginBottom:"0.45rem"}}>
                      {bracket.label}
                    </div>
                    <div style={{fontSize:"0.88rem",lineHeight:1.7}}>
                      <div><strong style={{color:"var(--gold)"}}>#1</strong> {queens[0] ? `${QUEEN_EMOJI[queens[0]] || ""} ${queens[0]}` : "—"}</div>
                      <div><strong style={{color:"var(--gold)"}}>#2</strong> {queens[1] ? `${QUEEN_EMOJI[queens[1]] || ""} ${queens[1]}` : "—"}</div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div
              style={{
                marginTop:"0.9rem",
                background:"rgba(245,200,66,0.06)",
                border:"1px solid rgba(245,200,66,0.16)",
                borderRadius:"4px",
                padding:"0.85rem 0.95rem",
                fontSize:"0.9rem",
                lineHeight:1.75,
              }}
            >
              <div style={{fontSize:"0.68rem",letterSpacing:"0.16em",textTransform:"uppercase",color:"var(--gold)",marginBottom:"0.35rem"}}>Overall</div>
              <div>👑 <strong>Winner:</strong> {selectedEntry.picks?.winner ? `${QUEEN_EMOJI[selectedEntry.picks.winner] || ""} ${selectedEntry.picks.winner}` : "—"}</div>
              <div>🥈 <strong>Runner-Up:</strong> {selectedEntry.picks?.runnerUp ? `${QUEEN_EMOJI[selectedEntry.picks.runnerUp] || ""} ${selectedEntry.picks.runnerUp}` : "—"}</div>
            </div>
          </div>
        </div>
      )}

      <div className={`toast${toastVisible?" show":""}`}>{toast}</div>
    </>
  );
}
